import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Loader2, ArrowLeft, RefreshCw, Database, Brain, Users,
  FileText, BookOpen, AlertTriangle, TrendingUp, Activity, Shield,
  DollarSign, UserPlus, ArrowUpRight, Search, X,
  CreditCard, RotateCcw, Plus, Minus, ChevronDown, ChevronUp, ExternalLink, Download, Copy, Trash2, Play, Clock, FlaskConical, BarChart3, GitCompare, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatTime, formatDateTime, formatRelative, formatIsoTooltip } from '../lib/dateFormat';
/* TANDA 12 (firma #182) — LA DIRECCIÓN SALE DE LA FUENTE ÚNICA.
   Aquí vivía el correo escrito en duro, y era la última copia (con su gemela de
   `AdminErrorsPage.js`) de una regla que ya vivía por variable de entorno en las
   otras cuatro superficies. Dos mecanismos para la misma pregunta es uno que se
   puede quedar atrás; ahora hay uno. Vive en `components/routeGuards.js`, que es
   además quien guarda esta ruta. Guarda: cero correos en duro en `src/`. */
import { ADMIN_EMAIL } from '../components/routeGuards';
// tutor-2 (F.4, #680): aqui se importaba `OpinionDeLaFicha` (la opinion del usuario en su ficha); la pieza se borro.
const PLAN_COLORS = { free: 'text-[#888]', pro: 'text-[#FF6B00]' };
const PLAN_BADGES = { free: 'bg-[#333] text-[#aaa]', pro: 'bg-[#FF6B00]/15 text-[#FF6B00]' };

// TANDA 6 (#158) — recorte para celdas de tabla. El texto completo va SIEMPRE en el
// `title` de la celda, asi que esto sólo decide cuánto se pinta: una fila no puede
// crecer por un mensaje de error kilométrico. Devuelve '' para null/undefined, que es
// lo que hace que la celda quede vacía en vez de decir "null".
function truncateText(value, max) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

// #158b — el detalle de un evento, en UNA linea y sin JSON crudo.
//
// La tabla de eventos enseñaba nombre y accion, y el `details` —donde vive el porque de
// la falla— no se pintaba en ningun lado. Volcar el objeto entero convertiria la tabla en
// un muro, asi que se compone lo que de verdad se lee de un vistazo.
//
// DOS NOMBRES PARA EL MENSAJE, a proposito: los emisores viejos escriben `error_message`
// y el de la red inesperada (#156) escribe `message`. Leer solo uno dejaria la mitad de
// las fallas con la celda a medias.
//
// `compacto` es lo que se pinta (id de documento recortado, para que quepa el mensaje) y
// `completo` lo que va al tooltip — ahi el id viaja ENTERO, que es el que hace falta para
// pegarlo en el visor de Call Failures.
function formatEventDetails(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return { compacto: '', completo: '' };
  }
  const mensaje = details.message ?? details.error_message;
  const docId = details.document_id ? String(details.document_id) : '';

  const armar = (id, recorte) => {
    const partes = [];
    if (details.error_type) partes.push(String(details.error_type));
    if (id) partes.push(`doc ${id}`);
    if (mensaje) partes.push(recorte ? truncateText(mensaje, 80) : String(mensaje));
    return partes.join(', ');
  };

  return { compacto: armar(docId.slice(0, 8), true), completo: armar(docId, false) };
}

// ── Reusable components ──
function UsageBar({ used, limit, label, warning, actionNeeded }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const barColor = warning ? 'bg-red-500' : pct > 60 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="space-y-2" data-testid={`usage-bar-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between text-sm">
        <span className="text-[#a0a0a0]">{label}</span>
        <span className="text-white font-mono text-xs">{used} / {limit}</span>
      </div>
      <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      {warning && actionNeeded && (
        <p className="text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {actionNeeded}</p>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color = 'text-[#FF6B00]' }) {
  return (
    <div className="glass-card rounded-[16px] p-5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color.replace('text-', 'bg-')}/10`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <span className="text-xs text-[#6a6a6a] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white font-['Space_Grotesk']">{value}</p>
      {sub && <p className="text-xs text-[#6a6a6a] mt-1">{sub}</p>}
    </div>
  );
}

// post-t7-1 (F2 y F3, #580): LAS CUENTAS DEL ADMIN. Un invitado "con actividad" hizo algo despues de nacer (un
// documento, una sesion de estudio o un evento distinto del de su creacion); los documentos del banco del profe van en
// su propia cuenta y los que esperan al alumno ya no cuentan como "processing". Con un servidor viejo (sin las llaves
// nuevas) se pinta lo de antes. Textos del admin: solo espanol, sin par.
const ESTADOS_DE_DOCUMENTO = ['ready', 'processing', 'error', 'partial', 'uploaded', 'pending', 'pending_credits', 'archived'];

export function conActividad(par) {
  return `${(par && par.con) || 0} con actividad, ${(par && par.sin) || 0} sin actividad`;
}

function desgloseDeEstados(mapa) {
  return ESTADOS_DE_DOCUMENTO.map((e) => `${(mapa && mapa[e]) || 0} ${e}`).join(', ');
}

export function TarjetasDeCuentasArriba({ totals, ug, eng, docStatus }) {
  const inv = ug.invitados;
  const nuevo = eng.doc_status_alumno !== undefined;
  return (
    <>
      <MetricCard icon={Users} label="Total Users" value={totals.users || 0}
        sub={`${ug.total_free || 0} free, ${ug.total_paying || 0} paying, ${ug.total_guests_active || 0} guests${inv ? ` (invitados: ${conActividad(inv.vivos)})` : ''}`} color="text-blue-400" />
      {nuevo ? (
        <MetricCard icon={FileText} label="Documentos del alumno" value={totals.documents_alumno || 0}
          sub={`${desgloseDeEstados(eng.doc_status_alumno)}, ${eng.esperan_al_alumno || 0} esperan al alumno`} color="text-emerald-400" />
      ) : (
        <MetricCard icon={FileText} label="Documents" value={totals.documents || 0}
          sub={desgloseDeEstados(docStatus)} color="text-emerald-400" />
      )}
      {nuevo && (
        <MetricCard icon={FileText} label="Documentos del profe" value={totals.documents_profe || 0}
          sub={desgloseDeEstados(eng.doc_status_profe)} color="text-violet-400" />
      )}
    </>
  );
}

// post-t7-2b (D3): sin los mosaicos "Active Guests" (la misma cuenta que ya dice "Total Users") y "Docs Today"
// (sus datos siguen llegando del servidor, que no se toco: ya no se pintan). Quedan dos tarjetas, asi que la rejilla es
// de DOS columnas en todo ancho (antes cuatro en computadora): sin hueco. El desglose de invitados se queda en
// "Total Users" y en "New Today".
export const REJILLA_DE_CRECIMIENTO = 'grid grid-cols-2 gap-4';

export function TarjetasDeCrecimiento({ ug }) {
  const inv = ug.invitados;
  return (
    <>
      <MetricCard icon={UserPlus} label="New Today" value={ug.users_today || 0}
        sub={inv
          ? `Invitados hoy: ${conActividad(inv.hoy)} | ${ug.users_today_registered || 0} registered | week: ${ug.users_this_week || 0} (invitados: ${conActividad(inv.semana)}), month: ${ug.users_this_month || 0} (invitados: ${conActividad(inv.mes)})`
          : `${ug.users_today_guests || 0} guests, ${ug.users_today_registered || 0} registered | week: ${ug.users_this_week || 0}, month: ${ug.users_this_month || 0}`} color="text-blue-400" />
      <MetricCard icon={ArrowUpRight} label="Paying" value={ug.total_paying || 0}
        sub={`${ug.total_pro || 0} Pro`} color="text-violet-400" />
    </>
  );
}

// Sales panel — purchase funnel + revenue from the new /sb-ctrl/analytics/sales aggregator.
function SalesSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSales, setShowSales] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/sb-ctrl/analytics/sales?days=30');
        if (alive) setData(res.data);
      } catch { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const counts = data?.counts || {};
  const byType = data?.purchases_by_type || {};
  const rev = data?.revenue_by_currency || {};
  const revLine = Object.keys(rev).length
    ? Object.entries(rev).map(([c, a]) => `${a} ${String(c).toUpperCase()}`).join(' + ')
    : '0';
  const typeSub = Object.keys(byType).length
    ? Object.entries(byType).map(([k, v]) => `${v} ${k}`).join(', ')
    : 'none';
  const recent = data?.recent_purchases || [];

  return (
    <div className="space-y-4" data-testid="admin-sales-section">
      <button onClick={() => setShowSales(!showSales)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="admin-toggle-sales">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSales ? 'rotate-180' : ''}`} />
        {showSales ? 'Hide' : 'Show'} Sales
      </button>
      {showSales && (
      <>
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-[#FF6B00]" /> Sales (30d)
      </h2>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#555]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={CreditCard} label="Purchases 30d" value={counts.purchase_completed || 0} sub={typeSub} color="text-emerald-400" />
            <MetricCard icon={DollarSign} label="Revenue 30d" value={revLine} sub="paid, by currency" color="text-amber-400" />
            <MetricCard icon={Activity} label="Checkouts 30d" value={data?.checkout_started || 0} sub={`${counts.subscription_renewed || 0} renewals`} color="text-blue-400" />
            <MetricCard icon={TrendingUp} label="Conversion" value={data?.conversion_pct != null ? `${data.conversion_pct}%` : '-'} sub="completed / started" color="text-violet-400" />
          </div>
          <div className="mt-4 overflow-x-auto glass-card rounded-[16px] p-4">
            <p className="text-sm font-semibold text-white mb-3">Last 20 purchases</p>
            <table className="w-full text-xs" data-testid="admin-sales-recent">
              <thead>
                <tr className="text-[#6a6a6a] border-b border-[#262626]">
                  <th className="text-left py-2 pr-3">When</th>
                  <th className="text-left py-2 pr-3">Type</th>
                  <th className="text-left py-2 pr-3">Plan / Pack</th>
                  <th className="text-right py-2 pr-3">Amount</th>
                  <th className="text-left py-2 pr-3">Cur</th>
                  <th className="text-left py-2 pr-3">User</th>
                  <th className="text-left py-2">Source</th>
                </tr>
              </thead>
              <tbody className="text-[#a0a0a0]">
                {recent.length === 0 ? (
                  <tr><td colSpan={7} className="py-3 text-center text-[#6a6a6a]">No purchases yet</td></tr>
                ) : recent.map((p, i) => (
                  <tr key={i} className="border-b border-[#1a1a1a]">
                    <td className="py-2 pr-3 whitespace-nowrap">{p.ts ? formatDateTime(p.ts) : ''}</td>
                    <td className="py-2 pr-3">{p.type || ''}</td>
                    <td className="py-2 pr-3">{p.plan_or_pack || ''}</td>
                    <td className="py-2 pr-3 text-right font-mono text-white">{p.amount != null ? p.amount : ''}</td>
                    <td className="py-2 pr-3 uppercase">{p.currency || ''}</td>
                    <td className="py-2 pr-3 font-mono">{p.user_id || ''}</td>
                    <td className="py-2">{p.source || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

// TANDA 25 bloque D (firma #232) — Pagos por proveedor.
//
// Desde que la app cobra por Apple hay DOS cajas, y el panel de Sales las suma sin
// distinguirlas. Esta tarjeta contesta las dos preguntas que no se podían contestar sin
// abrir Mongo a mano: cuántos Pro paga cada proveedor, y qué compras entraron por la
// App Store.
//
// ── CORREGIDA EN LA TANDA 25b, con lo que Carlos vio en producción ──
//
// NO SE SUMA NI UN MONTO. La primera versión mostraba «$621.99», que eran 5.99 EUR +
// 129 MXN + 229 MXN + … contados como dólares: el campo de las filas de Apple se llama
// `amount_usd` pero guarda la moneda LOCAL del storefront. Convertir necesitaría el tipo
// de cambio del día de cada cobro, que no está guardado. Cada compra muestra SU monto con
// SU moneda al lado, y el lector suma lo que quiera sumar.
//
// SE FUE LA TABLA «Credit packs — all time» Y SE FUERON LAS CUATRO TARJETAS DE MÉTRICAS.
// Arriba queda una línea de conteos de Pro; abajo, la lista de compras. Nada más: era
// demasiada información para una vista que se abre a revisar, no a vigilar.
//
// «Sin proveedor» solo aparece cuando es mayor que cero. Un cero permanente ocupa el
// mismo espacio que un número que sí importa y no dice nada.
//
// La deduplicación (misma compra en las dos colecciones) y el filtro de Sandbox viven en
// el servidor. Aquí no se re-hacen: dos copias de esa regla son dos oportunidades de que
// la pantalla y el endpoint cuenten historias distintas.
//
// Mismo patrón que `SalesSection`, plegada por defecto. CERO i18n — excepción firmada del
// admin, que es de un solo lector.
export function PagosPorProveedorSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/sb-ctrl/pagos-por-proveedor');
        if (alive) setData(res.data);
      } catch { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const pro = data?.pro_activos || {};
  const compras = data?.compras_apple || [];

  return (
    <div className="space-y-4" data-testid="admin-pagos-proveedor-section">
      <button onClick={() => setShow(!show)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="admin-toggle-pagos-proveedor">
        <ChevronDown className={`w-4 h-4 transition-transform ${show ? 'rotate-180' : ''}`} />
        {show ? 'Hide' : 'Show'} Payments by provider
      </button>
      {show && (
      <>
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-[#FF6B00]" /> Payments by provider
      </h2>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#555]" /></div>
      ) : (
        <>
          <p className="text-sm text-[#a0a0a0]" data-testid="admin-pro-por-proveedor">
            Pro activos:{' '}
            <span className="font-mono text-white">{pro.stripe ?? 0}</span> Stripe
            {', '}
            <span className="font-mono text-white">{pro.apple ?? 0}</span> Apple
            {(pro.otro ?? 0) > 0 && (
              <>
                {', '}
                <span className="font-mono text-white">{pro.otro}</span> sin proveedor
              </>
            )}
          </p>

          <div className="mt-4 overflow-x-auto glass-card rounded-[16px] p-4">
            <p className="text-sm font-semibold text-white mb-3">
              Apple purchases (last {data?.tope_lista ?? 100}, CDMX time)
            </p>
            <table className="w-full text-xs" data-testid="admin-compras-apple">
              <thead>
                <tr className="text-[#6a6a6a] border-b border-[#262626]">
                  <th className="text-left py-2 pr-3">When (CDMX)</th>
                  <th className="text-left py-2 pr-3">Type</th>
                  <th className="text-left py-2 pr-3">Plan / Pack</th>
                  <th className="text-right py-2 pr-3">Amount</th>
                  <th className="text-left py-2 pr-3">Cur</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2">Email</th>
                </tr>
              </thead>
              <tbody className="text-[#a0a0a0]">
                {compras.length === 0 ? (
                  <tr><td colSpan={7} className="py-3 text-center text-[#6a6a6a]">No Apple purchases yet</td></tr>
                ) : compras.map((c, i) => (
                  <tr key={i} className="border-b border-[#1a1a1a]">
                    <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_cdmx || ''}</td>
                    <td className="py-2 pr-3">{c.tipo || ''}</td>
                    <td className="py-2 pr-3">{c.detalle || ''}</td>
                    {/* Sin símbolo de moneda: el que aplique va en la columna de al lado,
                        y un `$` delante de un monto en pesos es justo el error que se
                        corrigió. */}
                    <td className="py-2 pr-3 text-right font-mono text-white">{c.monto != null ? c.monto : ''}</td>
                    <td className="py-2 pr-3 uppercase">{c.moneda || ''}</td>
                    <td className={`py-2 pr-3 ${c.estado === 'Completado' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {c.estado || ''}
                    </td>
                    <td className="py-2 font-mono break-all">{c.email || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

// Sync check panel — Sprint F2 shadow verifier: replays the answer-event log
// through the pure engine and lists divergences vs stored topic_performance.
// Read-only view + manual trigger; anomalies come from /sb-ctrl/sync-anomalies.
function SyncAnomaliesSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [show, setShow] = useState(false);

  const fetchAnomalies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sb-ctrl/sync-anomalies');
      setData(res.data);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (show && !data) fetchAnomalies(); }, [show, data, fetchAnomalies]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const res = await api.post('/sb-ctrl/sync-check/run');
      const s = res.data || {};
      toast.success(`Sync check: ${s.groups_checked ?? 0} groups, ${s.anomalies_found ?? 0} anomalies (${s.elapsed_ms ?? '?'} ms)`);
      fetchAnomalies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync check failed');
    } finally { setRunning(false); }
  };

  const anomalies = data?.anomalies || [];

  return (
    <div className="space-y-4" data-testid="admin-sync-anomalies-section">
      <button onClick={() => setShow(!show)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="admin-toggle-sync-anomalies">
        <ChevronDown className={`w-4 h-4 transition-transform ${show ? 'rotate-180' : ''}`} />
        {show ? 'Hide' : 'Show'} Engine Sync Check
      </button>
      {show && (
      <>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#FF6B00]" /> Engine Sync Check (shadow)
        </h2>
        <Button onClick={runCheck} disabled={running} size="sm" variant="outline" data-testid="admin-run-sync-check">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          <span className="ml-1">Run check</span>
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#555]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <MetricCard icon={AlertTriangle} label="Anomalies Total" value={data?.total ?? 0}
              sub="event replay vs stored state" color={(data?.total ?? 0) > 0 ? 'text-red-400' : 'text-emerald-400'} />
            <MetricCard icon={Activity} label="Showing Latest" value={data?.count ?? 0} sub="max 100" color="text-blue-400" />
          </div>
          <div className="mt-4 overflow-x-auto glass-card rounded-[16px] p-4">
            <p className="text-sm font-semibold text-white mb-3">Latest anomalies</p>
            <table className="w-full text-xs" data-testid="admin-sync-anomalies-table">
              <thead>
                <tr className="text-[#6a6a6a] border-b border-[#262626]">
                  <th className="text-left py-2 pr-3">Detected</th>
                  <th className="text-left py-2 pr-3">Kind</th>
                  <th className="text-left py-2 pr-3">User</th>
                  <th className="text-left py-2 pr-3">Topic</th>
                  <th className="text-right py-2 pr-3">Events</th>
                  <th className="text-left py-2">At event</th>
                </tr>
              </thead>
              <tbody className="text-[#a0a0a0]">
                {anomalies.length === 0 ? (
                  <tr><td colSpan={6} className="py-3 text-center text-[#6a6a6a]">No anomalies: event log and stored state agree</td></tr>
                ) : anomalies.map((a, i) => (
                  <tr key={a.id || i} className="border-b border-[#1a1a1a]">
                    <td className="py-2 pr-3 whitespace-nowrap">{a.detected_at ? formatDateTime(a.detected_at) : ''}</td>
                    <td className="py-2 pr-3 font-mono text-amber-400">{a.kind}</td>
                    <td className="py-2 pr-3 font-mono">{a.user_id}</td>
                    <td className="py-2 pr-3">{a.topic}</td>
                    <td className="py-2 pr-3 text-right">{a.event_span?.count ?? ''}</td>
                    <td className="py-2 font-mono">{a.at_event_id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

// ── #158c: visor de Call Failures ──────────────────────────────────────────────────
// `call_b_failures` y `call_c_failures` guardan el post-mortem crudo de una llamada que
// se cayo —la respuesta literal del modelo incluida— y hasta hoy solo se leian entrando
// a Mongo a mano. Esto las pone en el panel, en solo lectura.
//
// LAS DOS COLECCIONES NO COMPARTEN ESQUEMA. Call B trae `finish_reason` y contadores de
// tokens; Call C no. El sello de tiempo se llama distinto en cada una, y por eso el
// servidor devuelve `time_field` en la respuesta: la pantalla lee el sello por ese
// nombre en vez de adivinarlo. Lo que en Call B es `raw_content` en Call C es
// `raw_content_excerpt`.
//
// EL PROMPT NO SE PINTA COMPLETO PORQUE NO EXISTE: ninguno de los dos escritores guarda
// el texto del prompt. Call B guarda su huella y su largo (`user_prompt_sha256`,
// `user_prompt_len`) y Call C guarda la pregunta. Se muestra lo que hay, con su nombre.
// Exportada como sus hermanas `CostosInstitucionalesSection` y `ModelPricingSection`: es
// la costura por la que las suites del panel montan una seccion suelta.
export function CallFailuresSection() {
  const [show, setShow] = useState(false);
  const [call, setCall] = useState('b');
  const [docId, setDocId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [abierta, setAbierta] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/sb-ctrl/call-failures', {
        params: { call, document_id: docId.trim() || undefined, limit: 20 },
      });
      setData(res.data);
      setAbierta(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load call failures');
    } finally { setLoading(false); }
  }, [call, docId]);

  const filas = data?.rows || [];
  const timeField = data?.time_field || 'created_at';
  // El crudo vive bajo un nombre en cada coleccion; el prompt, bajo lo poco que se guarda.
  const crudoDe = (f) => f.raw_content ?? f.raw_content_excerpt ?? '';
  const promptDe = (f) => {
    if (f.user_prompt_sha256) {
      return `(el prompt no se persiste)\nsha256: ${f.user_prompt_sha256}\nlength: ${f.user_prompt_len ?? '?'}`;
    }
    if (f.question_text) return `question_text:\n${f.question_text}`;
    return '(el prompt no se persiste)';
  };

  return (
    <div className="space-y-4" data-testid="admin-call-failures-section">
      <button onClick={() => setShow(!show)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors"
        data-testid="admin-toggle-call-failures">
        <ChevronDown className={`w-4 h-4 transition-transform ${show ? 'rotate-180' : ''}`} />
        {show ? 'Hide' : 'Show'} Call Failures
      </button>
      {show && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FF6B00]" /> Call Failures
            </h2>
          </div>

          <div className="glass-card rounded-[16px] p-4 flex flex-wrap items-center gap-2">
            {[['b', 'Call B'], ['c', 'Call C']].map(([valor, etiqueta]) => (
              <button key={valor} onClick={() => setCall(valor)}
                data-testid={`admin-call-failures-pick-${valor}`}
                className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${call === valor ? 'bg-[#FF6B00]/15 text-[#FF6B00]' : 'text-[#555] hover:text-white'}`}>
                {etiqueta}
              </button>
            ))}
            <Input value={docId} onChange={(e) => setDocId(e.target.value)}
              placeholder="document_id (optional)"
              data-testid="admin-call-failures-doc-input"
              className="flex-1 min-w-[200px] bg-[#0d0d0d] border-[#262626] text-xs text-white font-mono" />
            <Button onClick={cargar} disabled={loading} size="sm" variant="outline"
              data-testid="admin-call-failures-load">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-1">Load</span>
            </Button>
          </div>

          {data && (
            <div className="glass-card rounded-[16px] p-4">
              <p className="text-xs text-[#666] mb-3" data-testid="admin-call-failures-summary">
                {data.collection}, {data.count} row{data.count === 1 ? '' : 's'}
              </p>
              {filas.length === 0 ? (
                <p className="text-xs text-[#555]" data-testid="admin-call-failures-empty">No failures found</p>
              ) : (
                <div className="space-y-2" data-testid="admin-call-failures-list">
                  {filas.map((f, i) => (
                    <div key={i} className="border border-[#1e1e1e] rounded-lg overflow-hidden">
                      <button onClick={() => setAbierta(abierta === i ? null : i)}
                        data-testid={`admin-call-failure-row-${i}`}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-[#1a1a1a]/50 transition-colors text-left">
                        <span className="font-mono text-[10px] text-white truncate">{f.document_id}</span>
                        <span className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] text-[#666]">{formatDateTime(f[timeField]) || String(f[timeField] ?? '')}</span>
                          {f.finish_reason && (
                            <span className="text-[10px] text-amber-400">{f.finish_reason}</span>
                          )}
                          {abierta === i ? <ChevronUp className="w-3.5 h-3.5 text-[#555]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#555]" />}
                        </span>
                      </button>
                      {abierta === i && (
                        <div className="border-t border-[#1e1e1e] p-3 space-y-3" data-testid={`admin-call-failure-body-${i}`}>
                          <div>
                            <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Raw response</p>
                            <pre className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-lg p-2 text-[10px] text-[#aaa] font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto"
                                 data-testid={`admin-call-failure-raw-${i}`}>{crudoDe(f)}</pre>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Prompt</p>
                            <pre className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-lg p-2 text-[10px] text-[#aaa] font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto"
                                 data-testid={`admin-call-failure-prompt-${i}`}>{promptDe(f)}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── User Management Section ──
// tutor-2 (F.4): exportada para su prueba.
export function UserManagement() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [action, setAction] = useState(null); // 'plan' | 'credits' | 'remove_credits' | 'reset'
  const [creditsToRemove, setCreditsToRemove] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [planChoice, setPlanChoice] = useState('pro');
  const [syncStripe, setSyncStripe] = useState(true);
  const [creditsToAdd, setCreditsToAdd] = useState(10);
  const [adminLog, setAdminLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [cancelMode, setCancelMode] = useState('immediate'); // 'immediate' | 'period_end'

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentStatusLoading, setPaymentStatusLoading] = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);

  const [showDocs, setShowDocs] = useState(false);
  const [userDocs, setUserDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [userPayments, setUserPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const search = async () => {
    if (query.length < 3) { toast.error('Enter at least 3 characters'); return; }
    setSearching(true);
    try {
      const res = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(query)}`);
      setResults(res.data.users || []);
      setSelected(null);
      if (res.data.users.length === 0) toast('No users found');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally { setSearching(false); }
  };

  const selectUser = (u) => {
    setSelected(u);
    setAction(null);
    setPaymentStatus(null);
    setGeneratedLink(null);
    setShowDocs(false);
    setUserDocs([]);
    setShowPayments(false);
    setUserPayments([]);
    setPlanChoice(u.plan === 'pro' ? 'free' : 'pro');
    // Fetch payment status in background
    setPaymentStatusLoading(true);
    api.get(`/sb-ctrl/users/${u.id}/payment-status`)
      .then(res => setPaymentStatus(res.data))
      .catch(() => setPaymentStatus(null))
      .finally(() => setPaymentStatusLoading(false));
  };

  const fetchUserDocs = async () => {
    if (userDocs.length > 0) { setShowDocs(!showDocs); return; }
    setDocsLoading(true);
    setShowDocs(true);
    try {
      const res = await api.get(`/sb-ctrl/users/${selected.id}/documents`);
      setUserDocs(res.data.documents || []);
    } catch { setUserDocs([]); }
    finally { setDocsLoading(false); }
  };

  const fetchUserPayments = async () => {
    if (userPayments.length > 0) { setShowPayments(!showPayments); return; }
    setPaymentsLoading(true);
    setShowPayments(true);
    try {
      const res = await api.get(`/sb-ctrl/users/${selected.id}/payments`);
      setUserPayments(res.data.payments || []);
    } catch { setUserPayments([]); }
    finally { setPaymentsLoading(false); }
  };

  /* PILOTO-1 (firma #79) — EL INTERRUPTOR DE LA BANDERA INSTITUCIONAL.
     Carlos JAMÁS corre un comando: busca el correo, abre la ficha y da UN clic.
     Este handler es todo el mecanismo, y refresca la ficha desde el servidor
     (no desde el estado local) para que lo que la pantalla muestre sea lo que
     la base guardó, nunca un optimismo del navegador. */
  const handleToggleInstitutional = async () => {
    const next = !selected.is_institutional;
    setActionLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/institutional`, { enabled: next });
      toast.success(res.data.message);
      const refreshed = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(selected.email)}`);
      const updated = (refreshed.data.users || []).find(u => u.id === selected.id);
      if (updated) setSelected(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change institutional access');
    } finally { setActionLoading(false); }
  };

  const handleChangePlan = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/change-plan`, { plan: planChoice, sync_stripe: syncStripe });
      toast.success(res.data.message);
      const refreshed = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(selected.email)}`);
      const updated = (refreshed.data.users || []).find(u => u.id === selected.id);
      if (updated) { setSelected(updated); selectUser(updated); }
      setAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change plan');
    } finally { setActionLoading(false); }
  };

  const handleSendPaymentLink = async () => {
    setPaymentLinkLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/payment-link`, { plan: planChoice });
      setGeneratedLink(res.data.url);
      toast.success(`Payment link generated for ${planChoice} plan`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payment link');
    } finally { setPaymentLinkLoading(false); }
  };

  const handleAddCredits = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/add-credits`, { credits: creditsToAdd });
      toast.success(res.data.message);
      const refreshed = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(selected.email)}`);
      const updated = (refreshed.data.users || []).find(u => u.id === selected.id);
      if (updated) setSelected(updated);
      setAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add credits');
    } finally { setActionLoading(false); }
  };

  const handleRemoveCredits = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/remove-credits`, { amount: creditsToRemove });
      toast.success(res.data.message);
      const refreshed = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(selected.email)}`);
      const updated = (refreshed.data.users || []).find(u => u.id === selected.id);
      if (updated) setSelected(updated);
      setAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove credits');
    } finally { setActionLoading(false); }
  };

  const handleResetCredits = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/reset-credits`);
      toast.success(res.data.message);
      const refreshed = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(selected.email)}`);
      const updated = (refreshed.data.users || []).find(u => u.id === selected.id);
      if (updated) setSelected(updated);
      setAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset credits');
    } finally { setActionLoading(false); }
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/sb-ctrl/users/${selected.id}/cancel-subscription`, { immediate: cancelMode === 'immediate' });
      toast.success(res.data.message);
      const refreshed = await api.get(`/sb-ctrl/users/search?q=${encodeURIComponent(selected.email)}`);
      const updated = (refreshed.data.users || []).find(u => u.id === selected.id);
      if (updated) setSelected(updated);
      setAction(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally { setActionLoading(false); }
  };

  const fetchLog = async () => {
    try {
      const res = await api.get('/sb-ctrl/admin-log');
      setAdminLog(res.data.logs || []);
      setShowLog(true);
    } catch { toast.error('Failed to load audit log'); }
  };

  return (
    <div className="space-y-4" data-testid="user-management-section">
      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
        <Users className="w-5 h-5 text-[#FF6B00]" /> User Management
      </h2>

      {/* Search */}
      <div className="flex gap-2">
        <Input value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Search by email..." className="flex-1 bg-[#0d0d0d] border-[#262626] text-white rounded-xl h-10"
          data-testid="admin-user-search-input" />
        <Button onClick={search} disabled={searching} className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-10 px-4"
          data-testid="admin-user-search-btn">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
        <Button variant="outline" onClick={fetchLog} className="border-[#262626] text-[#a0a0a0] hover:text-white rounded-xl h-10"
          data-testid="admin-audit-log-btn">
          Audit Log
        </Button>
      </div>

      {/* Search results */}
      {results.length > 0 && !selected && (
        <div className="glass-card rounded-[16px] p-3 space-y-1" data-testid="admin-search-results">
          {results.map(u => (
            <button key={u.id} onClick={() => selectUser(u)}
              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-[#1a1a1a] transition-colors text-left"
              data-testid={`admin-user-result-${u.email}`}>
              <div>
                <span className="text-sm text-white">{u.email}</span>
                <span className="text-xs text-[#555] ml-2">{u.name}</span>
              </div>
              <Badge className={`text-[10px] ${PLAN_BADGES[u.plan] || PLAN_BADGES.free}`}>{u.plan}</Badge>
            </button>
          ))}
        </div>
      )}

      {/* Selected user details */}
      {selected && (
        <div className="glass-card rounded-[16px] p-5 space-y-4" data-testid="admin-user-details">
          <div className="flex items-center justify-between">
            <div>
              {/* TANDA 24 (E3, firma #225): era el ÚNICO «No name» en duro del repo,
                  y es el que Carlos veía. El correo va justo debajo y ya identifica
                  la cuenta, así que un nombre ausente se muestra ausente — igual que
                  el saludo del panel, que dice «Bienvenido» a secas. */}
              <h3 className="text-base font-semibold text-white">{selected.name || ''}</h3>
              <p className="text-sm text-[#6a6a6a]">{selected.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`${PLAN_BADGES[selected.plan] || PLAN_BADGES.free}`} data-testid="admin-user-plan-badge">
                {selected.plan?.toUpperCase()}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setAction(null); }}
                className="text-[#555] hover:text-white" data-testid="admin-user-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Info grid — row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Stripe Status</p>
              <p className={`font-medium ${selected.has_stripe_subscription ? 'text-emerald-400' : 'text-[#666]'}`} data-testid="admin-user-stripe-status">
                {selected.has_stripe_subscription ? 'Active' : 'No subscription'}
              </p>
              {selected.stripe_customer_id ? (
                <a href={`https://dashboard.stripe.com/customers/${selected.stripe_customer_id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[#FF6B00] font-mono text-[10px] mt-0.5 truncate block hover:underline"
                  data-testid="admin-stripe-link">
                  {selected.stripe_customer_id}
                </a>
              ) : selected.stripe_subscription_id && (
                <p className="text-[#444] font-mono text-[10px] mt-0.5 truncate">{selected.stripe_subscription_id}</p>
              )}
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Credits</p>
              <p className="text-white font-mono" data-testid="admin-user-credits">
                {selected.credits_remaining} / {selected.credits_limit} <span className="text-[#555]">sub</span>
              </p>
              <p className="text-[#FF6B00] font-mono text-[10px]">+{selected.pack_credits} pack</p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Documents</p>
              <p className="text-white font-mono" data-testid="admin-user-docs">{selected.documents_count}</p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Registered</p>
              <p className="text-white text-[11px]" data-testid="admin-user-created" title={formatIsoTooltip(selected.created_at)}>
                {formatDateTime(selected.created_at) || 'N/A'}
              </p>
            </div>
          </div>

          {/* Info grid — row 2. PILOTO-1: 5 → 6 columnas (entra "Institutional"). */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 text-xs">
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Last Login</p>
              <p className="text-white text-[11px]" data-testid="admin-user-last-login" title={formatIsoTooltip(selected.last_login)}>
                {formatDateTime(selected.last_login) || 'Before tracking'}
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Last Active</p>
              <p className="text-white text-[11px]" data-testid="admin-user-last-active" title={formatIsoTooltip(selected.last_active)}>
                {formatDateTime(selected.last_active) || 'Never'}
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Registration</p>
              <p className="text-white text-[11px]" data-testid="admin-user-reg-method">
                {selected.registration_method === 'google' ? 'Google' : 'Email'}
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Next Billing</p>
              <p className="text-white text-[11px]" data-testid="admin-user-next-billing">
                {formatDate(selected.next_billing_date) || formatDate(selected.credit_reset_date) || 'N/A'}
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              {/* PILOTO-1: el estado de la bandera, a la vista junto al resto de
                  la ficha, para que el clic de abajo no sea a ciegas. */}
              <p className="text-[#555] mb-1">Institutional</p>
              <p className={`text-[11px] font-medium ${selected.is_institutional ? 'text-[#FF6B00]' : 'text-[#666]'}`}
                data-testid="admin-user-institutional-status">
                {selected.is_institutional ? 'Teacher (ON)' : 'Off'}
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-3">
              <p className="text-[#555] mb-1">Cancel Status</p>
              <p className={`text-[11px] font-medium ${selected.cancel_at_period_end ? 'text-amber-400' : selected.subscription_cancelled_at ? 'text-red-400' : 'text-[#666]'}`}
                data-testid="admin-user-cancel-status">
                {selected.cancel_at_period_end
                  ? `Cancels ${formatDate(selected.cancel_at) || 'at period end'}`
                  : selected.subscription_cancelled_at
                    ? `Cancelled ${formatDate(selected.subscription_cancelled_at)}`
                    : 'None'}
              </p>
            </div>
          </div>

          {/* tutor-2 (F.4, #680): aqui vivia el recuadro de la opinion del usuario (con "Regalar 3 creditos").
              Carlos da los creditos a mano con "Add Pack Credits"; las rutas del servidor se quedan sin uso. */}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={action === 'plan' ? 'default' : 'outline'}
              onClick={() => setAction(action === 'plan' ? null : 'plan')}
              className={action === 'plan' ? 'bg-[#FF6B00] text-white rounded-xl' : 'border-[#333] text-[#aaa] rounded-xl'}
              data-testid="admin-change-plan-btn">
              <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Change Plan
            </Button>
            <Button size="sm" variant={action === 'credits' ? 'default' : 'outline'}
              onClick={() => setAction(action === 'credits' ? null : 'credits')}
              className={action === 'credits' ? 'bg-emerald-600 text-white rounded-xl' : 'border-[#333] text-[#aaa] rounded-xl'}
              data-testid="admin-add-credits-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Pack Credits
            </Button>
            <Button size="sm" variant={action === 'remove_credits' ? 'default' : 'outline'}
              onClick={() => { setAction(action === 'remove_credits' ? null : 'remove_credits'); setCreditsToRemove(1); }}
              className={action === 'remove_credits' ? 'bg-red-600 text-white rounded-xl' : 'border-[#333] text-[#aaa] rounded-xl'}
              data-testid="admin-remove-credits-btn">
              <Minus className="w-3.5 h-3.5 mr-1.5" /> Remove Pack Credits
            </Button>
            <Button size="sm" variant={action === 'reset' ? 'default' : 'outline'}
              onClick={() => setAction(action === 'reset' ? null : 'reset')}
              className={action === 'reset' ? 'bg-amber-600 text-white rounded-xl' : 'border-[#333] text-[#aaa] rounded-xl'}
              data-testid="admin-reset-credits-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset Sub Credits
            </Button>
            {/* PILOTO-1 (firma #79) — EL BOTÓN. Enciende y apaga con el mismo
                clic; el rótulo dice qué va a pasar, no en qué estado está. */}
            <Button size="sm" variant="outline" onClick={handleToggleInstitutional} disabled={actionLoading}
              className={selected.is_institutional
                ? 'border-[#FF6B00]/40 text-[#FF6B00] rounded-xl'
                : 'border-[#333] text-[#aaa] rounded-xl'}
              data-testid="admin-toggle-institutional-btn">
              <GraduationCap className="w-3.5 h-3.5 mr-1.5" />
              {selected.is_institutional ? 'Disable Institutional' : 'Enable Institutional'}
            </Button>
            {selected.stripe_subscription_id && (
              <Button size="sm" variant={action === 'cancel' ? 'default' : 'outline'}
                onClick={() => setAction(action === 'cancel' ? null : 'cancel')}
                className={action === 'cancel' ? 'bg-red-600 text-white rounded-xl' : 'border-[#333] text-[#aaa] rounded-xl'}
                data-testid="admin-cancel-sub-btn">
                <X className="w-3.5 h-3.5 mr-1.5" /> Cancel Subscription
              </Button>
            )}
          </div>

          {/* Action panels */}
          {action === 'plan' && (
            <div className="bg-[#0d0d0d] border border-[#262626] rounded-xl p-4 space-y-3" data-testid="admin-plan-action-panel">
              <p className="text-sm text-white font-medium">Change plan for {selected.email}</p>
              <div className="flex gap-2">
                {['free', 'pro'].filter(p => p !== selected.plan).map(p => (
                  <button key={p} onClick={() => { setPlanChoice(p); setGeneratedLink(null); }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${planChoice === p ? 'bg-[#FF6B00] text-white' : 'bg-[#1a1a1a] text-[#888] hover:text-white'}`}
                    data-testid={`admin-plan-option-${p}`}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>

              {/* Payment status info */}
              {paymentStatusLoading ? (
                <div className="flex items-center gap-2 text-xs text-[#555]"><Loader2 className="w-3 h-3 animate-spin" /> Checking payment method...</div>
              ) : paymentStatus && (
                <div className={`text-xs p-2.5 rounded-lg border ${paymentStatus.has_payment_method ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' : 'bg-amber-500/5 border-amber-500/20 text-amber-400'}`}
                  data-testid="admin-payment-status">
                  {paymentStatus.has_payment_method ? (
                    <span>Payment method on file: {paymentStatus.card_brand} ending in {paymentStatus.card_last4}</span>
                  ) : (
                    <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No payment method on file</span>
                  )}
                </div>
              )}

              {planChoice !== 'free' && (
                <div className="space-y-2">
                  {/* Stripe sync option — disabled if no payment method */}
                  <label className={`flex items-center gap-2 ${paymentStatus && !paymentStatus.has_payment_method && !paymentStatus.has_active_subscription ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input type="radio" name="sync" checked={syncStripe}
                      onChange={() => setSyncStripe(true)}
                      disabled={paymentStatus && !paymentStatus.has_payment_method && !paymentStatus.has_active_subscription}
                      className="accent-[#FF6B00]" />
                    <span className="text-sm text-[#aaa]">Change plan + sync with Stripe</span>
                    {paymentStatus?.has_payment_method && <Badge className="bg-emerald-500/15 text-emerald-400 text-[10px]">Recommended</Badge>}
                  </label>
                  {paymentStatus && !paymentStatus.has_payment_method && !paymentStatus.has_active_subscription && (
                    <p className="text-[10px] text-amber-400 ml-5">Disabled: user has no payment method. Use override or send a payment link.</p>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="sync" checked={!syncStripe} onChange={() => setSyncStripe(false)} className="accent-[#FF6B00]" />
                    <span className="text-sm text-[#aaa]">Override plan in app only (no Stripe)</span>
                  </label>
                  {!syncStripe && (
                    <p className="text-xs text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> This will desync the user's plan from Stripe. Use only for testing or temporary overrides.
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={handleChangePlan} disabled={actionLoading}
                  className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-9 text-sm" data-testid="admin-confirm-plan-change">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : planChoice === 'free' ? 'Downgrade to Free' : syncStripe ? `Change to ${planChoice} (Stripe)` : `Override to ${planChoice}`}
                </Button>

                {/* Payment link button — only for paid plans when user has no payment method */}
                {planChoice !== 'free' && paymentStatus && !paymentStatus.has_payment_method && !paymentStatus.has_active_subscription && (
                  <Button onClick={handleSendPaymentLink} disabled={paymentLinkLoading} variant="outline"
                    className="border-[#333] text-[#aaa] hover:text-white rounded-xl h-9 text-sm" data-testid="admin-send-payment-link-btn">
                    {paymentLinkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Generate Payment Link</>}
                  </Button>
                )}
              </div>

              {/* Generated payment link */}
              {generatedLink && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-3 space-y-2" data-testid="admin-payment-link-result">
                  <p className="text-xs text-emerald-400 font-medium">Payment link generated. Send to user:</p>
                  <div className="flex gap-2">
                    <input readOnly value={generatedLink}
                      className="flex-1 bg-[#0d0d0d] border border-[#262626] rounded-lg text-xs text-white p-2 font-mono truncate" />
                    <Button size="sm" onClick={() => { navigator.clipboard.writeText(generatedLink); toast.success('Link copied!'); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs shrink-0" data-testid="admin-copy-payment-link">
                      Copy
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#555]">When the user pays via this link, their subscription will be created automatically via webhook.</p>
                </div>
              )}
            </div>
          )}

          {action === 'credits' && (
            <div className="bg-[#0d0d0d] border border-[#262626] rounded-xl p-4 space-y-3" data-testid="admin-credits-action-panel">
              <p className="text-sm text-white font-medium">Add pack credits for {selected.email}</p>
              <div className="flex gap-2 items-center">
                <Input type="number" value={creditsToAdd} onChange={e => setCreditsToAdd(parseInt(e.target.value) || 0)} min={1} max={1000}
                  className="w-24 bg-[#1a1a1a] border-[#333] text-white rounded-xl" data-testid="admin-credits-input" />
                <span className="text-sm text-[#666]">credits</span>
              </div>
              <Button onClick={handleAddCredits} disabled={actionLoading || creditsToAdd <= 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-9 text-sm" data-testid="admin-confirm-add-credits">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${creditsToAdd} credits`}
              </Button>
            </div>
          )}

          {action === 'remove_credits' && (
            <div className="bg-[#0d0d0d] border border-[#262626] rounded-xl p-4 space-y-3" data-testid="admin-remove-credits-action-panel">
              <p className="text-sm text-white font-medium">Remove pack credits from {selected.email}</p>
              <p className="text-xs text-[#666]">Current pack credits: <span className="text-[#FF6B00] font-mono">{selected.pack_credits}</span></p>
              <div className="flex gap-2 items-center">
                <Input type="number" value={creditsToRemove} onChange={e => setCreditsToRemove(parseInt(e.target.value) || 0)} min={1} max={selected.pack_credits}
                  className="w-24 bg-[#1a1a1a] border-[#333] text-white rounded-xl" data-testid="admin-remove-credits-input" />
                <span className="text-sm text-[#666]">credits</span>
              </div>
              {creditsToRemove > selected.pack_credits && (
                <p className="text-xs text-red-400">Cannot remove more than {selected.pack_credits} credits</p>
              )}
              <Button onClick={handleRemoveCredits} disabled={actionLoading || creditsToRemove <= 0 || creditsToRemove > selected.pack_credits}
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl h-9 text-sm" data-testid="admin-confirm-remove-credits">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Remove ${creditsToRemove} credits`}
              </Button>
            </div>
          )}

          {action === 'reset' && (
            <div className="bg-[#0d0d0d] border border-[#262626] rounded-xl p-4 space-y-3" data-testid="admin-reset-action-panel">
              <p className="text-sm text-white font-medium">Reset subscription credits for {selected.email}</p>
              <p className="text-xs text-[#666]">This will reset {selected.credits_used} used credits back to 0, giving the user their full {selected.credits_limit} credits.</p>
              <Button onClick={handleResetCredits} disabled={actionLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl h-9 text-sm" data-testid="admin-confirm-reset-credits">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Credits'}
              </Button>
            </div>
          )}

          {action === 'cancel' && (
            <div className="bg-[#0d0d0d] border border-red-500/20 rounded-xl p-4 space-y-3" data-testid="admin-cancel-action-panel">
              <p className="text-sm text-white font-medium">Cancel subscription for {selected.email}</p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="cancelMode" checked={cancelMode === 'immediate'} onChange={() => setCancelMode('immediate')} className="accent-red-500" />
                  <span className="text-sm text-[#aaa]">Cancel immediately: move to Free plan now</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="cancelMode" checked={cancelMode === 'period_end'} onChange={() => setCancelMode('period_end')} className="accent-red-500" />
                  <span className="text-sm text-[#aaa]">Cancel at period end: keep plan until billing cycle ends</span>
                </label>
              </div>
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {cancelMode === 'immediate'
                  ? `This will cancel ${selected.email}'s ${selected.plan} subscription immediately and move them to Free.`
                  : `The subscription will cancel at the end of the billing period. User keeps ${selected.plan} access until then.`}
              </p>
              <Button onClick={handleCancelSubscription} disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl h-9 text-sm" data-testid="admin-confirm-cancel-sub">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : cancelMode === 'immediate' ? 'Cancel Immediately' : 'Cancel at Period End'}
              </Button>
            </div>
          )}

          {/* Expandable: Documents */}
          <div className="border border-[#1e1e1e] rounded-xl overflow-hidden">
            <button onClick={fetchUserDocs}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#1a1a1a]/50 transition-colors"
              data-testid="admin-toggle-docs">
              <span className="text-xs font-medium text-[#aaa]">Documents ({selected.documents_count})</span>
              {docsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#555]" /> :
                showDocs ? <ChevronUp className="w-3.5 h-3.5 text-[#555]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#555]" />}
            </button>
            {showDocs && (
              <div className="border-t border-[#1e1e1e] max-h-48 overflow-y-auto" data-testid="admin-user-docs-list">
                {userDocs.length === 0 ? (
                  <p className="text-xs text-[#555] p-3">No documents</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead><tr className="text-[#555] border-b border-[#1e1e1e]">
                      <th className="text-left px-3 py-2 font-medium">Name</th>
                      <th className="text-left px-3 py-2 font-medium">Date</th>
                      <th className="text-left px-3 py-2 font-medium">Time</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-right px-3 py-2 font-medium">Q</th>
                      <th className="text-right px-3 py-2 font-medium">T</th>
                      {/* #158a: el estado decia QUE fallo, nunca POR QUE. */}
                      <th className="text-left px-3 py-2 font-medium">Error</th>
                      {/* #164b: la traduccion del de al lado. En español a proposito:
                          esta columna la lee Carlos, no un usuario. */}
                      <th className="text-left px-3 py-2 font-medium">Causa probable</th>
                    </tr></thead>
                    <tbody>{userDocs.map((d, i) => (
                      <tr key={d.id || i} className="border-b border-[#1a1a1a] hover:bg-[#0d0d0d]" title={formatIsoTooltip(d.uploaded)}>
                        <td className="px-3 py-2 text-white truncate max-w-[200px]">{d.name}</td>
                        <td className="px-3 py-2 text-[#666] whitespace-nowrap">{formatDate(d.uploaded) || 'N/A'}</td>
                        <td className="px-3 py-2 text-[#666] whitespace-nowrap">{formatTime(d.uploaded)}</td>
                        <td className="px-3 py-2"><Badge className={`text-[9px] ${d.status === 'ready' ? 'bg-emerald-500/15 text-emerald-400' : d.status === 'error' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>{d.status}</Badge></td>
                        <td className="px-3 py-2 text-right text-[#888] font-mono">{d.questions_count}</td>
                        <td className="px-3 py-2 text-right text-[#888] font-mono">{d.topics_count}</td>
                        {/* Recorte a 60 con el texto entero en el tooltip: la fila no puede
                            crecer por un mensaje largo, pero el mensaje no se pierde.
                            Celda vacia cuando no hay error (null o ausente). */}
                        <td className="px-3 py-2 text-[#a66] max-w-[220px] truncate"
                            title={d.error_message || ''}
                            data-testid="admin-doc-error">
                          {d.error_message ? truncateText(d.error_message, 60) : ''}
                        </td>
                        {/* Misma regla que la de al lado: recorte en la celda, texto
                            entero en el tooltip. Vacia cuando el doc no fallo. */}
                        <td className="px-3 py-2 text-[#c9a227] max-w-[260px] truncate"
                            title={d.probable_cause || ''}
                            data-testid="admin-doc-cause">
                          {d.probable_cause ? truncateText(d.probable_cause, 80) : ''}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Expandable: Payment History */}
          <div className="border border-[#1e1e1e] rounded-xl overflow-hidden">
            <button onClick={fetchUserPayments}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#1a1a1a]/50 transition-colors"
              data-testid="admin-toggle-payments">
              <span className="text-xs font-medium text-[#aaa]">Payment History</span>
              {paymentsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#555]" /> :
                showPayments ? <ChevronUp className="w-3.5 h-3.5 text-[#555]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#555]" />}
            </button>
            {showPayments && (
              <div className="border-t border-[#1e1e1e] max-h-48 overflow-y-auto" data-testid="admin-user-payments-list">
                {userPayments.length === 0 ? (
                  <p className="text-xs text-[#555] p-3">No payments</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead><tr className="text-[#555] border-b border-[#1e1e1e]">
                      <th className="text-left px-3 py-2 font-medium">Date</th>
                      <th className="text-left px-3 py-2 font-medium">Time</th>
                      <th className="text-left px-3 py-2 font-medium">Description</th>
                      <th className="text-right px-3 py-2 font-medium">Amount</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                    </tr></thead>
                    <tbody>{userPayments.map((p, i) => (
                      <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#0d0d0d]" title={formatIsoTooltip(p.date)}>
                        <td className="px-3 py-2 text-[#666] whitespace-nowrap">{formatDate(p.date) || 'N/A'}</td>
                        <td className="px-3 py-2 text-[#666] whitespace-nowrap">{formatTime(p.date)}</td>
                        <td className="px-3 py-2 text-white">{p.description}</td>
                        <td className="px-3 py-2 text-right text-[#FF6B00] font-mono">${typeof p.amount === 'number' ? p.amount.toFixed(2) : p.amount}</td>
                        <td className="px-3 py-2"><Badge className={`text-[9px] ${p.status === 'paid' ? 'bg-emerald-500/15 text-emerald-400' : p.status === 'refunded' ? 'bg-blue-500/15 text-blue-400' : p.status === 'initiated' ? 'bg-[#333]/50 text-[#888]' : 'bg-red-500/15 text-red-400'}`}>{p.status === 'initiated' ? 'not completed' : p.status}</Badge></td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit log */}
      {showLog && (
        <div className="glass-card rounded-[16px] p-5 space-y-3" data-testid="admin-audit-log">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Audit Log (last 50)</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowLog(false)} className="text-[#555] hover:text-white">
              <X className="w-4 h-4" />
            </Button>
          </div>
          {adminLog.length === 0 ? (
            <p className="text-xs text-[#555]">No actions logged yet.</p>
          ) : (
            <div className="max-h-60 overflow-y-auto space-y-1">
              {adminLog.map((log, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-2 border-b border-[#1a1a1a]" title={formatIsoTooltip(log.timestamp)}>
                  <span className="text-[#666] font-mono w-20 shrink-0 whitespace-nowrap">{formatDate(log.timestamp)}</span>
                  <span className="text-[#444] font-mono w-16 shrink-0 whitespace-nowrap">{formatTime(log.timestamp)}</span>
                  <Badge className="text-[9px] bg-[#1a1a1a] text-[#aaa]">{log.action}</Badge>
                  <span className="text-[#888] truncate">{log.target_user_id}</span>
                  <span className="text-[#555] truncate">{JSON.stringify(log.details)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── PIPE-INST T4 (firma #104-v2) — COSTOS INSTITUCIONALES ──
//
// LA VISTA QUE EL PROFESOR JAMAS VE. Vive en el area de admin, detras del mismo
// `AdminRoute` que el resto de esta pantalla, y su endpoint lleva el mismo
// `_check_admin` de un solo correo. No hay ni un enlace hacia aqui desde ninguna
// superficie institucional.
//
// DOS COLUMNAS QUE NO SON LO MISMO, y por eso van al lado y no sumadas:
//   Real        la suma de la bitacora: tokens medidos y tarifados.
//   Proyeccion  lo que el documento DEBERIA costar por su tamano.
// Cuando la fila de tarifas de `gpt-5.6-terra` todavia no existe, las filas
// llegan sin costo y se cuentan aparte: un total bajo que en realidad significa
// «no sabemos» es peor que no tener total.
//
// EN ESPANOL, como el resto del carril institucional. Es la unica seccion de esta
// pantalla que lo esta, y es a proposito: sus numeros hablan de una tuberia cuyo
// vocabulario entero —tema, paquete, a medias— se firmo en espanol.
// SE EXPORTA solo para que su prueba la monte sola: montar el panel entero
// arrastraria una docena de secciones que nada tienen que ver con esto.
// t7-2b remate, R3e: EL TEXTO DEL RENGLON "Chat del panel". Preguntas (una fila de la bitacora
// del chat por pregunta), llamadas al modelo (los dos pasos y las fotos que la fila anoto) y los
// USD del mes con CUATRO decimales: una pregunta cuesta del orden de 0.002 USD y con dos saldria
// $0.00. Las filas sin tarifa se dicen aparte. Sin respuesta de la ruta (o con otra forma), dice
// "sin datos" y no inventa un cero. Solo espanol, como el resto de la tarjeta.
function textoDelChat(chat) {
  const m = chat && chat.mes;
  if (!m || typeof m.preguntas !== 'number') return 'sin datos';
  const llamadas = Number(m.llamadas || 0);
  const partes = [
    `${m.preguntas} ${m.preguntas === 1 ? 'pregunta' : 'preguntas'}`,
    `${llamadas} ${llamadas === 1 ? 'llamada' : 'llamadas'}`,
    `$${Number(m.usd || 0).toFixed(4)}`,
  ];
  if (m.sin_tarifa > 0) partes.push(`sin tarifa: ${m.sin_tarifa}`);
  return partes.join(', ');
}

export function CostosInstitucionalesSection() {
  const [abierta, setAbierta] = useState(false);
  const [datos, setDatos] = useState(null);
  // t7-2b remate, R3e: lo que costo el chat del panel (`/sb-ctrl/chat-panel/costos`); null sin datos
  const [chat, setChat] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [filtro, setFiltro] = useState('mes');
  // T4.5 — el recorte por documento de la vista «Por tema». NACE VACIO («todos»)
  // a proposito: la vista tiene que seguir abriendo con todas las filas, en el
  // orden por gasto que ya manda el servidor.
  const [docSel, setDocSel] = useState('');

  const cargar = async () => {
    setCargando(true);
    // t7-2b remate, R3e: la gemela del chat se pide a la par. Vive en `routes/grupo_chat.py`
    // (`routes/admin.py` no se toca) y su falla no apaga la tarjeta ni avisa: el renglon dice
    // "sin datos".
    const pedirChat = api.get('/sb-ctrl/chat-panel/costos')
      .then((r) => (r && r.data && r.data.chat_panel) || null)
      .catch(() => null);
    try {
      const res = await api.get('/sb-ctrl/pi-costos');
      setDatos(res.data);
    } catch { toast.error('No se pudieron leer los costos institucionales'); }
    finally {
      setChat(await pedirChat);
      setCargando(false);
    }
  };

  useEffect(() => {
    if (abierta && datos === null) cargar();
    // Misma carga perezosa que Model Pricing: el efecto corre SOLO al desplegar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierta]);

  const usd = (v) => (v === null || v === undefined ? '-' : `$${Number(v).toFixed(2)}`);

  // EL SELECTOR ES DE CLIENTE, Y ES UNA DECISION (T4.5). El endpoint ya manda
  // `document_id` y `filename` en CADA fila de `por_tema`, asi que recortar aqui
  // no cuesta ni una lectura mas; hacerlo por query param obligaria a re-pedir
  // los datos en cada cambio de seleccion —hoy el efecto solo corre al
  // desplegar— y a estrenar un parametro para una respuesta que ya cabe entera.
  //
  // POR TEMA, Y SOLO POR TEMA: es la unica vista donde las filas de todos los
  // documentos se mezclan sin nada que las distinga (dos «Introduccion» de dos
  // PDFs son dos renglones identicos, y todas las filas de mapa se llaman igual).
  // «Por documento» ya viene una fila por documento: ahi el selector no anade
  // nada y quitaria filas sin motivo.
  const documentos = (datos?.por_documento || [])
    .map(d => ({ id: d.document_id, nombre: d.filename || d.document_id }))
    .filter(d => d.id);

  const base = datos
    ? (filtro === 'tema' ? datos.por_tema : datos.por_documento)
    : [];
  const filas = (filtro === 'tema' && docSel)
    ? base.filter(f => f.document_id === docSel)
    : base;

  return (
    <div className="space-y-4" data-testid="pi-costos-section">
      <button onClick={() => setAbierta(!abierta)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors"
        data-testid="toggle-pi-costos">
        <ChevronDown className={`w-4 h-4 transition-transform ${abierta ? 'rotate-180' : ''}`} />
        {abierta ? 'Ocultar' : 'Ver'} Costos institucionales
      </button>

      {abierta && (
        <div className="glass-card rounded-[16px] p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white" data-testid="pi-costos-titulo">
                Costos institucionales
              </h2>
            </div>
            <Button variant="outline" size="sm" onClick={cargar} disabled={cargando}
              className="border-[#262626] text-[#a0a0a0] hover:text-white rounded-lg text-xs"
              data-testid="pi-costos-refresh">
              <RefreshCw className={`w-3 h-3 mr-1 ${cargando ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          <p className="text-sm text-white" data-testid="pi-costos-gasto-mes">
            {`Gasto del mes: ${usd(datos ? datos.mes.real : 0)}`}
          </p>

          <div className="flex flex-wrap gap-2">
            {[['mes', 'Este mes'], ['documento', 'Por documento'], ['tema', 'Por tema']]
              .map(([clave, texto]) => (
                <button key={clave} onClick={() => setFiltro(clave)}
                  className={`text-xs rounded-lg px-3 py-1.5 border ${filtro === clave
                    ? 'border-amber-400/60 text-amber-300 bg-amber-400/10'
                    : 'border-[#262626] text-[#a0a0a0] hover:text-white'}`}
                  data-testid={`pi-costos-filtro-${clave}`}>{texto}</button>
              ))}
          </div>

          {filtro === 'tema' && documentos.length > 0 && (
            <select value={docSel} onChange={(e) => setDocSel(e.target.value)}
              className="w-full sm:w-auto text-xs rounded-lg px-3 py-1.5 border border-[#262626] bg-[#0d0d0d] text-[#e5e5e5]"
              data-testid="pi-costos-doc-select">
              <option value="">Todos los documentos</option>
              {documentos.map(d => (
                <option key={d.id} value={d.id}>{d.nombre}</option>
              ))}
            </select>
          )}

          {cargando ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#FF6B00]" /></div>
          ) : filtro === 'mes' ? (
            <div className="grid grid-cols-2 gap-2 text-sm" data-testid="pi-costos-mes">
              <span className="text-[#a0a0a0]">Real</span>
              <span className="text-right text-white font-mono">{usd(datos ? datos.mes.real : 0)}</span>
              <span className="text-[#a0a0a0]">Proyección</span>
              <span className="text-right text-white font-mono">{usd(datos ? datos.mes.proyeccion : 0)}</span>
              <span className="text-[#a0a0a0]">Llamadas</span>
              <span className="text-right text-white font-mono">{datos ? datos.mes.llamadas : 0}</span>
              <span className="text-[#a0a0a0]">Sin tarifa</span>
              <span className="text-right text-white font-mono">{datos ? datos.mes.sin_tarifa : 0}</span>
              {/* PIPE-INST T5.3 (firma #142) — EL DATO DEL DÍA, JUNTO A LOS DEL
                  MES. El carril tiene un fusible de 200 trabajos por día y hasta
                  ahora esta vista sólo enseñaba ventanas que no eran la suya (el
                  mes y el total histórico), así que saber cuánto cupo quedaba
                  hoy pedía mirar Mongo a mano.

                  EL NÚMERO SE PINTA, NO SE CALCULA: lo manda el servidor leyendo
                  la MISMA llave diaria del fusible (`fusible.hay_cupo`), para
                  que este número y el tope del que habla no puedan cambiar de
                  día a horas distintas. Derivarlo aquí —de la fecha del
                  navegador, del corte por documento— sería estrenar un segundo
                  «hoy». */}
              {/* TANDA 26b (#238) — LA ETIQUETA DICE QUE CUENTA, Y APARECE SU
                  GEMELA. «Llamadas hoy» era ambiguo hasta con el comentario
                  delante: no son peticiones HTTP al modelo (ésas viven en
                  «Llamadas», arriba) sino los TRABAJOS que el carril del banco
                  encoló hoy. Y al lado nace el otro fusible, el del consumidor,
                  que hasta esta tanda sólo se podía leer en Mongo a mano.

                  LOS DOS TOPES SALEN DEL SERVIDOR. Escribir «/ 200» aquí sería
                  estrenar una segunda fuente para un número que una variable de
                  entorno puede mover; el día que se moviera, el tablero
                  enseñaría un techo que ya no existe. */}
              <span className="text-[#a0a0a0]"
                data-testid="pi-costos-llamadas-hoy-etiqueta">Trabajos del banco hoy</span>
              <span className="text-right text-white font-mono"
                data-testid="pi-costos-llamadas-hoy">
                {datos ? (datos.mes.llamadas_hoy || 0) : 0} / {datos ? (datos.mes.llamadas_hoy_tope || 0) : 0}
              </span>
              <span className="text-[#a0a0a0]"
                data-testid="pi-costos-docs-consumidor-etiqueta">Documentos hoy (consumidor)</span>
              <span className="text-right text-white font-mono"
                data-testid="pi-costos-docs-consumidor">
                {datos ? (datos.mes.consumer_docs_today || 0) : 0} / {datos ? (datos.mes.consumer_docs_cap || 0) : 0}
              </span>
              {/* t7-2b remate, R3e: EL CHAT DEL PANEL, SU PROPIO RENGLON. Sus filas de la
                  bitacora (`gpt-5.6-luna_pi_chat`, tipo chat_panel) no entran en "Real" ni en
                  "Gasto del mes", que son del banco: van aqui, con su mes. */}
              <span className="text-[#a0a0a0]"
                data-testid="pi-costos-chat-etiqueta">Chat del panel</span>
              <span className="text-right text-white font-mono"
                data-testid="pi-costos-chat">
                {textoDelChat(chat)}
              </span>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="pi-costos-tabla">
              <div className="grid grid-cols-[1fr_80px_80px] gap-2 text-[10px] text-[#555] uppercase px-2">
                <span>{filtro === 'tema' ? 'Tema' : 'Documento'}</span>
                <span className="text-right">Real</span>
                <span className="text-right">Proyección</span>
              </div>
              {filas.length === 0 ? (
                <p className="text-xs text-[#6a6a6a] px-2" data-testid="pi-costos-vacio">
                  Todavía no hay gasto institucional registrado.
                </p>
              ) : filas.map((f, i) => (
                <div key={`${f.document_id}-${f.tema_id || 'doc'}-${i}`}
                  className="grid grid-cols-[1fr_80px_80px] gap-2 items-center bg-[#0d0d0d] rounded-lg px-2.5 py-2 border border-[#1a1a1a]"
                  data-testid={`pi-costos-fila-${i}`}>
                  <span className="text-xs text-white truncate">
                    {filtro === 'tema' ? (f.nombre || f.filename) : (f.filename || f.document_id)}
                    {f.sin_tarifa > 0 && (
                      <span className="ml-2 text-[10px] text-amber-400">sin tarifa: {f.sin_tarifa}</span>
                    )}
                  </span>
                  <span className="text-right text-xs text-white font-mono">{usd(f.real)}</span>
                  <span className="text-right text-xs text-[#a0a0a0] font-mono">{usd(f.proyeccion)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Model Pricing Management Section ──
// Exportada para su suite, con el mismo criterio (y el mismo precedente) que
// `CostosInstitucionalesSection`: el componente no cambia, solo deja de ser privado.
export function ModelPricingSection() {
  const [showSection, setShowSection] = useState(false);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModel, setEditModel] = useState(null);
  const [editInput, setEditInput] = useState('');
  const [editOutput, setEditOutput] = useState('');
  const [editMaxTokens, setEditMaxTokens] = useState('');
  const [addModel, setAddModel] = useState('');
  const [addInput, setAddInput] = useState('');
  const [addOutput, setAddOutput] = useState('');
  const [addMaxTokens, setAddMaxTokens] = useState('');
  // is_reasoning_model: the endpoint has always accepted it (POST /sb-ctrl/model-pricing) and
  // config.load_reasoning_models_from_db feeds MODELS_NO_TEMPERATURE from it, but this form
  // never sent it — so a reasoning model added here landed without the flag and every Call
  // A/B/C to it came back 400 ("temperature does not support 0.3 with this model").
  const [editReasoning, setEditReasoning] = useState(false);
  const [addReasoning, setAddReasoning] = useState(false);
  // T6 (firma #290) — LA UNIDAD DE LA TARIFA. Un modelo de transcripcion no se
  // cobra por token: Groq y OpenAI facturan por DURACION del audio. Sin este
  // selector, la unica forma de capturar una tarifa de audio seria disfrazarla de
  // tarifa por token, y la columna diria "tokens" siendo segundos.
  // El default es 'token', que es lo que significan las 21 filas que ya existen.
  const [editUnidad, setEditUnidad] = useState('token');
  const [addUnidad, setAddUnidad] = useState('token');
  const [saving, setSaving] = useState(false);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const res = await api.get('/sb-ctrl/model-pricing');
      setPricing(res.data.pricing || []);
    } catch { toast.error('Failed to load pricing'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (showSection && pricing.length === 0) fetchPricing();
    // Lazy fetch-on-expand: this effect must rerun ONLY when the section toggles;
    // the data guard is read fresh on each run and must not be a dependency (it
    // would refire the effect when the data arrives). Behavior predates F4.3a —
    // the disable exists so CI=true builds (warnings-as-errors) stay green.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const handleSave = async (model, inputRate, outputRate, maxTokens, isReasoning, unidad) => {
    setSaving(true);
    try {
      const payload = { model, input_rate: parseFloat(inputRate), output_rate: parseFloat(outputRate) };
      if (maxTokens) payload.max_completion_tokens = parseInt(maxTokens);
      // Always sent, in both directions: the endpoint only touches the flag when the field is
      // present (`if is_reasoning is not None`), so omitting it on an edit would silently keep
      // a stale value, and sending false is how a model gets un-marked.
      payload.is_reasoning_model = !!isReasoning;
      // T6: se manda SIEMPRE, por la misma razon exacta que la bandera de arriba.
      // El endpoint solo toca el campo cuando viene (`if unidad is not None`), asi
      // que omitirlo en una edicion conservaria un valor viejo — y mandar 'token'
      // es la unica forma de devolver a tokens una fila que se marco por error.
      payload.unidad = unidad || 'token';
      await api.post('/sb-ctrl/model-pricing', payload);
      toast.success(`Pricing saved for ${model}`);
      setEditModel(null);
      setAddModel(''); setAddInput(''); setAddOutput(''); setAddMaxTokens(''); setAddReasoning(false);
      setAddUnidad('token');
      fetchPricing();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (model) => {
    setDeleting(true);
    try {
      await api.delete(`/sb-ctrl/models/${encodeURIComponent(model)}/cascade`);
      toast.success(`Model '${model}' removed from all configurations`);
      setConfirmDelete(null);
      fetchPricing();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setConfirmDelete(null);
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4" data-testid="model-pricing-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-model-pricing">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Hide' : 'Show'} Model Pricing
      </button>

      {showSection && (
        <div className="glass-card rounded-[16px] p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">Model Pricing</h2>
              <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full">USD per 1M tokens</span>
            </div>
            <Button variant="outline" size="sm" onClick={fetchPricing} disabled={loading}
              className="border-[#262626] text-[#a0a0a0] hover:text-white rounded-lg text-xs" data-testid="refresh-pricing">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          <p className="text-xs text-[#6a6a6a] -mt-2">Used for A/B test cost estimates and processing cost tracking. Edit rates here when OpenAI changes pricing, no deploy needed.</p>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#FF6B00]" /></div>
          ) : (
            <div className="space-y-1.5">
              {/* Header */}
              <div className="grid grid-cols-[1fr_80px_80px_80px_60px] gap-2 text-[10px] text-[#555] uppercase px-2">
                <span>Model</span><span className="text-right">Input</span><span className="text-right">Output</span><span className="text-right">Max Tok</span><span />
              </div>
              {pricing.map(p => (
                <div key={p.model} className="grid grid-cols-[1fr_80px_80px_80px_60px] gap-2 items-center bg-[#0d0d0d] rounded-lg px-2.5 py-2 border border-[#1a1a1a]" data-testid={`pricing-row-${p.model}`}>
                  {editModel === p.model ? (
                    <>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-white font-mono">{p.model}</span>
                        <label className="flex items-center gap-1 text-[10px] text-[#888] cursor-pointer">
                          <input type="checkbox" checked={editReasoning}
                            onChange={e => setEditReasoning(e.target.checked)}
                            className="accent-violet-400 w-3 h-3"
                            data-testid={`pricing-edit-reasoning-${p.model}`} />
                          Reasoning model
                        </label>
                        {/* T6: la unidad de la tarifa. `segundo` es para los
                            modelos de transcripcion, que se facturan por duracion
                            del audio y no por token. */}
                        <select value={editUnidad}
                          onChange={e => setEditUnidad(e.target.value)}
                          className="bg-[#141414] border border-[#333] rounded text-[10px] text-white p-0.5"
                          data-testid={`pricing-edit-unidad-${p.model}`}>
                          <option value="token">per 1M tokens</option>
                          <option value="segundo">per second of audio</option>
                        </select>
                      </div>
                      <input type="number" step="0.01" value={editInput} onChange={e => setEditInput(e.target.value)}
                        className="bg-[#141414] border border-[#333] rounded text-xs text-white p-1 font-mono text-right w-full" />
                      <input type="number" step="0.01" value={editOutput} onChange={e => setEditOutput(e.target.value)}
                        className="bg-[#141414] border border-[#333] rounded text-xs text-white p-1 font-mono text-right w-full" />
                      <input type="number" step="1" value={editMaxTokens} onChange={e => setEditMaxTokens(e.target.value)}
                        className="bg-[#141414] border border-[#333] rounded text-xs text-white p-1 font-mono text-right w-full"
                        placeholder="16384" data-testid={`pricing-edit-tokens-${p.model}`} />
                      <div className="flex gap-1">
                        <button onClick={() => handleSave(p.model, editInput, editOutput, editMaxTokens, editReasoning, editUnidad)} disabled={saving}
                          className="text-emerald-400 hover:text-emerald-300 text-[10px]" data-testid={`pricing-save-${p.model}`}>
                          {saving ? '...' : 'Save'}
                        </button>
                        <button onClick={() => setEditModel(null)} className="text-[#555] hover:text-white text-[10px]">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-white font-mono">{p.model}</span>
                        {p.source === 'custom' && <span className="text-[8px] bg-violet-500/15 text-violet-400 px-1 py-0 rounded">custom</span>}
                        {p.is_reasoning_model && <span className="text-[8px] bg-violet-500/15 text-violet-400 px-1 py-0 rounded" data-testid={`pricing-reasoning-badge-${p.model}`}>Reasoning model</span>}
                        {/* T6: SOLO se enseña cuando NO es la unidad de siempre. El
                            encabezado de la tarjeta ya dice «USD per 1M tokens», así
                            que una etiqueta «token» en las 21 filas normales sería
                            ruido; lo que hay que poder ver de un vistazo es la fila
                            que se cobra distinto. */}
                        {p.unidad === 'segundo' && <span className="text-[8px] bg-sky-500/15 text-sky-300 px-1 py-0 rounded" data-testid={`pricing-unidad-badge-${p.model}`}>per second</span>}
                      </div>
                      <span className="text-xs text-[#aaa] font-mono text-right">${p.input_rate}</span>
                      <span className="text-xs text-[#aaa] font-mono text-right">${p.output_rate}</span>
                      <span className="text-xs text-[#aaa] font-mono text-right">{(p.max_completion_tokens || 16384).toLocaleString()}</span>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setEditModel(p.model); setEditInput(String(p.input_rate)); setEditOutput(String(p.output_rate)); setEditMaxTokens(String(p.max_completion_tokens || '')); setEditReasoning(!!p.is_reasoning_model); setEditUnidad(p.unidad || 'token'); }}
                          className="text-[#555] hover:text-[#FF6B00] text-[10px]" data-testid={`pricing-edit-${p.model}`}>Edit</button>
                        <button onClick={() => setConfirmDelete(p.model)} className="text-[#555] hover:text-red-400 text-[10px]" data-testid={`pricing-delete-${p.model}`}>Del</button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* Add new model */}
              <div className="grid grid-cols-[1fr_80px_80px_80px_60px] gap-2 items-center bg-[#111] rounded-lg px-2.5 py-2 border border-dashed border-[#262626] mt-2">
                <div className="flex flex-col gap-1">
                  <input value={addModel} onChange={e => setAddModel(e.target.value)} placeholder="New model name..."
                    className="bg-transparent border-none text-xs text-white font-mono outline-none placeholder:text-[#333]" data-testid="pricing-add-model" />
                  <label className="flex items-center gap-1 text-[10px] text-[#888] cursor-pointer">
                    <input type="checkbox" checked={addReasoning}
                      onChange={e => setAddReasoning(e.target.checked)}
                      className="accent-violet-400 w-3 h-3" data-testid="pricing-add-reasoning" />
                    Reasoning model
                  </label>
                  {/* T6 (firma #290): la unidad. `per second of audio` es lo que
                      hace capturable una tarifa de transcripcion — Groq
                      whisper-large-v3-turbo son $0.04/hora = 0.0000111111 por
                      segundo, y va en la columna de Input. */}
                  <select value={addUnidad} onChange={e => setAddUnidad(e.target.value)}
                    className="bg-[#141414] border border-[#262626] rounded text-[10px] text-white p-0.5"
                    data-testid="pricing-add-unidad">
                    <option value="token">per 1M tokens</option>
                    <option value="segundo">per second of audio</option>
                  </select>
                </div>
                <input type="number" step="0.01" value={addInput} onChange={e => setAddInput(e.target.value)} placeholder="0.00"
                  className="bg-[#141414] border border-[#262626] rounded text-xs text-white p-1 font-mono text-right w-full" data-testid="pricing-add-input" />
                <input type="number" step="0.01" value={addOutput} onChange={e => setAddOutput(e.target.value)} placeholder="0.00"
                  className="bg-[#141414] border border-[#262626] rounded text-xs text-white p-1 font-mono text-right w-full" data-testid="pricing-add-output" />
                <input type="number" step="1" value={addMaxTokens} onChange={e => setAddMaxTokens(e.target.value)} placeholder="16384"
                  className="bg-[#141414] border border-[#262626] rounded text-xs text-white p-1 font-mono text-right w-full" data-testid="pricing-add-tokens" />
                <button onClick={() => addModel.trim() && addInput && addOutput && handleSave(addModel.trim(), addInput, addOutput, addMaxTokens, addReasoning, addUnidad)}
                  disabled={!addModel.trim() || !addInput || !addOutput || saving}
                  className="text-emerald-400 hover:text-emerald-300 text-[10px] disabled:text-[#333]" data-testid="pricing-add-save">
                  {saving ? '...' : 'Add'}
                </button>
              </div>
            </div>
          )}

          {/* Cascade delete confirmation dialog */}
          {confirmDelete && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-3" data-testid="delete-confirm-dialog">
              <p className="text-sm text-red-400 font-medium">Delete {confirmDelete}?</p>
              <p className="text-xs text-[#888]">This will remove it from pricing, token limits, A/B test suggestions, and reasoning model list. This cannot be undone.</p>
              <div className="flex items-center gap-2">
                <Button onClick={() => handleDelete(confirmDelete)} disabled={deleting}
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-xs" data-testid="delete-confirm-yes">
                  {deleting ? 'Deleting...' : 'Yes, Delete Everywhere'}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmDelete(null)}
                  className="text-[#555] hover:text-white text-xs" data-testid="delete-confirm-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Equalization system removed — replaced by Call C blind distractor generation ──

// ── Token Budget Formula Config ──
function TokenFormulaConfig() {
  const [showSection, setShowSection] = useState(false);
  const [params, setParams] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/sb-ctrl/token-formula-config');
      setParams(res.data.params);
      setDefaults(res.data.defaults);
      setDraft(Object.fromEntries(Object.entries(res.data.params).map(([k, v]) => [k, String(v)])));
    } catch { toast.error('Failed to load token formula'); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(draft)) {
        if (v.trim()) payload[k] = parseInt(v);
      }
      const res = await api.post('/sb-ctrl/token-formula-config', { params: payload });
      setParams(res.data.params);
      setDraft(Object.fromEntries(Object.entries(res.data.params).map(([k, v]) => [k, String(v)])));
      toast.success('Token formula saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (showSection && !params) fetchConfig();
    // Lazy fetch-on-expand: this effect must rerun ONLY when the section toggles;
    // the data guard is read fresh on each run and must not be a dependency (it
    // would refire the effect when the data arrives). Behavior predates F4.3a —
    // the disable exists so CI=true builds (warnings-as-errors) stay green.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const fields = [
    { key: 'questions_base', label: 'Questions base tokens' },
    { key: 'questions_per_1k', label: 'Questions tokens per 1K chars' },
    { key: 'questions_floor', label: 'Questions minimum' },
    { key: 'flashcards_base', label: 'Flashcards base tokens' },
    { key: 'flashcards_per_1k', label: 'Flashcards tokens per 1K chars' },
    { key: 'flashcards_floor', label: 'Flashcards minimum' },
  ];

  return (
    <div className="space-y-4" data-testid="token-formula-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-token-formula">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Hide' : 'Show'} Token Budget Formula
      </button>

      {showSection && params && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white">Dynamic max_completion_tokens</h3>
            <p className="text-xs text-[#6a6a6a]">Formula: base + (doc_chars / 1000) * per_1k, floored at minimum, capped at model max</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {fields.map(f => (
              <div key={f.key} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-3">
                <label className="text-[10px] text-[#555] uppercase block mb-1">{f.label}</label>
                <div className="flex items-center gap-2">
                  <input type="number" value={draft[f.key] || ''} onChange={e => setDraft(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none"
                    data-testid={`token-formula-${f.key}`} />
                  {defaults && <span className="text-[9px] text-[#333] whitespace-nowrap">def: {defaults[f.key]?.toLocaleString()}</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={saveConfig} disabled={saving}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs" data-testid="token-formula-save">
              {saving ? 'Saving...' : 'Save Formula'}
            </Button>
            <Button variant="ghost" onClick={() => { if (defaults) setDraft(Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]))); }}
              className="text-[#555] hover:text-white text-xs" data-testid="token-formula-reset">
              Reset to Defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Production Model Configuration ──
function ProductionModelConfig() {
  const [showSection, setShowSection] = useState(false);
  const [config, setConfig] = useState(null);
  const [textModel, setTextModel] = useState('');
  const [visionModel, setVisionModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState([]);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/sb-ctrl/production-model');
      setConfig(res.data);
      setTextModel(res.data.text_model || '');
      setVisionModel(res.data.vision_model || '');
    } catch { toast.error('Failed to load model config'); }
  };

  const saveConfig = async () => {
    setSaving(true);
    setWarnings([]);
    try {
      const res = await api.post('/sb-ctrl/production-model', {
        text_model: textModel.trim() || undefined,
        vision_model: visionModel.trim() || undefined,
      });
      if (res.data.warnings?.length) setWarnings(res.data.warnings);
      toast.success('Production model updated');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const resetToEnv = async () => {
    try {
      await api.delete('/sb-ctrl/production-model');
      toast.success('Reset to .env defaults');
      fetchConfig();
      setWarnings([]);
    } catch { toast.error('Failed to reset'); }
  };

  useEffect(() => {
    if (showSection && !config) fetchConfig();
    // Lazy fetch-on-expand: this effect must rerun ONLY when the section toggles;
    // the data guard is read fresh on each run and must not be a dependency (it
    // would refire the effect when the data arrives). Behavior predates F4.3a —
    // the disable exists so CI=true builds (warnings-as-errors) stay green.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  return (
    <div className="space-y-4" data-testid="production-model-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-production-model">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Hide' : 'Show'} Production Model Configuration
      </button>

      {showSection && config && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Brain className="w-4 h-4 text-[#FF6B00]" /> Production Model
            </h3>
            <p className="text-xs text-[#6a6a6a]">Change takes effect on next document processed. .env fallback: {config.env_text_model}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4">
              <label className="text-[10px] text-[#555] uppercase block mb-2">Text Generation Model</label>
              <div className="relative">
                <select value={textModel} onChange={e => setTextModel(e.target.value)}
                  className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-2 font-mono focus:border-[#FF6B00] outline-none appearance-none"
                  data-testid="production-text-model-select">
                  {config.available_models?.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <input type="text" value={textModel} onChange={e => setTextModel(e.target.value)}
                placeholder="Or type custom model name..."
                className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-2 font-mono mt-2 focus:border-[#FF6B00] outline-none"
                data-testid="production-text-model-input" />
              <div className="mt-2 flex items-center gap-2 text-[10px]">
                <span className={config.has_pricing ? 'text-emerald-400' : 'text-amber-400'}>
                  {config.has_pricing ? 'Pricing configured' : 'No pricing'}
                </span>
                <span className="text-[#333]">|</span>
                <span className={config.is_reasoning ? 'text-violet-400' : 'text-[#555]'}>
                  {config.is_reasoning ? 'Reasoning model' : 'Standard model'}
                </span>
                <span className="text-[#333]">|</span>
                <span className="text-[#555]">Max tokens: {config.token_limit?.toLocaleString()}</span>
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4">
              <label className="text-[10px] text-[#555] uppercase block mb-2">Vision Model</label>
              <input type="text" value={visionModel} onChange={e => setVisionModel(e.target.value)}
                placeholder="gpt-4o"
                className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-2 font-mono focus:border-[#FF6B00] outline-none"
                data-testid="production-vision-model-input" />
              <p className="text-[10px] text-[#333] mt-2">.env fallback: {config.env_vision_model}</p>
            </div>
          </div>

          {warnings.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 space-y-1">
              {warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {w}
                </p>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={saveConfig} disabled={saving}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs" data-testid="production-model-save">
              {saving ? 'Saving...' : 'Save Model'}
            </Button>
            <Button variant="ghost" onClick={resetToEnv}
              className="text-[#555] hover:text-white text-xs" data-testid="production-model-reset">
              Reset to .env Defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Topic Split Configuration ──
function TopicSplitConfig() {
  const [showSection, setShowSection] = useState(false);
  const [params, setParams] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/sb-ctrl/topic-split-config');
      setParams(res.data.params);
      setDefaults(res.data.defaults);
      setDraft(Object.fromEntries(Object.entries(res.data.params).map(([k, v]) => [k, String(v)])));
    } catch { toast.error('Failed to load topic split config'); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(draft)) {
        if (v.trim()) payload[k] = k.includes('model') ? v.trim() : parseInt(v);
      }
      const res = await api.post('/sb-ctrl/topic-split-config', { params: payload });
      setParams(res.data.params);
      setDraft(Object.fromEntries(Object.entries(res.data.params).map(([k, v]) => [k, String(v)])));
      toast.success('Topic split config saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (showSection && !params) fetchConfig();
    // Lazy fetch-on-expand: this effect must rerun ONLY when the section toggles;
    // the data guard is read fresh on each run and must not be a dependency (it
    // would refire the effect when the data arrives). Behavior predates F4.3a —
    // the disable exists so CI=true builds (warnings-as-errors) stay green.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const fields = [
    { key: 'topic_split_threshold', label: 'Split Threshold (chars)', desc: 'Documents above this size use topic splitting' },
    { key: 'topics_per_group', label: 'Topics per Group', desc: 'How many topics per parallel call' },
    { key: 'topic_detection_model', label: 'Detection Model', desc: 'Fast model for topic detection' },
    { key: 'topic_detection_timeout', label: 'Detection Timeout (s)', desc: 'Seconds before detection times out' },
  ];

  return (
    <div className="space-y-4" data-testid="topic-split-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-topic-split">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Hide' : 'Show'} Topic Split Configuration
      </button>

      {showSection && params && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white">Large Document Topic Splitting</h3>
            <p className="text-xs text-[#6a6a6a]">Documents exceeding the threshold are split by topic for parallel processing</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.key} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-3">
                <label className="text-[10px] text-[#555] uppercase block mb-1">{f.label}</label>
                <input
                  type={f.key.includes('model') ? 'text' : 'number'}
                  value={draft[f.key] || ''} onChange={e => setDraft(p => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none"
                  data-testid={`topic-split-${f.key}`} />
                <p className="text-[9px] text-[#333] mt-1">{f.desc}{defaults ? ` (default: ${defaults[f.key]})` : ''}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={saveConfig} disabled={saving}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs" data-testid="topic-split-save">
              {saving ? 'Saving...' : 'Save Config'}
            </Button>
            <Button variant="ghost" onClick={() => { if (defaults) setDraft(Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]))); }}
              className="text-[#555] hover:text-white text-xs" data-testid="topic-split-reset">
              Reset to Defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Length Bias Control (Targeted Equalization) ──
function BiasConfig() {
  const [showSection, setShowSection] = useState(false);
  const [params, setParams] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/sb-ctrl/bias-config');
      setParams(res.data.params);
      setDefaults(res.data.defaults);
      setDraft(Object.fromEntries(Object.entries(res.data.params).map(([k, v]) => [k, String(v)])));
    } catch { toast.error('Failed to load bias config'); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(draft)) {
        if (v.trim()) payload[k] = parseFloat(v);
      }
      const res = await api.post('/sb-ctrl/bias-config', { params: payload });
      setParams(res.data.params);
      setDraft(Object.fromEntries(Object.entries(res.data.params).map(([k, v]) => [k, String(v)])));
      toast.success('Bias config saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (showSection && !params) fetchConfig();
    // Lazy fetch-on-expand: this effect must rerun ONLY when the section toggles;
    // the data guard is read fresh on each run and must not be a dependency (it
    // would refire the effect when the data arrives). Behavior predates F4.3a —
    // the disable exists so CI=true builds (warnings-as-errors) stay green.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const fields = [
    { key: 'bias_threshold', label: 'Trigger Threshold', desc: 'Equalize if correct-is-longest exceeds this ratio (0.35 = 35%)' },
    { key: 'target_bias', label: 'Target Bias', desc: 'Equalize enough questions to reach this ratio (0.25 = 25%)' },
  ];

  return (
    <div className="space-y-4" data-testid="bias-config-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-bias-config">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Hide' : 'Show'} Length Bias Control
      </button>

      {showSection && params && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white">Targeted Equalization</h3>
            <p className="text-xs text-[#6a6a6a]">After Call C, if correct-is-longest bias exceeds threshold, a random subset is equalized via gpt-4.1-mini (blind) to reach target</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.key} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-3">
                <label className="text-[10px] text-[#555] uppercase block mb-1">{f.label}</label>
                <div className="flex items-center gap-2">
                  <input type="number" step="0.01" min="0" max="1" value={draft[f.key] || ''}
                    onChange={e => setDraft(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none"
                    data-testid={`bias-config-${f.key}`} />
                  {defaults && <span className="text-[9px] text-[#333] whitespace-nowrap">def: {(defaults[f.key] * 100).toFixed(0)}%</span>}
                </div>
                <p className="text-[9px] text-[#333] mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={saveConfig} disabled={saving}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs" data-testid="bias-config-save">
              {saving ? 'Saving...' : 'Save Config'}
            </Button>
            <Button variant="ghost" onClick={() => { if (defaults) setDraft(Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]))); }}
              className="text-[#555] hover:text-white text-xs" data-testid="bias-config-reset">
              Reset to Defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── #418 (elegir-b): «Opciones de respuesta: elegir, no editar» ──────────────────────────
   El interruptor de la perilla `ELEGIR_MODO` y su tablita de verificación.

   VA EN ESPAÑOL, como las causas del panel de errores: esto lo lee Carlos, no un usuario, y
   por eso no pasa por i18n ni mueve el bust de locales.

   Clases: TODAS copiadas de las tarjetas que ya están en esta página (BiasConfig y sus
   hermanas). No se estrena ni una, porque la hoja compilada de Tailwind tiene que salir
   byte-idéntica a la de `main`. Usable en el teléfono: una sola columna, botones de 44 px de
   alto y la tabla dentro de su propio `overflow-x-auto`. */
export function ElegirModoConfig() {
  const [showSection, setShowSection] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [correo, setCorreo] = useState('');

  const cargar = async (email) => {
    setCargando(true);
    try {
      const q = email && email.trim() ? `?email=${encodeURIComponent(email.trim())}` : '';
      const res = await api.get(`/sb-ctrl/elegir-modo${q}`);
      setDatos(res.data);
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setCargando(false); }
  };

  const guardar = async (modo) => {
    setGuardando(true);
    setConfirmando(false);
    try {
      const res = await api.post('/sb-ctrl/elegir-modo', { modo });
      setDatos(res.data);
      const n = res.data.recarga_segundos || 0;
      setAviso(n === 0
        ? 'Guardado. Aplica a los documentos que empiecen desde ahora.'
        : `Guardado. Las cajas lo leen en menos de ${Math.ceil(n / 60)} minutos.`);
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  useEffect(() => {
    if (showSection && !datos) cargar('');
    // Mismo patrón perezoso que BiasConfig: este efecto sólo puede volver a correr cuando la
    // sección se abre o se cierra; el guardia de datos se lee fresco en cada corrida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const encendido = datos && datos.modo === 'b';
  const guion = (v) => (v === null || v === undefined || v === '' ? '-' : v);
  const par = (p) => (p && typeof p.de === 'number' ? `${p.x} de ${p.de}` : '-');
  const resumen = (datos && datos.resumen) || {};
  const promedio = (g) => guion((resumen[g] || {}).centavos_por_pregunta);
  const docsDe = (g) => ((resumen[g] || {}).docs || 0);
  const columnas = ['Fecha', 'Archivo', 'Preguntas', 'Modo', 'Tiempo total (s)', 'Elegir (s)',
    'Costo total (¢ USD)', '¢ por pregunta', 'Elegir (¢ USD)',
    'Lugar logrado', 'Correcta intacta'];

  return (
    <div className="space-y-4" data-testid="elegir-modo-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-elegir-modo">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Opciones de respuesta
      </button>

      {showSection && datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="elegir-modo-card">
          <div>
            <h3 className="text-base font-semibold text-white">Opciones de respuesta: elegir, no editar</h3>
            <p className="text-xs text-[#6a6a6a]">Encendido, la respuesta correcta se escribe una sola vez y ya no se reescribe; el código sortea en qué lugar de largo queda. Apagado, todo funciona como hoy.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${encendido ? 'text-green-400' : 'text-[#888]'}`} data-testid="elegir-modo-estado">
              {encendido ? 'Estado: ENCENDIDO (variante B)' : 'Estado: APAGADO'}
            </span>
            {encendido ? (
              <Button onClick={() => guardar('off')} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="elegir-modo-apagar">
                {guardando ? 'Guardando...' : 'Apagar'}
              </Button>
            ) : (
              <Button onClick={() => setConfirmando(true)} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="elegir-modo-encender">
                {guardando ? 'Guardando...' : 'Encender'}
              </Button>
            )}
          </div>

          {confirmando && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="elegir-modo-confirmar">
              <p className="text-[13px] text-[#e5e5e5]">¿Encender para todos los documentos nuevos de alumnos? Los documentos que ya existen no cambian.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => guardar('b')} disabled={guardando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="elegir-modo-confirmar-si">
                  Sí, encender
                </Button>
                <Button variant="ghost" onClick={() => setConfirmando(false)}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="elegir-modo-confirmar-no">
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="elegir-modo-aviso">{aviso}</p>}

          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-[#e5e5e5]">Últimos 10 documentos de esta cuenta</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full">
                <label className="text-[10px] text-[#555] uppercase block mb-1">Correo de la cuenta</label>
                <input type="text" value={correo} onChange={e => setCorreo(e.target.value)}
                  className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none min-h-[44px]"
                  data-testid="elegir-modo-correo" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => cargar(correo)} disabled={cargando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="elegir-modo-ver">
                  Ver
                </Button>
                <Button variant="ghost" onClick={() => cargar(correo)} disabled={cargando}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="elegir-modo-actualizar">
                  Actualizar
                </Button>
              </div>
            </div>

            {datos.cuenta === null ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="elegir-modo-sin-cuenta">No existe una cuenta con ese correo.</p>
            ) : datos.documentos.length === 0 ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="elegir-modo-sin-documentos">Esta cuenta no tiene documentos.</p>
            ) : (
              <>
              <div className="overflow-x-auto" data-testid="elegir-modo-tabla">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-[#1a1a1a]">
                      {columnas.map(c => (
                        <th key={c} className="text-left py-2 px-2 text-[#555] uppercase whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {datos.documentos.map(d => (
                      <tr key={d.id} className="border-b border-[#1a1a1a]" data-testid={`elegir-modo-fila-${d.id}`}>
                        <td className="py-2 px-2 text-[#6a6a6a] whitespace-nowrap">{guion(d.fecha ? formatDate(d.fecha) : null)}</td>
                        <td className="py-2 px-2 text-[#aaa]">{guion(d.archivo)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.preguntas)}</td>
                        <td className={`py-2 px-2 font-mono ${d.modo === 'b' ? 'text-green-400' : 'text-[#555]'}`}>{d.modo}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.tiempo_total_s)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.elegir_s)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.costo_total_centavos)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.centavos_por_pregunta)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.elegir_centavos)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono whitespace-nowrap">{par(d.lugar_logrado)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono whitespace-nowrap">{par(d.correcta_intacta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-[#6a6a6a] mt-2" data-testid="elegir-modo-promedio">
                {`Promedio de estos documentos, apagados: ${promedio('off')} ¢ por pregunta (${docsDe('off')} docs), encendidos: ${promedio('b')} ¢ por pregunta (${docsDe('b')} docs)`}
              </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ── #425 (callc-cache): «Caché de opciones» ──────────────────────────────────────────────
   El interruptor de la perilla `CALLC_CACHE` y su tablita para el smoke por parejas.

   VA EN ESPAÑOL, por la misma razón que su hermana de arriba: esto lo lee Carlos, no un
   usuario, y por eso no pasa por i18n ni mueve el bust de locales.

   Clases y textos de servicio: TODOS copiados de `ElegirModoConfig`, que a su vez los copió
   de BiasConfig. No se estrena ni una clase, porque la hoja compilada de Tailwind tiene que
   salir byte-idéntica a la de `main`. */
export function CallcCacheConfig() {
  const [showSection, setShowSection] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [correo, setCorreo] = useState('');

  const cargar = async (email) => {
    setCargando(true);
    try {
      const q = email && email.trim() ? `?email=${encodeURIComponent(email.trim())}` : '';
      const res = await api.get(`/sb-ctrl/callc-cache${q}`);
      setDatos(res.data);
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setCargando(false); }
  };

  const guardar = async (modo) => {
    setGuardando(true);
    setConfirmando(false);
    try {
      const res = await api.post('/sb-ctrl/callc-cache', { modo });
      setDatos(res.data);
      const n = res.data.recarga_segundos || 0;
      setAviso(n === 0
        ? 'Guardado. Aplica a los documentos que empiecen desde ahora.'
        : `Guardado. Las cajas lo leen en menos de ${Math.ceil(n / 60)} minutos.`);
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  useEffect(() => {
    if (showSection && !datos) cargar('');
    // Mismo patrón perezoso que ElegirModoConfig y BiasConfig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const encendido = datos && datos.modo === 'on';
  const guion = (v) => (v === null || v === undefined || v === '' ? '-' : v);
  const par = (p) => (p && typeof p.de === 'number' ? `${p.x} de ${p.de}` : '-');
  const resumen = (datos && datos.resumen) || {};
  const promedio = (g) => guion((resumen[g] || {}).centavos_por_pregunta);
  const cacheado = (g) => guion((resumen[g] || {}).cacheado_de_100);
  const docsDe = (g) => ((resumen[g] || {}).docs || 0);
  const columnas = ['Fecha', 'Archivo', 'Preguntas', 'Caché', 'Tiempo total (s)',
    'Costo total (¢ USD)', '¢ por pregunta', 'Cacheado de cada 100', 'Correcta intacta'];

  return (
    <div className="space-y-4" data-testid="callc-cache-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-callc-cache">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Caché de opciones
      </button>

      {showSection && datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="callc-cache-card">
          <div>
            <h3 className="text-base font-semibold text-white">Caché de opciones</h3>
            <p className="text-xs text-[#6a6a6a]">Abarata el paso de las opciones sin cambiar ni una palabra de lo que lee el modelo. Aplica a los documentos de alumno que empiecen a procesarse después del clic.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${encendido ? 'text-green-400' : 'text-[#888]'}`} data-testid="callc-cache-estado">
              {encendido ? 'Estado: ENCENDIDO' : 'Estado: APAGADO'}
            </span>
            {encendido ? (
              <Button onClick={() => guardar('off')} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="callc-cache-apagar">
                {guardando ? 'Guardando...' : 'Apagar'}
              </Button>
            ) : (
              <Button onClick={() => setConfirmando(true)} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="callc-cache-encender">
                {guardando ? 'Guardando...' : 'Encender'}
              </Button>
            )}
          </div>

          {confirmando && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="callc-cache-confirmar">
              <p className="text-[13px] text-[#e5e5e5]">¿Encender para todos los documentos nuevos de alumnos? Los documentos que ya existen no cambian.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => guardar('on')} disabled={guardando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="callc-cache-confirmar-si">
                  Sí, encender
                </Button>
                <Button variant="ghost" onClick={() => setConfirmando(false)}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="callc-cache-confirmar-no">
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="callc-cache-aviso">{aviso}</p>}

          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-[#e5e5e5]">Últimos 10 documentos de esta cuenta</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full">
                <label className="text-[10px] text-[#555] uppercase block mb-1">Correo de la cuenta</label>
                <input type="text" value={correo} onChange={e => setCorreo(e.target.value)}
                  className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none min-h-[44px]"
                  data-testid="callc-cache-correo" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => cargar(correo)} disabled={cargando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="callc-cache-ver">
                  Ver
                </Button>
                <Button variant="ghost" onClick={() => cargar(correo)} disabled={cargando}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="callc-cache-actualizar">
                  Actualizar
                </Button>
              </div>
            </div>

            {datos.cuenta === null ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="callc-cache-sin-cuenta">No existe una cuenta con ese correo.</p>
            ) : datos.documentos.length === 0 ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="callc-cache-sin-documentos">Esta cuenta no tiene documentos.</p>
            ) : (
              <>
              <div className="overflow-x-auto" data-testid="callc-cache-tabla">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-[#1a1a1a]">
                      {columnas.map(c => (
                        <th key={c} className="text-left py-2 px-2 text-[#555] uppercase whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {datos.documentos.map(d => (
                      <tr key={d.id} className="border-b border-[#1a1a1a]" data-testid={`callc-cache-fila-${d.id}`}>
                        <td className="py-2 px-2 text-[#6a6a6a] whitespace-nowrap">{guion(d.fecha ? formatDate(d.fecha) : null)}</td>
                        <td className="py-2 px-2 text-[#aaa]">{guion(d.archivo)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.preguntas)}</td>
                        <td className={`py-2 px-2 font-mono ${d.cache === 'encendida' ? 'text-green-400' : 'text-[#555]'}`}>{d.cache}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.tiempo_total_s)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.costo_total_centavos)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.centavos_por_pregunta)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.cacheado_de_100)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono whitespace-nowrap">{par(d.correcta_intacta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-[#6a6a6a] mt-2" data-testid="callc-cache-promedio">
                {`Promedio de estos documentos, apagados: ${promedio('apagada')} ¢ por pregunta, ${cacheado('apagada')} de cada 100 cacheados (${docsDe('apagada')} docs), encendidos: ${promedio('encendida')} ¢ por pregunta, ${cacheado('encendida')} de cada 100 cacheados (${docsDe('encendida')} docs)`}
              </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* estilos-1: la perilla ESTILOS_EXAMEN ("Estilos de examen") y la seccion "Refrescar demo del alumno".
   Solo espanol (lo lee Carlos): no pasa por i18n ni mueve el bust. Las clases son TODAS copiadas de la
   tarjeta de "Respuesta natural" de abajo, para que la hoja de Tailwind no gane ni una. */
export function EstilosExamenConfig() {
  const [showSection, setShowSection] = useState(false);
  const [modo, setModo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    if (!showSection || modo !== null) return;
    api.get('/sb-ctrl/estilos-examen').then((res) => setModo(res.data.modo)).catch(() => setAviso('No se pudo leer la perilla.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const guardar = async (nuevo) => {
    setGuardando(true);
    try {
      const res = await api.post('/sb-ctrl/estilos-examen', { modo: nuevo });
      setModo(res.data.modo);
      setAviso('Guardado. Aplica a las subidas que empiecen desde ahora.');
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  const encendido = modo === 'on';
  return (
    <div className="space-y-4" data-testid="estilos-examen-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-estilos-examen">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Estilos de examen
      </button>
      {showSection && modo !== null && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="estilos-examen-card">
          <div>
            <h3 className="text-base font-semibold text-white">Estilos de examen</h3>
            <p className="text-xs text-[#6a6a6a]">Muestra a los alumnos la pastilla "Estilo:", la hoja para elegir examen, la sección de Ajustes y el nombre del examen en la tarjeta. Apagada, nadie ve nada y la subida ignora el estilo. Se enciende después del merge de estilos-2.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${encendido ? 'text-green-400' : 'text-[#888]'}`} data-testid="estilos-examen-estado">
              {encendido ? 'Estado: ENCENDIDO' : 'Estado: APAGADO'}
            </span>
            <Button onClick={() => guardar(encendido ? 'off' : 'on')} disabled={guardando}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="estilos-examen-cambiar">
              {guardando ? 'Guardando...' : (encendido ? 'Apagar' : 'Encender')}
            </Button>
          </div>
          {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="estilos-examen-aviso">{aviso}</p>}
        </div>
      )}
    </div>
  );
}

export function RefrescarDemoAlumno() {
  const [showSection, setShowSection] = useState(false);
  const [ultima, setUltima] = useState(undefined);
  const [corriendo, setCorriendo] = useState(false);
  const [motivos, setMotivos] = useState([]);
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    if (!showSection || ultima !== undefined) return;
    api.get('/sb-ctrl/demo-alumno').then((res) => setUltima(res.data.ultima || null)).catch(() => setUltima(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const refrescar = async () => {
    setCorriendo(true);
    setMotivos([]);
    setAviso('');
    try {
      const res = await api.post('/sb-ctrl/demo-alumno/refrescar');
      setUltima(res.data.ultima || null);
      if (res.data.escrito) {
        setAviso('Listo: las fechas del demo se movieron y el Readiness se conservó.');
      } else {
        setMotivos(res.data.motivos || []);
        setAviso('No se escribió nada: el Readiness no cuadra.');
      }
    } catch {
      setAviso('No se pudo refrescar. Intenta de nuevo.');
    } finally { setCorriendo(false); }
  };

  return (
    <div className="space-y-4" data-testid="demo-alumno-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-demo-alumno">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Refrescar demo del alumno
      </button>
      {showSection && ultima !== undefined && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="demo-alumno-card">
          <div>
            <h3 className="text-base font-semibold text-white">Refrescar demo del alumno</h3>
            <p className="text-xs text-[#6a6a6a]" data-testid="demo-alumno-ultima">{ultima ? `Última vez: ${ultima}` : 'Última vez: nunca'}</p>
          </div>
          <Button onClick={refrescar} disabled={corriendo}
            className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="demo-alumno-refrescar">
            {corriendo ? 'Refrescando...' : 'Refrescar fechas del demo'}
          </Button>
          {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="demo-alumno-aviso">{aviso}</p>}
          {motivos.length > 0 && (
            <div className="space-y-3" data-testid="demo-alumno-motivos">
              {motivos.map((m) => <p key={m} className="text-xs text-[#6a6a6a]">{m}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── #426 (respuesta-natural): «Respuesta natural» ────────────────────────────────────────
   El interruptor de la perilla `RESPUESTA_NATURAL` y su tablita para el smoke por parejas.

   VA EN ESPAÑOL, por la misma razón que sus dos hermanas de arriba: esto lo lee Carlos, no un
   usuario, y por eso no pasa por i18n ni mueve el bust de locales.

   Clases y textos de servicio: TODOS copiados de `CallcCacheConfig`, que a su vez los copió de
   `ElegirModoConfig` y ésa de BiasConfig. No se estrena ni una clase, porque la hoja compilada
   de Tailwind tiene que salir byte-idéntica a la de `main`. El rojo de «Lugar de la correcta»
   es el mismo que esta página ya usa en otras veintidos celdas: no se estrena nada.
   (Regla 5.6 de la casa: en un comentario bajo `src/` no se escribe el nombre de
   ninguna clase de Tailwind -- escribirla ahi la publicaria en la hoja compilada.)

   UNA diferencia deliberada con las otras dos tarjetas, y sólo en ésta: tras «Encender» o
   «Apagar» la tabla se queda en el correo que se estaba mirando (el POST lleva el correo). Las
   otras dos NO se tocan. */
export function RespuestaNaturalConfig() {
  const [showSection, setShowSection] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [correo, setCorreo] = useState('');

  const cargar = async (email) => {
    setCargando(true);
    try {
      const q = email && email.trim() ? `?email=${encodeURIComponent(email.trim())}` : '';
      const res = await api.get(`/sb-ctrl/respuesta-natural${q}`);
      setDatos(res.data);
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setCargando(false); }
  };

  const guardar = async (modo) => {
    setGuardando(true);
    setConfirmando(false);
    try {
      // el correo viaja en el POST: así la tabla se queda donde estaba (única diferencia
      // con las otras dos tarjetas).
      const res = await api.post('/sb-ctrl/respuesta-natural', { modo, email: correo });
      setDatos(res.data);
      const n = res.data.recarga_segundos || 0;
      setAviso(n === 0
        ? 'Guardado. Aplica a los documentos que empiecen desde ahora.'
        : `Guardado. Las cajas lo leen en menos de ${Math.ceil(n / 60)} minutos.`);
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  useEffect(() => {
    if (showSection && !datos) cargar('');
    // Mismo patrón perezoso que CallcCacheConfig, ElegirModoConfig y BiasConfig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const encendido = datos && datos.modo === 'on';
  const elegirEnB = datos && datos.elegir_modo === 'b';
  const guion = (v) => (v === null || v === undefined || v === '' ? '-' : v);
  const par = (p) => (p && typeof p.de === 'number' ? `${p.x} de ${p.de}` : '-');
  const resumen = (datos && datos.resumen) || {};
  const promedio = (g) => guion((resumen[g] || {}).centavos_por_pregunta);
  const porque = (g) => guion((resumen[g] || {}).porque_de_100);
  const docsDe = (g) => ((resumen[g] || {}).docs || 0);
  const columnas = ['Fecha', 'Archivo', 'Preguntas', 'Natural', 'Escogidas por código',
    'Tiempo total (s)', 'Costo total (¢ USD)', '¢ por pregunta', 'Con porque de cada 100',
    'Lugar de la correcta', 'Correcta intacta', 'Avisos'];

  return (
    <div className="space-y-4" data-testid="respuesta-natural-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-respuesta-natural">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Respuesta natural
      </button>

      {showSection && datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="respuesta-natural-card">
          <div>
            <h3 className="text-base font-semibold text-white">Respuesta natural</h3>
            <p className="text-xs text-[#6a6a6a]">Quita el "porque" de las opciones cuando la pregunta no lo pide y deja que el código escoja las opciones en las preguntas de dato. Sólo actúa con "Opciones de respuesta" en modo b. Aplica a los documentos de alumno que empiecen a procesarse después del clic.</p>
            {!elegirEnB && (
              <p className="text-xs text-[#6a6a6a]" data-testid="respuesta-natural-sin-elegir">"Opciones de respuesta" no está en modo b: aunque enciendas aquí, no actúa.</p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${encendido ? 'text-green-400' : 'text-[#888]'}`} data-testid="respuesta-natural-estado">
              {encendido ? 'Estado: ENCENDIDO' : 'Estado: APAGADO'}
            </span>
            {encendido ? (
              <Button onClick={() => guardar('off')} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="respuesta-natural-apagar">
                {guardando ? 'Guardando...' : 'Apagar'}
              </Button>
            ) : (
              <Button onClick={() => setConfirmando(true)} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="respuesta-natural-encender">
                {guardando ? 'Guardando...' : 'Encender'}
              </Button>
            )}
          </div>

          {confirmando && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="respuesta-natural-confirmar">
              <p className="text-[13px] text-[#e5e5e5]">¿Encender para todos los documentos nuevos de alumnos? Los documentos que ya existen no cambian.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => guardar('on')} disabled={guardando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="respuesta-natural-confirmar-si">
                  Sí, encender
                </Button>
                <Button variant="ghost" onClick={() => setConfirmando(false)}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="respuesta-natural-confirmar-no">
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="respuesta-natural-aviso">{aviso}</p>}

          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-[#e5e5e5]">Últimos 10 documentos de esta cuenta</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="w-full">
                <label className="text-[10px] text-[#555] uppercase block mb-1">Correo de la cuenta</label>
                <input type="text" value={correo} onChange={e => setCorreo(e.target.value)}
                  className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none min-h-[44px]"
                  data-testid="respuesta-natural-correo" />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => cargar(correo)} disabled={cargando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="respuesta-natural-ver">
                  Ver
                </Button>
                <Button variant="ghost" onClick={() => cargar(correo)} disabled={cargando}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="respuesta-natural-actualizar">
                  Actualizar
                </Button>
              </div>
            </div>

            {datos.cuenta === null ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="respuesta-natural-sin-cuenta">No existe una cuenta con ese correo.</p>
            ) : datos.documentos.length === 0 ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="respuesta-natural-sin-documentos">Esta cuenta no tiene documentos.</p>
            ) : (
              <>
              <div className="overflow-x-auto" data-testid="respuesta-natural-tabla">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-[#1a1a1a]">
                      {columnas.map(c => (
                        <th key={c} className="text-left py-2 px-2 text-[#555] uppercase whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {datos.documentos.map(d => (
                      <tr key={d.id} className="border-b border-[#1a1a1a]" data-testid={`respuesta-natural-fila-${d.id}`}>
                        <td className="py-2 px-2 text-[#6a6a6a] whitespace-nowrap">{guion(d.fecha ? formatDate(d.fecha) : null)}</td>
                        <td className="py-2 px-2 text-[#aaa]">{guion(d.archivo)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.preguntas)}</td>
                        <td className={`py-2 px-2 font-mono ${d.natural === 'encendida' ? 'text-green-400' : 'text-[#555]'}`}>{d.natural}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono whitespace-nowrap">{par(d.escogidas)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.tiempo_total_s)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.costo_total_centavos)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.centavos_por_pregunta)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(d.porque_de_100)}</td>
                        <td className="py-2 px-2 font-mono whitespace-nowrap" data-testid={`respuesta-natural-lugar-${d.id}`}>
                          {d.lugar_correcta
                            ? d.lugar_correcta.map((v, i) => (
                                <span key={i} className={v > 35 ? 'text-red-400' : 'text-[#aaa]'}>
                                  {i ? `, ${v}` : `${v}`}
                                </span>
                              ))
                            : <span className="text-[#aaa]">-</span>}
                        </td>
                        <td className="py-2 px-2 text-[#aaa] font-mono whitespace-nowrap">{par(d.correcta_intacta)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono whitespace-nowrap">{guion(d.avisos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-[#6a6a6a] mt-2" data-testid="respuesta-natural-promedio">
                {`Promedio de estos documentos, apagados: ${promedio('apagada')} ¢ por pregunta, ${porque('apagada')} de cada 100 opciones con porque (${docsDe('apagada')} docs), encendidos: ${promedio('encendida')} ¢ por pregunta, ${porque('encendida')} de cada 100 opciones con porque (${docsDe('encendida')} docs)`}
              </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* -- blog-es: la seccion "Español" de la tarjeta del blog -------------------------------
   La traduccion automatica al español: la perilla BLOG_TRADUCCION, el motor de cada dia, el
   tope del mes, lo gastado y la lista de los ultimos 30 articulos en ingles con el estado de
   su gemelo y "Traducir de nuevo". El API es GET/POST /api/sb-ctrl/blog/es, que contesta
   CODIGOS; los textos viven aqui, solo en español, como el resto de esta pagina.
   Reusa las clases que ya trae la tarjeta del blog (regla 5.6: este comentario no nombra
   ninguna). */
export const BLOG_ES_MOTORES = {
  'gpt-6.1-sol': 'OpenAI gpt-6.1-sol (ya en el servidor)',
  'claude-opus-5-5': 'Claude Opus 5.5 (tu API de Claude)',
  'claude-fable-5-1': 'Claude Fable 5.1 (tu API de Claude)',
};
export const BLOG_ES_MOTIVOS = {
  no_paso: 'no pasó la revisión después de tres intentos',
  salida_cortada: 'la respuesta del modelo llegó cortada',
  formato: 'la respuesta no vino en el formato esperado',
  rechazo: 'el modelo se negó a traducirlo',
  sin_saldo: 'se acabó el saldo de la API',
  error_red: 'no se pudo conectar con el modelo',
  tope_mes: 'se llegó al tope de gasto del mes',
  error_inesperado: 'falló algo inesperado; avísale a Claude',
  tope_articulo: 'llegó al tope de gasto por artículo',
  interrumpida: 'se interrumpió dos veces',
  repetida: 'se intentó demasiadas veces hoy',
};
export const BLOG_ES_ESTADOS = {
  publicada: 'Publicada',
  retenida: 'Retenida',
  desactualizada: 'Desactualizada',
  pendiente: 'Pendiente',
  en_cola: 'En cola',
  retirada: 'Retirada (el inglés no está publicado)',
  a_mano: 'Despublicada a mano',
  traduciendo: 'Traduciéndose ahora',
};

/* -- blog-es-2: la caja de correcciones de la revision diaria ---------------------------
   Carlos pega el bloque que manda la revision diaria; "Revisar" lo manda sin aplicar y pinta el
   estado de cada correccion y, por articulo, si la revision automatica sigue limpia; "Aplicar"
   solo se enciende despues de revisar el MISMO texto con algo aplicable; "Deshacer" devuelve los
   valores viejos de una. API: POST /api/sb-ctrl/blog/es/correcciones y .../deshacer. Solo en
   espanol, como el resto de esta pagina; reusa las clases de la tarjeta del blog (regla 5.6: este
   comentario no nombra ninguna). */
export const BLOG_ES_CORRECCION_ESTADOS = {
  se_aplica: 'Se aplica',
  no_encontrado: 'No se encontró el texto',
  cuenta_distinta: 'El texto aparece {n} veces y se esperaban {m}',
  campo_invalido: 'Campo inválido',
  articulo_no_existe: 'No existe ese artículo',
  simbolo_prohibido: 'Trae un símbolo prohibido',
};
export const BLOG_ES_CORRECCION_RECHAZOS = {
  se_pisan: 'Dos correcciones se pisan: no se aplica ninguna de este artículo',
  html_no_saneado: 'El HTML corregido no queda igual al limpiarlo: no se aplica',
  sin_original: 'No se encontró el artículo en inglés: no se aplica',
};

export function textoDeCorreccion(c) {
  const base = BLOG_ES_CORRECCION_ESTADOS[c.estado] || c.estado;
  if (c.estado === 'cuenta_distinta') return base.replace('{n}', c.halladas).replace('{m}', c.veces);
  return base;
}

export function textoDeArticulo(a) {
  if (!a.rechazo) return 'La revisión automática sigue limpia';
  if (a.rechazo === 'falla_nueva') {
    const f = (a.fallas_nuevas || [])[0] || {};
    return `La revisión automática encontraría una falla nueva (punto ${f.punto}: ${f.detalle}${f.texto ? `, "${f.texto}"` : ''}): no se aplica`;
  }
  return BLOG_ES_CORRECCION_RECHAZOS[a.rechazo] || a.rechazo;
}

function CorreccionesDelBlog({ recientesIniciales }) {
  const [texto, setTexto] = useState('');
  const [revisado, setRevisado] = useState('');
  const [revision, setRevision] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  const [recientes, setRecientes] = useState(recientesIniciales || []);

  const pedir = async (aplicar) => {
    setOcupado(true);
    setAviso('');
    try {
      const res = await api.post('/sb-ctrl/blog/es/correcciones', { texto, aplicar });
      const d = res.data || {};
      setRecientes(d.correcciones_recientes || []);
      if (aplicar) {
        const hecho = d.aplicado || {};
        setAviso(`Listo: ${hecho.correcciones || 0} correcciones en ${hecho.escritos || 0} artículos. Se ve en la página en unos 10 minutos.`);
        setRevision(d.revision || null);
        setRevisado('');
      } else {
        setRevision(d.revision || null);
        setRevisado(texto);
      }
    } catch (err) {
      const detalle = err && err.response && err.response.data && err.response.data.detail;
      setAviso(detalle ? `No se pudo leer el texto: ${detalle}` : 'No se pudo revisar. Intenta de nuevo.');
      setRevision(null);
      setRevisado('');
    } finally { setOcupado(false); }
  };

  const deshacer = async (id) => {
    setOcupado(true);
    setAviso('');
    try {
      const res = await api.post('/sb-ctrl/blog/es/correcciones/deshacer', { id });
      const d = res.data || {};
      setRecientes(d.correcciones_recientes || []);
      setAviso(d.resultado && d.resultado.ok
        ? 'Listo: se deshizo. Se ve en la página en unos 10 minutos.'
        : 'No se pudo deshacer: el artículo cambió después de esa corrección.');
    } catch {
      setAviso('No se pudo deshacer. Intenta de nuevo.');
    } finally { setOcupado(false); }
  };

  const puedeAplicar = !!revision && revisado === texto && texto.trim() !== '' && revision.aplicables > 0;

  return (
    <div className="space-y-2" data-testid="blog-es-correcciones">
      <h4 className="text-xs font-semibold text-[#e5e5e5]">Correcciones de la revisión diaria</h4>
      <label htmlFor="blog-es-correcciones-texto" className="text-[11px] text-[#e5e5e5]">Pega aquí el bloque de correcciones que manda la revisión diaria.</label>
      <textarea id="blog-es-correcciones-texto" rows={8} value={texto} disabled={ocupado}
        onChange={(e) => { setTexto(e.target.value); setRevisado(''); }}
        className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl text-xs text-[#e5e5e5] w-full px-4" data-testid="blog-es-correcciones-texto" />
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Button onClick={() => pedir(false)} disabled={ocupado || texto.trim() === ''}
          className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-es-correcciones-revisar">
          Revisar
        </Button>
        <Button onClick={() => pedir(true)} disabled={ocupado || !puedeAplicar}
          className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-es-correcciones-aplicar">
          Aplicar
        </Button>
      </div>
      {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="blog-es-correcciones-aviso">{aviso}</p>}
      {revision && (
        <div className="space-y-2" data-testid="blog-es-correcciones-resultado">
          <p className="text-[11px] text-[#aaa]" data-testid="blog-es-correcciones-cuenta">{`Se aplican ${revision.aplicables} de ${revision.total} correcciones en ${revision.articulos_aplicables} artículos.`}</p>
          {(revision.articulos || []).map(a => (
            <div key={a.slug} className="border-b border-[#1a1a1a] py-2 space-y-2" data-testid={`blog-es-correcciones-articulo-${a.slug}`}>
              <p className="text-[11px] text-[#aaa]">{a.slug}</p>
              <p className={`text-[11px] ${a.rechazo ? 'text-red-400' : 'text-green-400'}`} data-testid={`blog-es-correcciones-vara-${a.slug}`}>{textoDeArticulo(a)}</p>
            </div>
          ))}
          <ul className="space-y-2">
            {(revision.correcciones || []).map(c => (
              <li key={c.n} className="text-[10px] text-[#6a6a6a]" data-testid={`blog-es-correccion-${c.n}`}>
                {`${c.n}. ${c.slug} (${c.campo}): ${textoDeCorreccion(c)}${c.por_que ? ` (${c.por_que})` : ''}`}
              </li>
            ))}
          </ul>
        </div>
      )}
      <h4 className="text-xs font-semibold text-[#e5e5e5]">Últimas correcciones</h4>
      <div className="space-y-2" data-testid="blog-es-correcciones-recientes">
        {recientes.length === 0 && <p className="text-[11px] text-[#6a6a6a]">Todavía no hay correcciones.</p>}
        {recientes.map(r => (
          <div key={r.id} className="flex flex-col sm:flex-row items-center gap-2" data-testid={`blog-es-reciente-${r.id}`}>
            <span className="text-[11px] text-[#aaa]">{`${(r.cuando || '').slice(0, 16).replace('T', ' ')} ${r.slug} (${r.cuantas} correcciones)`}</span>
            {r.deshecha ? (
              <span className="text-[11px] text-[#6a6a6a]">Deshecha</span>
            ) : r.resultado !== 'aplicada' ? (
              <span className="text-[11px] text-[#6a6a6a]">No se aplicó</span>
            ) : (
              <button type="button" onClick={() => deshacer(r.id)} disabled={ocupado}
                className="text-[#555] hover:text-white text-xs px-2" data-testid={`blog-es-deshacer-${r.id}`}>Deshacer</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BlogEspanol() {
  const [datos, setDatos] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [tope, setTope] = useState('');
  const [abierto, setAbierto] = useState('');
  const [aviso, setAviso] = useState('');

  const tomar = (d) => { setDatos(d); setTope(String(d.tope_usd)); };

  const cargar = async () => {
    try {
      const res = await api.get('/sb-ctrl/blog/es');
      tomar(res.data);
    } catch {
      setAviso('No se pudo cargar. Intenta de nuevo.');
    }
  };

  const mandar = async (cuerpo) => {
    setGuardando(true);
    try {
      const res = await api.post('/sb-ctrl/blog/es', cuerpo);
      tomar(res.data);
      setAviso('Listo.');
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  useEffect(() => {
    cargar();
    // Se carga una vez al abrir la tarjeta del blog, como sus hermanas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!datos) {
    return (
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="blog-es">
        <h4 className="text-xs font-semibold text-[#e5e5e5]">Español</h4>
        <p className="text-[11px] text-[#6a6a6a]" data-testid="blog-es-cargando">{aviso || 'Cargando...'}</p>
      </div>
    );
  }

  const encendida = datos.modo === 'on';
  const dinero = (n) => Number(n || 0).toFixed(2);

  return (
    <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="blog-es">
      <h4 className="text-xs font-semibold text-[#e5e5e5]">Español</h4>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <span className="text-[13px] text-[#e5e5e5]">Traducción automática al español</span>
        <button type="button" role="switch" aria-checked={encendida} disabled={guardando}
          onClick={() => mandar({ modo: encendida ? 'off' : 'on' })}
          className={`text-[13px] font-mono ${encendida ? 'text-green-400' : 'text-[#888]'}`} data-testid="blog-es-interruptor">
          {encendida ? 'Encendida' : 'Apagada'}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <label htmlFor="blog-es-motor" className="text-[11px] text-[#e5e5e5]">Motor de cada día:</label>
        <select id="blog-es-motor" value={datos.motor} disabled={guardando}
          onChange={(e) => mandar({ motor: e.target.value })}
          className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl text-xs text-[#e5e5e5] w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-es-motor">
          {(datos.motores || []).map(m => (
            <option key={m.valor} value={m.valor} disabled={!m.disponible}>
              {BLOG_ES_MOTORES[m.valor] + (m.disponible ? '' : ' (falta la llave en Render)')}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <label htmlFor="blog-es-tope" className="text-[11px] text-[#e5e5e5]">Tope de gasto al mes (USD):</label>
        <input id="blog-es-tope" type="number" min="0" step="1" value={tope}
          onChange={(e) => setTope(e.target.value)}
          className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl text-xs text-[#e5e5e5] w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-es-tope" />
        <Button onClick={() => mandar({ tope_usd: tope })} disabled={guardando}
          className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-es-tope-guardar">
          Guardar
        </Button>
      </div>

      <p className="text-[11px] text-[#aaa]" data-testid="blog-es-gastado">{`Gastado este mes: ${dinero(datos.gastado_mes)} USD de ${dinero(datos.tope_usd)}`}</p>
      <p className="text-[11px] text-[#aaa]" data-testid="blog-es-publicadas">{`Publicadas: ${datos.publicadas} de ${datos.total_en}`}</p>
      {datos.aviso_sin_llave && (
        <p className="text-[11px] text-red-400" data-testid="blog-es-aviso-llave">El motor escogido no tiene llave en el servidor: no se está traduciendo.</p>
      )}
      {datos.aviso_tope && (
        <p className="text-[11px] text-red-400" data-testid="blog-es-aviso-tope">Se llegó al tope del mes: se reanuda el día 1.</p>
      )}
      {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="blog-es-aviso">{aviso}</p>}

      <div className="space-y-2" data-testid="blog-es-lista">
        {(datos.articulos || []).map(a => (
          <div key={a.en_id} className="border-b border-[#1a1a1a] py-2 space-y-2" data-testid={`blog-es-fila-${a.en_id}`}>
            <p className="text-[11px] text-[#aaa]">{a.title}</p>
            <div className="flex flex-col sm:flex-row items-center gap-2">
              {a.estado === 'publicada' ? (
                <a href={a.url_es} target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-green-400" data-testid={`blog-es-estado-${a.en_id}`}>{BLOG_ES_ESTADOS.publicada}</a>
              ) : a.estado === 'retenida' ? (
                <button type="button" onClick={() => setAbierto(abierto === a.en_id ? '' : a.en_id)}
                  className="text-[11px] text-red-400" data-testid={`blog-es-estado-${a.en_id}`}>
                  {`${BLOG_ES_ESTADOS.retenida}: ${BLOG_ES_MOTIVOS[a.motivo] || a.motivo}`}
                </button>
              ) : (
                <span className="text-[11px] text-[#6a6a6a]" data-testid={`blog-es-estado-${a.en_id}`}>{BLOG_ES_ESTADOS[a.estado] || a.estado}</span>
              )}
              <button type="button" onClick={() => mandar({ traducir: a.en_id })} disabled={guardando}
                className="text-[#555] hover:text-white text-xs px-2" data-testid={`blog-es-traducir-${a.en_id}`}>Traducir de nuevo</button>
            </div>
            {abierto === a.en_id && (
              <ul className="space-y-2" data-testid={`blog-es-fallas-${a.en_id}`}>
                {(a.fallas || []).map((f, i) => (
                  <li key={i} className="text-[10px] text-[#6a6a6a]">{f.de === 'error' ? `Error: ${f.detalle}` : `${f.de === 'vara' ? 'Vara' : 'Revisor'} ${f.punto}: ${f.detalle}${f.texto ? ` (${f.texto})` : ''}`}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <CorreccionesDelBlog recientesIniciales={datos.correcciones_recientes} />
    </div>
  );
}

/* -- #438 (blog-1): la tarjeta del blog ------------------------------------------------
   El freno maestro `BLOG_ENABLED`, los enchufes de Distribb y los ultimos 20 articulos.

   VA EN ESPANOL, por la misma razon que sus cuatro hermanas de arriba: esto lo lee Carlos,
   no un usuario, y por eso no pasa por i18n ni mueve el bust de locales.

   Clases y textos de servicio: TODOS copiados de `RespuestaNaturalConfig`, que a su vez los
   copio de `CallcCacheConfig`. El verde y el rojo de los estados son los mismos que esta
   pagina ya usa en otras celdas. (Regla 5.6 de la casa: en un comentario bajo `src/` no se
   escribe el nombre de ninguna clase de Tailwind -- escribirla ahi la publicaria en la hoja
   compilada, y por eso este bloque no nombra ninguna.)

   EL TOKEN SE VE UNA SOLA VEZ, y la tarjeta lo dice con todas sus letras: lo que el servidor
   guarda es su huella, asi que no hay forma de volver a ensenarlo. Regenerar invalida el
   anterior en el acto, y por eso ese boton pasa por la confirmacion en linea del estilo de
   la casa. */
export function BlogConfig() {
  const [showSection, setShowSection] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [confirmaToken, setConfirmaToken] = useState('');
  const [aviso, setAviso] = useState('');

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await api.get('/sb-ctrl/blog');
      setDatos(res.data);
    } catch {
      setAviso('No se pudo cargar. Intenta de nuevo.');
    } finally { setCargando(false); }
  };

  const guardar = async (modo) => {
    setGuardando(true);
    setConfirmando(false);
    try {
      const res = await api.post('/sb-ctrl/blog', { modo });
      setDatos(res.data);
      setAviso(modo === 'on'
        ? 'Encendido. quizessa.com/blog ya responde.'
        : 'Apagado. Todo /blog responde 404 y el webhook responde 503.');
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  const generarToken = async (fuente) => {
    setGuardando(true);
    setConfirmaToken('');
    try {
      const res = await api.post(`/sb-ctrl/blog/token/${fuente}`);
      setDatos(res.data);
      setAviso('Token generado. Copialo AHORA: no se vuelve a mostrar.');
    } catch {
      setAviso('No se pudo generar el token. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  const accion = async (id, cual) => {
    setGuardando(true);
    try {
      const res = await api.post(`/sb-ctrl/blog/articulo/${id}`, { accion: cual });
      setDatos(res.data);
      setAviso('Listo.');
    } catch {
      setAviso('No se pudo. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  useEffect(() => {
    if (showSection && !datos) cargar();
    // Mismo patron perezoso que las cuatro tarjetas de arriba.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const encendido = datos && datos.modo === 'on';
  const guion = (v) => (v === null || v === undefined || v === '' ? '-' : v);
  const columnas = ['Fecha', 'Título', 'Idioma', 'Estado', 'Origen', 'Motivo', 'Acciones'];

  return (
    <div className="space-y-4" data-testid="blog-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-blog">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Blog
      </button>

      {showSection && !datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="blog-card-vacia">
          <p className="text-[13px] text-[#e5e5e5]">{cargando ? 'Cargando...' : (aviso || 'No se pudo cargar.')}</p>
          {!cargando && (
            <Button onClick={cargar}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-reintentar">
              Reintentar
            </Button>
          )}
        </div>
      )}

      {showSection && datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="blog-card">
          <div>
            <h3 className="text-base font-semibold text-white">Blog</h3>
            <p className="text-xs text-[#6a6a6a]">El freno maestro de quizessa.com/blog. Publicar o no cada artículo lo decide Distribb; esto sólo apaga el blog entero. Apagado, todo /blog responde 404 y el webhook responde 503.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${encendido ? 'text-green-400' : 'text-[#888]'}`} data-testid="blog-estado">
              {encendido ? 'Estado: ENCENDIDO' : 'Estado: APAGADO'}
            </span>
            {encendido ? (
              <Button onClick={() => guardar('off')} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-apagar">
                {guardando ? 'Guardando...' : 'Apagar'}
              </Button>
            ) : (
              <Button onClick={() => setConfirmando(true)} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-encender">
                {guardando ? 'Guardando...' : 'Encender'}
              </Button>
            )}
            <a href="/blog" target="_blank" rel="noopener noreferrer"
              className="text-[#555] hover:text-white text-xs px-4" data-testid="blog-ver-sitio">Ver el blog</a>
          </div>

          {confirmando && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="blog-confirmar">
              <p className="text-[13px] text-[#e5e5e5]">¿Encender el blog? quizessa.com/blog empieza a responder y Google puede indexarlo.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => guardar('on')} disabled={guardando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-confirmar-si">
                  Sí, encender
                </Button>
                <Button variant="ghost" onClick={() => setConfirmando(false)}
                  className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-confirmar-no">
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {aviso && <p className="text-[11px] text-[#6a6a6a]" data-testid="blog-aviso">{aviso}</p>}

          {datos.token_nuevo && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="blog-token-nuevo">
              <p className="text-[13px] text-[#e5e5e5]">{`Token de ${datos.token_fuente}. Cópialo ahora: no se vuelve a mostrar.`}</p>
              <p className="text-[11px] text-[#aaa] font-mono break-all" data-testid="blog-token-valor">{datos.token_nuevo}</p>
              <Button onClick={() => { navigator.clipboard.writeText(datos.token_nuevo); toast.success('Token copiado'); }}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-token-copiar">
                Copiar token
              </Button>
            </div>
          )}

          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-[#e5e5e5]">Enchufes de Distribb</h4>
            {(datos.webhooks || []).map(w => (
              <div key={w.fuente} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-2" data-testid={`blog-webhook-${w.fuente}`}>
                <p className="text-[11px] text-[#e5e5e5]">{`${w.fuente} (${w.idioma === 'es' ? 'español' : 'inglés'})`}</p>
                <p className="text-[10px] text-[#aaa] font-mono break-all" data-testid={`blog-webhook-url-${w.fuente}`}>{w.url}</p>
                <p className="text-[10px] text-[#6a6a6a]" data-testid={`blog-webhook-estado-${w.fuente}`}>
                  {w.tiene_token ? 'Token puesto' : 'Sin token todavía'}
                  {w.ultima_entrega ? `, última entrega: ${formatDateTime(w.ultima_entrega.cuando)} (${w.ultima_entrega.resultado}, ${w.ultima_entrega.articulos} artículos)` : ', sin entregas'}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="ghost" onClick={() => { navigator.clipboard.writeText(w.url); toast.success('Dirección copiada'); }}
                    className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid={`blog-webhook-copiar-${w.fuente}`}>
                    Copiar dirección
                  </Button>
                  {w.tiene_token ? (
                    confirmaToken === w.fuente ? (
                      <>
                        <Button onClick={() => generarToken(w.fuente)} disabled={guardando}
                          className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid={`blog-webhook-regenerar-si-${w.fuente}`}>
                          Sí, regenerar
                        </Button>
                        <Button variant="ghost" onClick={() => setConfirmaToken('')}
                          className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid={`blog-webhook-regenerar-no-${w.fuente}`}>
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <Button variant="ghost" onClick={() => setConfirmaToken(w.fuente)} disabled={guardando}
                        className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid={`blog-webhook-regenerar-${w.fuente}`}>
                        Regenerar token
                      </Button>
                    )
                  ) : (
                    <Button onClick={() => generarToken(w.fuente)} disabled={guardando}
                      className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid={`blog-webhook-generar-${w.fuente}`}>
                      Generar token
                    </Button>
                  )}
                </div>
                {confirmaToken === w.fuente && (
                  <p className="text-[11px] text-[#6a6a6a]" data-testid={`blog-webhook-aviso-${w.fuente}`}>Al regenerar, el token anterior deja de valer en el acto y Distribb no puede publicar hasta que pegues el nuevo.</p>
                )}
              </div>
            ))}
          </div>

          <BlogEspanol />

          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-[#e5e5e5]">Últimos 20 artículos</h4>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={cargar} disabled={cargando}
                className="text-[#555] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="blog-actualizar">
                Actualizar
              </Button>
            </div>

            {(datos.articulos || []).length === 0 ? (
              <p className="text-[11px] text-[#6a6a6a]" data-testid="blog-sin-articulos">Todavía no ha llegado ningún artículo.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="blog-tabla">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-[#1a1a1a]">
                      {columnas.map(c => (
                        <th key={c} className="text-left py-2 px-2 text-[#555] uppercase whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {datos.articulos.map(a => (
                      <tr key={a.id} className="border-b border-[#1a1a1a]" data-testid={`blog-fila-${a.id}`}>
                        <td className="py-2 px-2 text-[#6a6a6a] whitespace-nowrap">{guion(a.fecha ? formatDate(a.fecha) : null)}</td>
                        <td className="py-2 px-2 text-[#aaa]">{guion(a.title)}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(a.lang)}</td>
                        <td className={`py-2 px-2 font-mono ${a.status === 'published' ? 'text-green-400' : 'text-[#555]'}`} data-testid={`blog-estado-${a.id}`}>{a.status}</td>
                        <td className="py-2 px-2 text-[#aaa] font-mono">{guion(a.source)}</td>
                        <td className={`py-2 px-2 font-mono ${a.motivo ? 'text-red-400' : 'text-[#aaa]'}`} data-testid={`blog-motivo-${a.id}`}>{guion(a.motivo)}</td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <a href={a.url} target="_blank" rel="noopener noreferrer"
                            className="text-[#555] hover:text-white text-xs px-2" data-testid={`blog-ver-${a.id}`}>Ver</a>
                          {a.status === 'published' ? (
                            <button onClick={() => accion(a.id, 'despublicar')} disabled={guardando}
                              className="text-[#555] hover:text-white text-xs px-2" data-testid={`blog-despublicar-${a.id}`}>Despublicar</button>
                          ) : (
                            <button onClick={() => accion(a.id, 'publicar')} disabled={guardando}
                              className="text-[#555] hover:text-white text-xs px-2" data-testid={`blog-publicar-${a.id}`}>Volver a publicar</button>
                          )}
                          {a.motivo && a.source !== 'traduccion' ? (
                            <button onClick={() => accion(a.id, 'reprocesar')} disabled={guardando}
                              className="text-[#555] hover:text-white text-xs px-2" data-testid={`blog-reprocesar-${a.id}`}>Reprocesar</button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* t7-2c (#532, E2): "Reporte semanal de prueba". Escoges un grupo y el servidor arma el
   correo del domingo de HOY de ese grupo (la plantilla v2) y lo manda SOLO al correo del
   profe dueño, sin escribir la fila del domingo ni tocar su foto. El API es
   `GET /api/sb-ctrl/reporte-semanal/grupos` (la lista) y
   `POST /api/sb-ctrl/reporte-semanal/prueba` con `{group_id}` (contesta `{enviado, mensaje}`).
   VA EN ESPAÑOL, como sus hermanas: nace plegada, la lista se pide al abrirla y el mensaje del
   servidor se pinta tal cual. Clases de servicio copiadas de la del chat del panel. */
export function ReporteSemanalPrueba() {
  const [showSection, setShowSection] = useState(false);
  const [grupos, setGrupos] = useState(null);
  const [grupo, setGrupo] = useState('');
  const [mandando, setMandando] = useState(false);
  const [aviso, setAviso] = useState('');

  const cargar = async () => {
    setAviso('');
    try {
      const res = await api.get('/sb-ctrl/reporte-semanal/grupos');
      const lista = (res.data && res.data.grupos) || [];
      setGrupos(lista);
      if (lista.length) setGrupo(lista[0].id);
    } catch {
      setAviso('No se pudo cargar. Intenta de nuevo.');
    }
  };

  useEffect(() => {
    if (showSection && !grupos) cargar();
    // Mismo patrón perezoso que ChatPanelConfig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const mandar = async () => {
    if (!grupo) return;
    setMandando(true);
    try {
      const res = await api.post('/sb-ctrl/reporte-semanal/prueba', { group_id: grupo });
      setAviso((res.data && res.data.mensaje) || '');
    } catch {
      setAviso('No se pudo mandar. Intenta de nuevo.');
    } finally { setMandando(false); }
  };

  // t7-3 remate 8 (#591, C16, regla del cero): cero alumnos se dice "Sin alumnos", nunca "0 alumnos"
  const alumnosDe = (n) => (!n ? 'Sin alumnos' : `${n} ${n === 1 ? 'alumno' : 'alumnos'}`);
  const rotulo = (g) => `${g.nombre}, ${g.correo}, ${alumnosDe(g.alumnos)}`;

  return (
    <div className="space-y-4" data-testid="reporte-prueba-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-reporte-prueba" style={{ position: 'relative' }}>
        {/* t7-3 remate 7 (regla 2 de #581): el area de toque de 44 sin cambiar el dibujo (mide 20) */}
        <span aria-hidden="true" data-area-de-toque="si" style={{ position: 'absolute', left: 0, right: 0, top: -12, bottom: -12 }} />
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Reporte semanal de prueba
      </button>

      {showSection && !grupos && aviso && (
        <p className="text-[11px] text-[#a0a0a0]" data-testid="reporte-prueba-aviso">{aviso}</p>
      )}

      {showSection && grupos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="reporte-prueba-card">
          <div>
            <h3 className="text-base font-semibold text-white" data-testid="reporte-prueba-titulo">Reporte semanal de prueba</h3>
            <p className="text-xs text-[#a0a0a0]">Arma el correo del domingo de hoy del grupo que escojas y lo manda solo al correo del profe dueño. No toca el envío del domingo.</p>
          </div>
          {/* t7-3 remate 8 (#591, C124): sin grupos que mandar, lo dice; sin selector vacio ni boton que no hace nada */}
          {grupos.length === 0 ? (
            <p className="text-xs text-[#a0a0a0]" data-testid="reporte-prueba-vacio">Ningún grupo tiene alumnos y publicaciones activas.</p>
          ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={grupo} onChange={e => setGrupo(e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none min-h-[44px]"
              data-testid="reporte-prueba-grupo">
              {grupos.map(g => <option key={g.id} value={g.id}>{rotulo(g)}</option>)}
            </select>
            <Button onClick={mandar} disabled={mandando || !grupo}
              className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="reporte-prueba-mandar">
              Mandar el reporte semanal de prueba
            </Button>
          </div>
          )}
          {aviso && <p className="text-[11px] text-[#a0a0a0]" data-testid="reporte-prueba-aviso">{aviso}</p>}
        </div>
      )}
    </div>
  );
}

/* t7-3 remate 8 (#592): "Grupo demo", el botón "Refrescar demo".
   Recorre la práctica sembrada del grupo demo para que la última sea de ayer y pone la caducidad
   de sus publicaciones a 6 días de hoy (`services/pipe_inst/demo_refresco.py`). Solo toca filas
   sembradas (`demo_seed_id`) y la fecha de caducidad de ese grupo: ningún usuario real. El API es
   `GET /api/sb-ctrl/demo/estado` y `POST /api/sb-ctrl/demo/refrescar`. VA EN ESPAÑOL, como sus
   hermanas: nace plegada, el estado se pide al abrirla y vuelve a pedirse después de un refresco. */
const MESES_DEL_DEMO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** "AAAA-MM-DD" -> "16 de octubre" (el día ya viene en la zona del profe: no se convierte nada). */
export function diaYMesDelDemo(dia) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia || '');
  if (!m) return '';
  return `${Number(m[3])} de ${MESES_DEL_DEMO[Number(m[2]) - 1]}`;
}

function haceDelDemo(n) {
  if (n === 0) return 'hoy';
  if (n === 1) return 'ayer';
  return `hace ${n} días`;
}

/* post-t7-1 L (#600): la práctica real de los alumnos del demo, que el mismo clic borra. Los singulares y la frase
   sin respuestas (solo progreso guardado) son derivados, marcados en el reporte. */
const respuestasDelDemo = (n) => (n === 1 ? '1 respuesta' : `${n} respuestas`);
const sesionesDelDemo = (n) => (n === 1 ? '1 sesión' : `${n} sesiones`);

export function textoDePracticaReal(p) {
  if (!p || !p.filas) return 'Sin práctica real.';
  // la regla del cero: un conteo en cero no se dice (post-t7-1 N, revisor de textos)
  let cuanto = 'progreso guardado sin respuestas';
  if (p.respuestas && p.sesiones) cuanto = `${respuestasDelDemo(p.respuestas)} en ${sesionesDelDemo(p.sesiones)}`;
  else if (p.respuestas) cuanto = respuestasDelDemo(p.respuestas);
  else if (p.sesiones) cuanto = `${sesionesDelDemo(p.sesiones)} sin respuestas`;
  return `Práctica real de los alumnos del demo: ${cuanto}. Se borra al refrescar.`;
}

function textoDelBorrado(b) {
  if (!b.respuestas) return 'Se borró la práctica real de los alumnos del demo.';
  return b.respuestas === 1
    ? 'Se borró 1 respuesta real de los alumnos del demo.'
    : `Se borraron ${b.respuestas} respuestas reales de los alumnos del demo.`;
}

/** El texto del resultado de un refresco (los cinco del encargo; los derivados van marcados en el reporte). */
export function textoDelRefresco(r) {
  const res = (r && r.resultado) || '';
  if (res === 'listo') {
    const fechas = r.caduca_dia
      ? `La última práctica sembrada quedó en ayer y las publicaciones con fecha caducan el ${diaYMesDelDemo(r.caduca_dia)}.`
      : 'La última práctica sembrada quedó en ayer.';
    const borradas = r.borradas || {};
    if (!borradas.filas) return `Listo. ${fechas}`;
    // post-t7-1 L (#600): sin `fechas` (un servidor de antes) se entiende que si las recorrio
    return `Listo. ${textoDelBorrado(borradas)} ${r.fechas === false ? 'Las fechas ya estaban al día.' : fechas}`;
  }
  if (res === 'al_dia') return 'El demo ya está al día.';
  if (res === 'reanudado') return 'Se terminó un refresco que había quedado a medias. Toca otra vez para ponerlo al día.';
  if (res === 'sin_demo') return 'No hay grupo demo sembrado.';
  return textoDelProblema((r && r.motivo) || 'el servidor contestó con un error.');
}

function textoDelProblema(motivo) {
  const frase = String(motivo).trim();
  const minuscula = frase.charAt(0).toLowerCase() + frase.slice(1);
  return `No se pudo refrescar el demo: ${/[.!?]$/.test(minuscula) ? minuscula : `${minuscula}.`}`;
}

export function GrupoDemo() {
  const [showSection, setShowSection] = useState(false);
  const [estado, setEstado] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  const [aviso, setAviso] = useState('');

  // t7-3 remate 8, cierre: la lectura que va justo despues de un refresco no pisa el aviso del refresco
  const cargar = async (despuesDeRefrescar = false) => {
    try {
      const res = await api.get('/sb-ctrl/demo/estado');
      setEstado(res.data || { hay_demo: false });
    } catch {
      if (!despuesDeRefrescar) setAviso('No se pudo cargar. Intenta de nuevo.');
    }
  };

  useEffect(() => {
    if (showSection && !estado) cargar();
    // Mismo patrón perezoso que ReporteSemanalPrueba.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const refrescar = async () => {
    setConfirmando(false);
    setRefrescando(true);
    setAviso('');
    try {
      // t7-3 remate 8, cierre: hasta 90 s (el refresco va con paso y tarda cerca de medio minuto; debajo del corte de
      // 100 s de Cloudflare). La espera de la casa, 10 s, lo daba por perdido aunque el servidor terminara bien.
      const res = await api.post('/sb-ctrl/demo/refrescar', undefined, { timeout: 90000 });
      setAviso(textoDelRefresco(res.data));
    } catch (e) {
      if (!(e && e.response)) {
        // sin respuesta (se acabo la espera o se cayo la red): el servidor puede haber terminado bien
        setAviso('No se pudo confirmar el refresco. Espera un minuto y toca "Refrescar demo" otra vez: si ya terminó, dirá que el demo ya está al día.');
      } else {
        const status = e.response.status;
        setAviso(textoDelProblema(status === 429 ? 'demasiados intentos; espera un minuto.' : 'el servidor contestó con un error.'));
      }
    } finally {
      setRefrescando(false);
      await cargar(true);
    }
  };

  // post-t7-1 (A7): con cero, "sin alumnos sembrados" (la regla del cero; antes "0 alumnos sembrados")
  const alumnos = (n) => (!n ? 'sin alumnos sembrados' : (n === 1 ? '1 alumno sembrado' : `${n} alumnos sembrados`));
  const confirmacion = (e) => {
    const resto = 'pone la caducidad de sus publicaciones a 6 días de hoy. No toca a ningún usuario real.';
    // con N = 0 solo falta la caducidad: la primera parte no se dice (regla del cero; texto derivado)
    if (!e.dias) return `P${resto.slice(1)}`;
    const dias = e.dias === 1 ? '1 día' : `${e.dias} días`;
    return `Recorre ${dias} la práctica sembrada de los ${e.alumnos} alumnos del demo, para que la última sea de ayer, y ${resto}`;
  };

  return (
    <div className="space-y-4" data-testid="demo-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-demo" style={{ position: 'relative' }}>
        {/* el área de toque de 44 sin cambiar el dibujo, como la de su hermana de arriba */}
        <span aria-hidden="true" data-area-de-toque="si" style={{ position: 'absolute', left: 0, right: 0, top: -12, bottom: -12 }} />
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Grupo demo
      </button>

      {showSection && !estado && aviso && (
        <p className="text-[11px] text-[#a0a0a0]" data-testid="demo-aviso">{aviso}</p>
      )}

      {showSection && estado && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="demo-card">
          <h3 className="text-base font-semibold text-white" data-testid="demo-titulo">Grupo demo</h3>
          {!estado.hay_demo ? (
            <p className="text-xs text-[#a0a0a0]" data-testid="demo-sin-demo">No hay grupo demo sembrado.</p>
          ) : (
            <>
              <div className="space-y-1 text-xs text-[#e5e5e5]">
                <p data-testid="demo-grupo">{`Grupo: ${estado.grupo}, ${alumnos(estado.alumnos)}.`}</p>
                {estado.ultima_practica && (
                  <p data-testid="demo-ultima">
                    {`Última práctica sembrada: ${haceDelDemo(estado.ultima_practica.hace)}, ${diaYMesDelDemo(estado.ultima_practica.dia)}.`}
                  </p>
                )}
                {(estado.publicaciones || []).map((p, i) => (
                  <p key={`${p.nombre}-${i}`} data-testid={`demo-pub-${i}`}>
                    {`${p.nombre}: ${p.caducada ? 'caducó' : 'caduca'} el ${diaYMesDelDemo(p.caduca_dia)}.`}
                  </p>
                ))}
                {estado.practica_real && (
                  <p data-testid="demo-practica-real">{textoDePracticaReal(estado.practica_real)}</p>
                )}
                <p data-testid="demo-ultimo-refresco">
                  {estado.ultimo_refresco_dia
                    ? `Último refresco: ${diaYMesDelDemo(estado.ultimo_refresco_dia)}.`
                    : 'Todavía no se ha refrescado.'}
                </p>
                {estado.problema && (
                  <p className="text-[#a0a0a0]" data-testid="demo-problema">{textoDelProblema(estado.problema)}</p>
                )}
              </div>
              {!confirmando && (
                <Button onClick={() => setConfirmando(true)} disabled={refrescando}
                  // post-t7-1 (A7): apagado, "Refrescando..." se lee igual que el boton encendido (sin la opacidad del apagado)
                  style={refrescando ? { opacity: 1, cursor: 'wait' } : undefined}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="demo-refrescar">
                  {refrescando ? 'Refrescando...' : 'Refrescar demo'}
                </Button>
              )}
              {refrescando && (
                <p className="text-[11px] text-[#a0a0a0]" data-testid="demo-espera">
                  Tarda cerca de medio minuto. No cierres esta página.
                </p>
              )}
              {confirmando && (
                <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="demo-confirmar">
                  <p className="text-[13px] text-[#e5e5e5]" data-testid="demo-confirmar-texto">{confirmacion(estado)}</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={refrescar} disabled={refrescando}
                      className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="demo-confirmar-si">
                      Sí, refrescar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmando(false)}
                      className="text-[#a0a0a0] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="demo-confirmar-no">
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          {aviso && <p className="text-[11px] text-[#a0a0a0]" data-testid="demo-aviso">{aviso}</p>}
        </div>
      )}
    </div>
  );
}

/* t7-2b (#518): "Chat del panel del profe (Pregúntale al panel)".
   El interruptor de la perilla `CHAT_PANEL` de `db.config` (nace APAGADA) y, al lado, el
   tope diario de preguntas por profe (`CHAT_PANEL_TOPE_DIARIO`, 50 por omisión; 0 = sin
   tope). Apagada, el avance del grupo no pinta la pastilla y las rutas del chat contestan
   que no está disponible. El API es `GET/POST /api/sb-ctrl/chat-panel`: el POST acepta
   `{modo}` y/o `{tope_diario}` y contesta lo mismo que el GET.

   VA EN ESPAÑOL, como sus hermanas de arriba: esto lo lee Carlos, no un usuario, y por eso
   no pasa por i18n. Calcada de `CallcCacheConfig`: nace plegada, el GET es perezoso (al
   abrirla), encender pide confirmación y apagar es de un clic. Dos diferencias, y solo
   estas: (1) si el GET falla la tarjeta lo dice (la de la caché se queda en blanco, porque
   su aviso vive dentro de la tarjeta que no llega a pintarse); (2) el campo numérico del
   tope, que ninguna hermana tiene. Clases de servicio copiadas de ella. */
export function ChatPanelConfig() {
  const [showSection, setShowSection] = useState(false);
  const [datos, setDatos] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [tope, setTope] = useState('');

  const tomar = (d) => {
    setDatos(d);
    setTope(d && d.tope_diario !== undefined && d.tope_diario !== null ? String(d.tope_diario) : '');
  };

  const cargar = async () => {
    setAviso('');
    try {
      const res = await api.get('/sb-ctrl/chat-panel');
      tomar(res.data);
    } catch {
      setAviso('No se pudo cargar. Intenta de nuevo.');
    }
  };

  const avisoDeGuardado = (d) => {
    const n = (d && d.recarga_segundos) || 0;
    return n === 0
      ? 'Guardado. Aplica a las preguntas que lleguen desde ahora.'
      : `Guardado. Las cajas lo leen en menos de ${Math.ceil(n / 60)} minutos.`;
  };

  const guardar = async (modo) => {
    setGuardando(true);
    setConfirmando(false);
    try {
      const res = await api.post('/sb-ctrl/chat-panel', { modo });
      tomar(res.data);
      setAviso(avisoDeGuardado(res.data));
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  const guardarTope = async () => {
    const limpio = String(tope).trim();
    if (!/^\d+$/.test(limpio)) {
      setAviso('El tope tiene que ser un número entero, 0 o mayor.');
      return;
    }
    setGuardando(true);
    try {
      const res = await api.post('/sb-ctrl/chat-panel', { tope_diario: parseInt(limpio, 10) });
      tomar(res.data);
      setAviso(avisoDeGuardado(res.data));
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  useEffect(() => {
    if (showSection && !datos) cargar();
    // Mismo patrón perezoso que CallcCacheConfig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const encendido = datos && datos.modo === 'on';

  return (
    <div className="space-y-4" data-testid="chat-panel-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-chat-panel" style={{ position: 'relative' }}>
        {/* t7-3 remate 7 (regla 2 de #581): el area de toque de 44 sin cambiar el dibujo (mide 20) */}
        <span aria-hidden="true" data-area-de-toque="si" style={{ position: 'absolute', left: 0, right: 0, top: -12, bottom: -12 }} />
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Chat del panel del profe
      </button>

      {showSection && !datos && aviso && (
        <p className="text-[11px] text-[#a0a0a0]" data-testid="chat-panel-aviso">{aviso}</p>
      )}

      {showSection && datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="chat-panel-card">
          <div>
            <h3 className="text-base font-semibold text-white" data-testid="chat-panel-titulo">Chat del panel del profe (Pregúntale al panel)</h3>
            <p className="text-xs text-[#a0a0a0]">Apagado, el avance del grupo no pinta la pastilla y el chat contesta que no está disponible. Encendido, el profe dueño de cada grupo le puede preguntar al panel. Cada pregunta se anota en los costos institucionales.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${encendido ? 'text-green-400' : 'text-[#888]'}`} data-testid="chat-panel-estado">
              {encendido ? 'Estado: ENCENDIDO' : 'Estado: APAGADO'}
            </span>
            {encendido ? (
              <Button onClick={() => guardar('off')} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="chat-panel-apagar">
                {guardando ? 'Guardando...' : 'Apagar'}
              </Button>
            ) : (
              <Button onClick={() => setConfirmando(true)} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="chat-panel-encender">
                {guardando ? 'Guardando...' : 'Encender'}
              </Button>
            )}
          </div>

          {confirmando && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="chat-panel-confirmar">
              <p className="text-[13px] text-[#e5e5e5]">¿Encender el chat para todos los profes? Cada pregunta cuesta y se anota en los costos institucionales.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => guardar('on')} disabled={guardando}
                  className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="chat-panel-confirmar-si">
                  Sí, encender
                </Button>
                <Button variant="ghost" onClick={() => setConfirmando(false)}
                  className="text-[#a0a0a0] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="chat-panel-confirmar-no">
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="chat-panel-tope" className="text-xs font-semibold text-[#e5e5e5] block">Tope diario de preguntas por profe (0 = sin tope)</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input id="chat-panel-tope" type="number" min="0" step="1" inputMode="numeric" value={tope}
                onChange={e => setTope(e.target.value)}
                className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none min-h-[44px]"
                data-testid="chat-panel-tope" />
              <Button onClick={guardarTope} disabled={guardando}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="chat-panel-tope-guardar">
                Guardar tope
              </Button>
            </div>
          </div>

          {aviso && <p className="text-[11px] text-[#a0a0a0]" data-testid="chat-panel-aviso">{aviso}</p>}
        </div>
      )}
    </div>
  );
}

/* tutor-1 (A7, Anexo D): "Tutor del alumno". La perilla `TUTOR` de `db.config` (nace APAGADA: sin fila), con sus tres
   valores (apagado, solo yo, encendido), los topes de Gratis e invitado, los dos frenos escondidos de Pro y los dos
   modelos; y el resumen de hoy, del mes, de quien mas lo usa y de los pulgares abajo. El API es
   `GET/POST /api/sb-ctrl/tutor` (el POST acepta cualquier subconjunto de llaves y contesta lo mismo que el GET) y
   `GET /api/sb-ctrl/tutor/resumen`. Solo en espanol, como sus hermanas: la lee Carlos. Calcada de `ChatPanelConfig`:
   nace plegada, el GET es perezoso, encender pide confirmacion. */
const CAMPOS_DEL_TUTOR = [
  { llave: 'tope_free', etiqueta: 'Tope de Gratis (mensajes al día)', numero: true },
  { llave: 'tope_guest', etiqueta: 'Tope de invitado', numero: true },
  { llave: 'freno_pro', etiqueta: 'Freno escondido de Pro (mensajes al día)', numero: true },
  { llave: 'freno_web', etiqueta: 'Freno escondido de búsquedas en la web (al día)', numero: true },
  { llave: 'model', etiqueta: 'Modelo', numero: false },
  { llave: 'model_web', etiqueta: 'Modelo cuando busca en la web', numero: false },
];

export function TutorConfig() {
  const [showSection, setShowSection] = useState(false);
  const [datos, setDatos] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [campos, setCampos] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState('');

  const tomar = (d) => {
    setDatos(d);
    const c = {};
    CAMPOS_DEL_TUTOR.forEach(({ llave }) => { c[llave] = d && d[llave] !== undefined && d[llave] !== null ? String(d[llave]) : ''; });
    setCampos(c);
  };

  const cargar = async () => {
    setAviso('');
    try {
      const [res, res2] = await Promise.all([api.get('/sb-ctrl/tutor'), api.get('/sb-ctrl/tutor/resumen')]);
      tomar(res.data);
      setResumen(res2.data);
    } catch {
      setAviso('No se pudo cargar. Intenta de nuevo.');
    }
  };

  const mandar = async (cuerpo) => {
    setGuardando(true);
    setConfirmando(false);
    try {
      const res = await api.post('/sb-ctrl/tutor', cuerpo);
      tomar(res.data);
      setAviso('Guardado. Aplica a los mensajes que lleguen desde ahora.');
    } catch {
      setAviso('No se pudo guardar. Intenta de nuevo.');
    } finally { setGuardando(false); }
  };

  const guardarCampo = (campo) => {
    const valor = String(campos[campo.llave] || '').trim();
    if (campo.numero && !/^\d+$/.test(valor)) { setAviso('No se pudo guardar. Intenta de nuevo.'); return; }
    if (!campo.numero && (!valor || valor.length > 60)) { setAviso('No se pudo guardar. Intenta de nuevo.'); return; }
    mandar({ [campo.llave]: campo.numero ? parseInt(valor, 10) : valor });
  };

  useEffect(() => {
    if (showSection && !datos) cargar();
    // Mismo patrón perezoso que ChatPanelConfig.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  const modo = datos && datos.modo;
  const estado = modo === 'on' ? 'Estado: ENCENDIDO' : (modo === 'admin' ? 'Estado: SÓLO YO' : 'Estado: APAGADO');
  const boton = 'bg-[#FF6B00] hover:bg-[#e55f00] text-white rounded-xl text-xs w-full sm:w-auto min-h-[44px] px-4';
  const bloque = (b) => (b ? `${b.mensajes} mensajes, ${b.busquedas} búsquedas, ${b.usd} USD` : '');

  return (
    <div className="space-y-4" data-testid="tutor-admin-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-tutor-admin" style={{ position: 'relative' }}>
        <span aria-hidden="true" data-area-de-toque="si" style={{ position: 'absolute', left: 0, right: 0, top: -12, bottom: -12 }} />
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        {showSection ? 'Ocultar' : 'Mostrar'} Tutor del alumno
      </button>

      {showSection && !datos && aviso && (
        <p className="text-[11px] text-[#a0a0a0]" data-testid="tutor-admin-aviso">{aviso}</p>
      )}

      {showSection && datos && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4" data-testid="tutor-admin-card">
          <div>
            <h3 className="text-base font-semibold text-white" data-testid="tutor-admin-titulo">Tutor del alumno</h3>
            <p className="text-xs text-[#a0a0a0]">Apagado, nadie ve el Tutor y sus rutas contestan que no existe. En 'Sólo yo', únicamente tu cuenta lo ve (para probarlo en producción). Encendido, lo ven todos: invitado 3 mensajes al día, Gratis 5, Pro sin tope visible. Cada mensaje se anota en costos con el modelo del tutor.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <span className={`text-[13px] font-mono ${modo === 'on' ? 'text-green-400' : (modo === 'admin' ? 'text-[#F7A06A]' : 'text-[#888]')}`} data-testid="tutor-admin-estado">
              {estado}
            </span>
            <Button onClick={() => mandar({ modo: 'off' })} disabled={guardando || modo === 'off'} className={boton} data-testid="tutor-admin-apagar">Apagar</Button>
            <Button onClick={() => mandar({ modo: 'admin' })} disabled={guardando || modo === 'admin'} className={boton} data-testid="tutor-admin-solo-yo">Sólo yo</Button>
            <Button onClick={() => setConfirmando(true)} disabled={guardando || modo === 'on'} className={boton} data-testid="tutor-admin-encender">Encender</Button>
          </div>

          {confirmando && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-4 space-y-3" data-testid="tutor-admin-confirmar">
              <p className="text-[13px] text-[#e5e5e5]">¿Encender el Tutor para todos? Gratis e invitado ven su tope; Pro no tiene tope visible y sus frenos escondidos avisan por correo.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => mandar({ modo: 'on' })} disabled={guardando} className={boton} data-testid="tutor-admin-confirmar-si">Sí, encender</Button>
                <Button variant="ghost" onClick={() => setConfirmando(false)}
                  className="text-[#a0a0a0] hover:text-white text-xs w-full sm:w-auto min-h-[44px] px-4" data-testid="tutor-admin-confirmar-no">
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {CAMPOS_DEL_TUTOR.map((campo) => (
            <div className="space-y-2" key={campo.llave}>
              <label htmlFor={`tutor-admin-${campo.llave}`} className="text-xs font-semibold text-[#e5e5e5] block">{campo.etiqueta}</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input id={`tutor-admin-${campo.llave}`} type={campo.numero ? 'number' : 'text'} min={campo.numero ? '0' : undefined}
                  step={campo.numero ? '1' : undefined} inputMode={campo.numero ? 'numeric' : undefined} value={campos[campo.llave] || ''}
                  onChange={(e) => setCampos((c) => ({ ...c, [campo.llave]: e.target.value }))}
                  className="w-full bg-[#141414] border border-[#262626] rounded-lg text-xs text-white p-1.5 font-mono focus:border-[#FF6B00] outline-none min-h-[44px]"
                  data-testid={`tutor-admin-${campo.llave}`} />
                <Button onClick={() => guardarCampo(campo)} disabled={guardando} className={boton} data-testid={`tutor-admin-guardar-${campo.llave}`}>
                  Guardar
                </Button>
              </div>
            </div>
          ))}

          {resumen && (
            <div className="space-y-2 text-xs text-[#e5e5e5]" data-testid="tutor-admin-resumen">
              <p>Hoy: {bloque(resumen.hoy)}. Este mes: {bloque(resumen.mes)}.</p>
              <p className="font-semibold">Quién más lo usa (30 días)</p>
              <ul className="space-y-1">
                {(resumen.usuarios || []).map((u) => (
                  <li key={u.correo} className="font-mono text-[11px] text-[#a0a0a0]">{u.correo}, {u.plan}, {u.mensajes} mensajes, {u.busquedas} búsquedas</li>
                ))}
              </ul>
              <p>Pulgar abajo: {(resumen.pulgares && resumen.pulgares.abajo) || 0} de {(resumen.pulgares && resumen.pulgares.total) || 0} respuestas (30 días)</p>
            </div>
          )}

          {aviso && <p className="text-[11px] text-[#a0a0a0]" data-testid="tutor-admin-aviso">{aviso}</p>}
        </div>
      )}
    </div>
  );
}

// ── User Analytics ──
function UserAnalytics() {
  const [showSection, setShowSection] = useState(false);
  const [funnelType, setFunnelType] = useState('guest');
  const [funnel, setFunnel] = useState(null);
  const [friction, setFriction] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [outcomesPlan, setOutcomesPlan] = useState('guest');
  const [events, setEvents] = useState(null);
  // tutor-2 (F.2): el cuadro "Tutor (30 dias)", leido en la misma carga.
  const [tutorPantallas, setTutorPantallas] = useState(null);
  // marca-2 (firma #740): el cuadro "Clientes vistos (30 dias)", leido en la misma carga.
  const [clientesVistos, setClientesVistos] = useState(null);
  const [borradas, setBorradas] = useState(null);
  const [loadingF, setLoadingF] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoadingF(true);
    try {
      const [f, fr, o, e, tp, cv, br] = await Promise.all([
        api.get(`/sb-ctrl/analytics/funnel?days=30&user_type=${funnelType}`),
        api.get('/sb-ctrl/analytics/friction?days=30'),
        api.get(`/sb-ctrl/analytics/upload-outcomes?days=30&plan=${outcomesPlan}`),
        api.get('/sb-ctrl/analytics/events?days=7'),
        api.get('/sb-ctrl/tutor/pantallas?days=30').catch(() => null),
        api.get('/sb-ctrl/clientes-vistos').catch(() => null),
        api.get('/sb-ctrl/borradas').catch(() => null),
      ]);
      setFunnel(f.data);
      setFriction(fr.data);
      setOutcomes(o.data);
      setEvents(e.data);
      setTutorPantallas(tp ? tp.data : null);
      setClientesVistos(cv ? cv.data : null);
      setBorradas(br ? br.data : null);
    } catch {}
    setLoadingF(false);
  }, [funnelType, outcomesPlan]);

  useEffect(() => { if (showSection) fetchAll(); }, [showSection, fetchAll]);

  const funnelSteps = funnel?.funnel ? Object.entries(funnel.funnel) : [];
  const base = funnelSteps.length > 0 ? funnelSteps[0][1].unique_users : 1;

  // Find biggest drop
  let biggestDropIdx = -1;
  let biggestDrop = 0;
  for (let i = 1; i < funnelSteps.length; i++) {
    const drop = funnelSteps[i - 1][1].percentage - funnelSteps[i][1].percentage;
    if (drop > biggestDrop) { biggestDrop = drop; biggestDropIdx = i; }
  }

  const stepLabels = {
    session_created: 'Session Created', dashboard_viewed: 'Dashboard Viewed',
    upload_attempted: 'Upload Attempted', upload_succeeded: 'Upload Succeeded',
    processing_completed: 'Processing Done', study_started: 'Study Started',
    conversion_completed: 'Converted', pricing_viewed: 'Pricing Viewed',
  };

  const frictionLabels = {
    friction_char_limit: 'Char Limit', friction_credits: 'Credits', friction_doc_limit: 'Doc Limit',
    friction_guest_daily: 'Guest Daily', friction_guest_lifetime: 'Guest Lifetime',
    friction_file_error: 'File Error', friction_upload_error: 'Upload Error',
    // tutor-2 (F.1)
    friction_tutor_cap: 'Tutor: tope del día', friction_tutor_error: 'Tutor: falla',
  };

  const outcomeLabels = {
    succeeded: { label: 'Succeeded', color: 'bg-emerald-500' },
    char_limit: { label: 'Char Limit', color: 'bg-[#FF6B00]' },
    credits_insufficient: { label: 'Insufficient Credits', color: 'bg-amber-500' },
    doc_limit: { label: 'Doc Limit', color: 'bg-amber-500' },
    file_error: { label: 'File Error', color: 'bg-red-500' },
    guest_daily: { label: 'Guest Daily', color: 'bg-orange-500' },
    guest_lifetime: { label: 'Guest Lifetime', color: 'bg-orange-500' },
    upload_error: { label: 'Network Error', color: 'bg-red-500' },
    unknown: { label: 'Unknown', color: 'bg-[#333]' },
  };

  return (
    <>
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="admin-toggle-user-analytics">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        <BarChart3 className="w-4 h-4" /> {showSection ? 'Hide' : 'Show'} User Analytics
      </button>

      {showSection && (
        <div className="space-y-6">
          {loadingF && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" /></div>}

          {/* Funnel */}
          {funnel && (
            <div className="glass-card rounded-[20px] p-6" data-testid="analytics-funnel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-white">User Funnel (30d)</h3>
                <div className="flex gap-1">
                  {['guest', 'registered'].map(t => (
                    <button key={t} onClick={() => setFunnelType(t)}
                      className={`px-3 py-1 text-xs rounded-lg ${funnelType === t ? 'bg-[#FF6B00] text-white' : 'bg-[#1a1a1a] text-[#6a6a6a]'}`}
                      data-testid={`funnel-toggle-${t}`}>
                      {t === 'guest' ? 'Guests' : 'Registered'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {funnelSteps.map(([step, data], i) => {
                  const pct = base > 0 ? (data.unique_users / base) * 100 : 0;
                  const isBigDrop = i === biggestDropIdx;
                  return (
                    <div key={step} className="flex items-center gap-3 text-xs" data-testid={`funnel-step-${step}`}>
                      <span className="w-32 text-[#a0a0a0] truncate">{stepLabels[step] || step}</span>
                      <div className="flex-1 h-5 bg-[#1a1a1a] rounded overflow-hidden relative">
                        <div className="h-full bg-[#FF6B00] rounded transition-all" style={{ width: `${Math.max(pct, 1)}%` }} />
                      </div>
                      <span className="w-8 text-right text-white font-mono">{data.unique_users}</span>
                      <span className="w-10 text-right text-[#555] font-mono">{data.percentage}%</span>
                      {isBigDrop && <span className="text-red-400 text-[10px]" title={`-${biggestDrop}% drop`}>!!</span>}
                    </div>
                  );
                })}
              </div>
              {/* Intent-to-success drop */}
              {funnel.funnel.upload_attempted && funnel.funnel.upload_succeeded && (
                <p className="text-xs text-[#555] mt-3">
                  {Math.round(100 - (funnel.funnel.upload_succeeded.unique_users / Math.max(funnel.funnel.upload_attempted.unique_users, 1)) * 100)}% of users who tried to upload failed.
                </p>
              )}
            </div>
          )}

          {/* Upload Outcomes */}
          {outcomes && (
            <div className="glass-card rounded-[20px] p-6" data-testid="analytics-outcomes">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-white">Upload Outcomes (30d)</h3>
                <div className="flex gap-1">
                  {['guest', 'free', 'pro', 'all'].map(p => (
                    <button key={p} onClick={() => setOutcomesPlan(p)}
                      className={`px-2 py-1 text-[10px] rounded-lg ${outcomesPlan === p ? 'bg-[#FF6B00] text-white' : 'bg-[#1a1a1a] text-[#6a6a6a]'}`}
                      data-testid={`outcomes-plan-${p}`}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-[#555] mb-3">Total Attempts: {outcomes.total_attempts}</p>
              <div className="space-y-1.5">
                {Object.entries(outcomes.outcomes).filter(([, v]) => v > 0).map(([key, count]) => {
                  const pct = outcomes.total_attempts > 0 ? (count / outcomes.total_attempts * 100) : 0;
                  const info = outcomeLabels[key] || { label: key, color: 'bg-[#333]' };
                  return (
                    <div key={key} className="flex items-center gap-3 text-xs" data-testid={`outcome-${key}`}>
                      <span className="w-32 text-[#a0a0a0] truncate">{info.label}</span>
                      <div className="flex-1 h-4 bg-[#1a1a1a] rounded overflow-hidden">
                        <div className={`h-full ${info.color} rounded transition-all`} style={{ width: `${Math.max(pct, 0.5)}%` }} />
                      </div>
                      <span className="w-8 text-right text-white font-mono">{count}</span>
                      <span className="w-10 text-right text-[#555] font-mono">{Math.round(pct)}%</span>
                    </div>
                  );
                })}
              </div>
              {outcomes.processing_outcomes.total_succeeded_uploads > 0 && (
                <div className="mt-4 pt-3 border-t border-[#1a1a1a]">
                  <p className="text-xs text-[#555] mb-2">Of {outcomes.processing_outcomes.total_succeeded_uploads} successful uploads:</p>
                  <div className="flex gap-4 text-xs">
                    <span className="text-emerald-400">{outcomes.processing_outcomes.processed_successfully} processed</span>
                    <span className="text-red-400">{outcomes.processing_outcomes.processing_failed} failed</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Friction Conversion */}
          {friction && (
            <div className="glass-card rounded-[20px] p-6" data-testid="analytics-friction">
              <h3 className="text-base font-semibold text-white mb-4">Friction Conversion (30d)</h3>
              <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(friction.friction_types).map(([type, actions]) => {
                  const shown = actions.shown || 0;
                  return (
                    <div key={type} className="bg-[#0e0e0e] border border-[#1a1a1a] rounded-xl p-4 min-w-[180px]" data-testid={`friction-card-${type}`}>
                      <p className="text-xs text-[#a0a0a0] mb-1">{frictionLabels[type] || type}</p>
                      <p className="text-lg font-bold text-white">{shown} <span className="text-xs font-normal text-[#555]">shown</span></p>
                      <p className="text-xs text-[#FF6B00] mb-2">{actions.conversion_rate || '0%'} took action</p>
                      <div className="text-[10px] text-[#444] space-y-0.5">
                        {Object.entries(actions).filter(([k]) => !['shown', 'left_without_action', 'conversion_rate'].includes(k)).map(([k, v]) => (
                          <p key={k}>{v} {k.replace(/_/g, ' ')}</p>
                        ))}
                        <p>{actions.left_without_action || 0} left</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[#444]">
                By plan: {Object.entries(friction.by_plan || {}).map(([p, c]) => `${p}: ${c}`).join(', ')}
              </p>
            </div>
          )}

          {/* tutor-2 (F.2): Tutor (30 dias), por pantalla y aparato; conteos, jamas porcentajes. */}
          {tutorPantallas && <CuadroDelTutor datos={tutorPantallas} />}

          {/* marca-2 (firma #740): Clientes vistos (30 dias), por dominio, web o app y build; conteos. */}
          {clientesVistos && <CuadroClientesVistos datos={clientesVistos} />}
          {borradas && <CuadroBorradas datos={borradas} />}

          {/* Recent Events */}
          {events && (
            <div className="glass-card rounded-[20px] p-6" data-testid="analytics-events">
              <h3 className="text-base font-semibold text-white mb-4">Recent Events (7d)</h3>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0e0e0e]">
                    <tr className="text-[#555] text-left">
                      <th className="pb-2 pr-3">Date</th>
                      <th className="pb-2 pr-3">Time</th>
                      <th className="pb-2 pr-3">User</th>
                      <th className="pb-2 pr-3">Plan</th>
                      <th className="pb-2 pr-3">Type</th>
                      <th className="pb-2 pr-3">Event</th>
                      <th className="pb-2 pr-3">Action</th>
                      {/* #158b: el porque de la falla vivia en `details` y no se pintaba. */}
                      <th className="pb-2 pr-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(events.events || []).slice(0, 50).map((e, i) => (
                      <tr key={i} className="border-t border-[#1a1a1a] text-[#a0a0a0]" title={formatIsoTooltip(e.created_at)}>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{formatDate(e.created_at)}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{formatTime(e.created_at)}</td>
                        <td className="py-1.5 pr-3 max-w-[120px] truncate">{e.user_plan === 'guest' ? `Guest (${(e.user_id || '').slice(0, 6)})` : e.user_email}</td>
                        <td className="py-1.5 pr-3">{e.user_plan}</td>
                        <td className="py-1.5 pr-3"><span className={e.category === 'friction' ? 'text-red-400' : 'text-emerald-400'}>{e.category}</span></td>
                        <td className="py-1.5 pr-3">{e.event_type?.replace('friction_', '').replace(/_/g, ' ')}</td>
                        <td className="py-1.5 pr-3">{e.action}</td>
                        {(() => {
                          const det = formatEventDetails(e.details);
                          // #164b: la causa va DELANTE del detalle crudo — es lo que se
                          // lee de un vistazo; los ids y el mensaje quedan detras para
                          // quien necesite rastrear. Sin causa, la celda es la de #158b.
                          const causa = e.probable_cause || '';
                          const compacto = causa
                            ? `${causa}${det.compacto ? `, ${det.compacto}` : ''}`
                            : det.compacto;
                          const completo = causa
                            ? `${causa}${det.completo ? `\n${det.completo}` : ''}`
                            : det.completo;
                          return (
                            <td className="py-1.5 pr-3 max-w-[280px] truncate font-mono text-[10px] text-[#777]"
                                title={completo}
                                data-testid="analytics-event-details">
                              {compacto}
                            </td>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}


// ── Test Runner ──
function TestRunner() {
  const [tab, setTab] = useState('new');
  const [docs, setDocs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [docId, setDocId] = useState('');
  const [label, setLabel] = useState('');
  const [callAModel, setCallAModel] = useState('');
  const [callCModel, setCallCModel] = useState('');
  const [callBModel, setCallBModel] = useState('');
  const [temperature, setTemperature] = useState('');
  const [promptA, setPromptA] = useState('');
  const [promptC, setPromptC] = useState('');
  const [promptEqSystem, setPromptEqSystem] = useState('');
  const [promptEqUser, setPromptEqUser] = useState('');
  const [promptAUser, setPromptAUser] = useState('');
  const [promptCUser, setPromptCUser] = useState('');
  const [promptB, setPromptB] = useState('');
  const [promptBUser, setPromptBUser] = useState('');
  const [promptP0, setPromptP0] = useState('');
  const [promptP0User, setPromptP0User] = useState('');
  const [showPromptA, setShowPromptA] = useState(false);
  const [showPromptC, setShowPromptC] = useState(false);
  const [showPromptEqSystem, setShowPromptEqSystem] = useState(false);
  const [showPromptEqUser, setShowPromptEqUser] = useState(false);
  const [showPromptAUser, setShowPromptAUser] = useState(false);
  const [showPromptCUser, setShowPromptCUser] = useState(false);
  const [showPromptB, setShowPromptB] = useState(false);
  const [showPromptBUser, setShowPromptBUser] = useState(false);
  const [showPromptP0, setShowPromptP0] = useState(false);
  const [showPromptP0User, setShowPromptP0User] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedRuns, setSelectedRuns] = useState([]);
  const [detailRun, setDetailRun] = useState(null);
  const [showSection, setShowSection] = useState(false);
  const [includeCallC, setIncludeCallC] = useState(true);
  const [strictTopicLabeling, setStrictTopicLabeling] = useState(true);
  // Sprint X3.2 — Phase 4.6 rationale generation controls
  const [enableRationale, setEnableRationale] = useState(true);
  const [rationaleVariant, setRationaleVariant] = useState('A');
  const [promptRationale, setPromptRationale] = useState('');
  const [showPromptRationale, setShowPromptRationale] = useState(false);
  const timerRef = useRef(null);

  const fetchDocs = async () => {
    try { const r = await api.get('/admin/test-runner/documents'); setDocs(r.data.documents || []); } catch {}
  };
  const fetchRuns = async () => {
    try { const r = await api.get('/admin/test-runner/runs'); setRuns(r.data.runs || []); } catch {}
  };
  const fetchModels = async () => {
    try { const r = await api.get('/admin/test-runner/models'); setModels(r.data.models || []); } catch {}
  };

  useEffect(() => {
    // Always fetch models on mount - unconditional
    api.get('/admin/test-runner/models')
      .then(r => { setModels(r.data.models || []); })
      .catch(err => console.error('Failed to load models:', err));
  }, []);

  useEffect(() => {
    if (showSection) { fetchDocs(); fetchRuns(); }
  }, [showSection]);

  const executeRun = async () => {
    if (!docId || !label.trim()) { toast.error('Document and label required'); return; }
    setRunning(true); setElapsed(0); setLastResult(null);
    const start = Date.now();
    timerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    try {
      const payload = {
        document_id: docId,
        label: label.trim(),
        include_call_c: includeCallC,
        strict_topic_labeling: strictTopicLabeling,
        enable_rationale_generation: enableRationale,
        rationale_variant: rationaleVariant,
      };
      if (callAModel) payload.call_a_model = callAModel;
      if (callCModel) payload.call_c_model = callCModel;
      if (callBModel) payload.call_b_model = callBModel;
      if (temperature !== '') payload.temperature = parseFloat(temperature);
      if (promptA.trim()) payload.prompt_override_call_a = promptA.trim();
      if (promptC.trim()) payload.prompt_override_call_c = promptC.trim();
      if (promptEqSystem.trim()) payload.prompt_override_eq_system = promptEqSystem.trim();
      if (promptEqUser.trim()) payload.prompt_override_eq_user = promptEqUser.trim();
      if (promptAUser.trim()) payload.prompt_override_call_a_user = promptAUser.trim();
      if (promptCUser.trim()) payload.prompt_override_call_c_user = promptCUser.trim();
      if (promptB.trim()) payload.prompt_override_call_b = promptB.trim();
      if (promptBUser.trim()) payload.prompt_override_call_b_user = promptBUser.trim();
      if (promptP0.trim()) payload.prompt_override_phase0 = promptP0.trim();
      if (promptP0User.trim()) payload.prompt_override_phase0_user = promptP0User.trim();
      if (promptRationale.trim()) payload.rationale_prompt_override = promptRationale.trim();
      const r = await api.post('/admin/test-runner/run', payload);
      setLastResult(r.data);
      if (r.data.status === 'completed') toast.success('Test run completed');
      else toast.error(r.data.error || 'Run failed');
      fetchRuns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { clearInterval(timerRef.current); setRunning(false); }
  };

  const deleteRun = async (runId) => {
    if (!window.confirm('Delete this run?')) return;
    try {
      await api.delete(`/admin/test-runner/run/${runId}`);
      toast.success('Deleted');
      setRuns(prev => prev.filter(r => r.run_id !== runId));
      setSelectedRuns(s => s.filter(r => r !== runId));
    } catch { toast.error('Delete failed'); }
  };

  const viewDetail = async (runId) => {
    try { const r = await api.get(`/admin/test-runner/run/${runId}`); setDetailRun(r.data); } catch { toast.error('Failed to load details'); }
  };

  const toggleSelect = (runId) => {
    setSelectedRuns(s => s.includes(runId) ? s.filter(r => r !== runId) : [...s, runId]);
  };

  const compareRuns = selectedRuns.map(id => runs.find(r => r.run_id === id)).filter(Boolean);

  const CustomDropdown = ({ value, onChange, options, placeholder, tid, label: lbl, maxVisible = 10 }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);
    React.useEffect(() => {
      const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);
    const selectedLabel = options.find(o => o.value === value)?.label || placeholder || 'Select...';
    const itemH = 28;
    return (
      <div ref={ref} className="relative">
        {lbl && <span className="text-[10px] text-[#555] block mb-1">{lbl}</span>}
        <button type="button" onClick={() => setOpen(!open)} data-testid={tid}
          className="w-full bg-[#141414] border border-[#1a1a1a] rounded-lg text-xs text-white p-2 font-mono focus:border-[#FF6B00] outline-none text-left truncate flex items-center justify-between">
          <span className={value ? 'text-white' : 'text-[#555]'}>{selectedLabel}</span>
          <ChevronDown className={`w-3 h-3 text-[#555] transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-[#141414] border border-[#1a1a1a] rounded-lg shadow-lg overflow-y-auto"
            style={{ maxHeight: maxVisible * itemH }}>
            {options.map(o => (
              <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                className={`px-2 py-1.5 text-xs font-mono cursor-pointer hover:bg-[#222] ${value === o.value ? 'text-[#FF6B00]' : 'text-white'}`}>
                {o.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ModelSelect = ({ value, onChange, tid, label: lbl }) => {
    const opts = [{ value: '', label: '(Production Default)' }, ...models.map(m => ({ value: m, label: m }))];
    return <CustomDropdown value={value} onChange={onChange} options={opts} placeholder="(Production Default)" tid={tid} label={lbl} maxVisible={8} />;
  };

  const t = lastResult?.totals || {};
  const bloom = t.bloom_distribution || {};
  const biasColor = (v) => v > 50 ? 'text-red-400' : v < 40 ? 'text-green-400' : 'text-yellow-400';

  return (
    <div className="space-y-4" data-testid="test-runner-section">
      <button onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="toggle-test-runner">
        <ChevronDown className={`w-4 h-4 transition-transform ${showSection ? 'rotate-180' : ''}`} />
        <FlaskConical className="w-4 h-4" /> {showSection ? 'Hide' : 'Show'} Test Runner
      </button>

      {showSection && (
        <div className="glass-card rounded-[20px] p-5 md:p-6 space-y-4">
          {/* Sub-tabs */}
          <div className="flex gap-2 border-b border-[#1a1a1a] pb-2">
            {[['new', 'New Run', Play], ['history', 'History', Clock], ['compare', 'Compare', GitCompare]].map(([k, lbl, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`test-runner-tab-${k}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${tab === k ? 'bg-[#FF6B00]/15 text-[#FF6B00]' : 'text-[#555] hover:text-white'}`}>
                <Icon className="w-3.5 h-3.5" /> {lbl}
              </button>
            ))}
          </div>

          {/* ── New Run ── */}
          {tab === 'new' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-[10px] text-[#555] block mb-1">Document</span>
                  <CustomDropdown value={docId} onChange={setDocId} tid="test-runner-doc" maxVisible={8}
                    placeholder="Select document..."
                    options={[
                      { value: '', label: 'Select document...' },
                      ...[...docs].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map(d => ({
                        value: d.document_id,
                        label: `${d.name} (${d.char_count?.toLocaleString() || '?'} chars)`
                      }))
                    ]} />
                </div>
                <div>
                  <span className="text-[10px] text-[#555] block mb-1">Label</span>
                  <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. haiku-temp07-run3"
                    className="bg-[#141414] border-[#1a1a1a] text-xs text-white font-mono" data-testid="test-runner-label" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <ModelSelect value={callAModel} onChange={setCallAModel} tid="test-runner-model-a" label="Call A (Questions)" />
                <ModelSelect value={callCModel} onChange={setCallCModel} tid="test-runner-model-c" label="Call C (Distractors)" />
                <ModelSelect value={callBModel} onChange={setCallBModel} tid="test-runner-model-b" label="Call B (Flashcards)" />
                <div>
                  <span className="text-[10px] text-[#555] block mb-1">Temperature <span className="text-[#333]">(ignored for reasoning)</span></span>
                  <Input type="number" min="0" max="1" step="0.1" value={temperature} onChange={e => setTemperature(e.target.value)}
                    placeholder="default" className="bg-[#141414] border-[#1a1a1a] text-xs text-white font-mono" data-testid="test-runner-temp" />
                </div>
              </div>

              {/* Include Call C checkbox — between Temperature and Prompt Overrides */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={includeCallC} onChange={e => setIncludeCallC(e.target.checked)}
                  className="accent-[#FF6B00] w-3.5 h-3.5" data-testid="test-runner-include-call-c" />
                <span className="text-[10px] text-[#888]">Include Call C <span className="text-[#444]">(distractor generation, uncheck to skip)</span></span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={strictTopicLabeling} onChange={e => setStrictTopicLabeling(e.target.checked)}
                  className="accent-[#FF6B00] w-3.5 h-3.5" data-testid="test-runner-strict-topic" />
                <span className="text-[10px] text-[#888]">Strict Topic Labeling <span className="text-[#444]">(forces Call A to use exact Phase 0 topic names, reduces topic filter drops)</span></span>
              </label>

              {/* Sprint X3.2 — Phase 4.6 rationale generation toggle + variant selector */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={enableRationale} onChange={e => setEnableRationale(e.target.checked)}
                    className="accent-[#FF6B00] w-3.5 h-3.5" data-testid="test-runner-enable-rationale" />
                  <span className="text-[10px] text-[#888]">Enable Rationale Generation <span className="text-[#444]">(per-distractor explanations, gpt-4.1-mini, parallel with EQ)</span></span>
                </label>
                <div className={`flex items-center gap-2 ${enableRationale ? '' : 'opacity-40 pointer-events-none'}`}>
                  <span className="text-[10px] text-[#555]">Variant:</span>
                  <label className="flex items-center gap-1 cursor-pointer select-none" data-testid="test-runner-rationale-variant-a">
                    <input type="radio" name="rationale-variant" value="A" checked={rationaleVariant === 'A'}
                      onChange={e => setRationaleVariant(e.target.value)} className="accent-[#FF6B00] w-3 h-3" />
                    <span className="text-[10px] text-[#888]">A <span className="text-[#444]">(distractor only)</span></span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer select-none" data-testid="test-runner-rationale-variant-b">
                    <input type="radio" name="rationale-variant" value="B" checked={rationaleVariant === 'B'}
                      onChange={e => setRationaleVariant(e.target.value)} className="accent-[#FF6B00] w-3 h-3" />
                    <span className="text-[10px] text-[#888]">B <span className="text-[#444]">(distractor + misconception)</span></span>
                  </label>
                </div>
              </div>

              {/* Prompt overrides */}
              {[['Phase 0 System Prompt', showPromptP0, setShowPromptP0, promptP0, setPromptP0, 'test-runner-prompt-p0'],
                ['Phase 0 User Prompt', showPromptP0User, setShowPromptP0User, promptP0User, setPromptP0User, 'test-runner-prompt-p0-user'],
                ['Call A System Prompt', showPromptA, setShowPromptA, promptA, setPromptA, 'test-runner-prompt-a'],
                ['Call A User Prompt', showPromptAUser, setShowPromptAUser, promptAUser, setPromptAUser, 'test-runner-prompt-a-user'],
                ['Call B System Prompt', showPromptB, setShowPromptB, promptB, setPromptB, 'test-runner-prompt-b'],
                ['Call B User Prompt', showPromptBUser, setShowPromptBUser, promptBUser, setPromptBUser, 'test-runner-prompt-b-user'],
                ['Call C System Prompt', showPromptC, setShowPromptC, promptC, setPromptC, 'test-runner-prompt-c'],
                ['Call C User Prompt', showPromptCUser, setShowPromptCUser, promptCUser, setPromptCUser, 'test-runner-prompt-c-user'],
                ['EQ System Prompt', showPromptEqSystem, setShowPromptEqSystem, promptEqSystem, setPromptEqSystem, 'test-runner-prompt-eq-system'],
                ['EQ User Prompt', showPromptEqUser, setShowPromptEqUser, promptEqUser, setPromptEqUser, 'test-runner-prompt-eq-user'],
                ['Rationale System Prompt', showPromptRationale, setShowPromptRationale, promptRationale, setPromptRationale, 'test-runner-prompt-rationale']].map(([lbl, show, setShow, val, setVal, tid]) => (
                <div key={tid}>
                  <button onClick={() => setShow(!show)} className="flex items-center gap-1 text-[10px] text-[#444] hover:text-[#888] transition-colors">
                    <ChevronDown className={`w-3 h-3 transition-transform ${show ? 'rotate-180' : ''}`} />
                    {show ? `Custom ${lbl}` : `Using production prompt (${lbl})`}
                  </button>
                  {show && (
                    <textarea value={val} onChange={e => setVal(e.target.value)} rows={4} data-testid={tid}
                      className="w-full mt-1 bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg text-[10px] text-white p-2 font-mono resize-y focus:border-[#FF6B00] outline-none"
                      placeholder={
                        tid === 'test-runner-prompt-a-user'
                          ? "Must contain {text_content} placeholder where document text will be injected. {visual_section} is optional. See build_questions_prompt() for default."
                          : tid === 'test-runner-prompt-p0-user'
                          ? "Must contain {text_content} placeholder where document text will be injected. {visual_section} is optional. See _build_topic_detection_prompt() for default."
                          : tid === 'test-runner-prompt-b-user'
                          ? "Must contain {text_content} placeholder where document text will be injected. {visual_section} is optional. See build_flashcards_prompt() for default."
                          : tid === 'test-runner-prompt-c-user'
                          ? "Must contain {batch_json} placeholder where the question batch JSON will be injected. See _make_distractor_call() for default."
                          : tid === 'test-runner-prompt-rationale'
                          ? "Override the rationale-generation system prompt. The user message (language + JSON batch of questions) is built separately. See PROMPT_RATIONALE_VARIANT_A_SYSTEM / VARIANT_B_SYSTEM for defaults. Leave empty to use the variant selected above."
                          : "Paste custom system prompt here. Document text and other dynamic data are injected normally."
                      } />
                  )}
                </div>
              ))}

              <div className="flex items-center gap-3">
                <Button onClick={executeRun} disabled={running || !docId || !label.trim()}
                  className="bg-[#FF6B00] hover:bg-[#FF6B00]/80 text-white rounded-xl text-xs" data-testid="test-runner-start">
                  {running ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Running ({elapsed}s)</> : <><Play className="w-3.5 h-3.5 mr-1" /> Start Run</>}
                </Button>
              </div>

              {/* Result summary */}
              {lastResult && (
                <div className={`rounded-xl p-4 ${lastResult.status === 'completed' ? 'bg-green-500/5 border border-green-500/20' : 'bg-red-500/5 border border-red-500/20'}`} data-testid="test-runner-result">
                  {lastResult.status === 'failed' ? (
                    <p className="text-red-400 text-xs">{lastResult.error}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-green-400"><Activity className="w-3.5 h-3.5" /> Run completed</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-[10px]">
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Questions</span><span className="text-white font-mono">{t.questions_final}</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Drops</span><span className="text-white font-mono">{t.drops_total} ({(t.drop_rate * 100).toFixed(0)}%)</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Flashcards</span><span className="text-white font-mono">{t.flashcards_final}</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Topics</span><span className="text-white font-mono">{t.topics_covered}</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Bias Pre→Post</span><span className={biasColor(t.bias_post_eq)} style={{fontFamily:'monospace'}}>{(t.bias_pre_eq*100).toFixed(0)}%→{(t.bias_post_eq*100).toFixed(0)}%</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Severe Pre→Post</span><span className={biasColor(t.severe_post_eq)} style={{fontFamily:'monospace'}}>{(t.severe_pre_eq*100).toFixed(0)}%→{(t.severe_post_eq*100).toFixed(0)}%</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Bloom</span><span className="text-white font-mono text-[9px]">{[1,2,3,4,5,6].map(l => `${l}:${bloom[l]||0}`).join(' ')}</span></div>
                        <div className="bg-[#0a0a0a] rounded-lg p-2"><span className="text-[#555] block">Time</span><span className="text-white font-mono">{t.time_wall_clock?.toFixed(0)}s</span></div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button variant="ghost" size="sm" onClick={() => { setTab('history'); }} className="text-[#555] hover:text-white text-[10px]">View in History</Button>
                        <Button variant="ghost" size="sm" onClick={executeRun} disabled={running} className="text-[#555] hover:text-white text-[10px]">Run Again</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── History ── */}
          {tab === 'history' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#555]">{runs.length} runs</span>
                <Button variant="ghost" size="sm" onClick={fetchRuns} className="text-[#555] hover:text-white text-[10px]"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
              </div>
              {runs.length === 0 ? <p className="text-xs text-[#333]">No runs yet</p> : (
                <div className="overflow-x-auto">
                  <div className="max-h-[220px] overflow-y-auto" data-testid="test-runner-history-scroll">
                  <table className="w-full text-[10px]" data-testid="test-runner-history-table">
                    <thead className="sticky top-0 bg-[#111] z-10"><tr className="text-[#555] border-b border-[#1a1a1a]">
                      <th className="p-1 text-left"></th>
                      <th className="p-1 text-left">Label</th><th className="p-1 text-left">Document</th>
                      <th className="p-1 text-left">Models</th><th className="p-1">Temp</th>
                      <th className="p-1">Call C</th>
                      <th className="p-1">Strict</th>
                      <th className="p-1">Qs</th><th className="p-1">Drops</th><th className="p-1">Rate</th>
                      <th className="p-1">FCs</th><th className="p-1">Topics</th>
                      <th className="p-1">Bias</th><th className="p-1">Severe</th>
                      <th className="p-1">Cost</th><th className="p-1">Time</th>
                      <th className="p-1">Bloom</th><th className="p-1">Actions</th>
                    </tr></thead>
                    <tbody>
                      {runs.map(r => {
                        const s = r.stats?.totals || {};
                        const c = r.config || {};
                        const bl = s.bloom_distribution || {};
                        const allSame = c.call_a_model === c.call_c_model && c.call_c_model === c.call_b_model;
                        return (
                          <tr key={r.run_id} className="border-b border-[#0a0a0a] hover:bg-[#0a0a0a]/50 text-white">
                            <td className="p-1"><input type="checkbox" checked={selectedRuns.includes(r.run_id)}
                              onChange={() => toggleSelect(r.run_id)} className="accent-[#FF6B00]" /></td>
                            <td className="p-1 font-mono text-[#FF6B00]">{r.label}</td>
                            <td className="p-1 text-[#888] max-w-[100px] truncate">{r.document_name}</td>
                            <td className="p-1 font-mono text-[#666] max-w-[120px] truncate">{allSame ? `All: ${c.call_a_model}` : `A:${c.call_a_model} C:${c.call_c_model} B:${c.call_b_model}`}</td>
                            <td className="p-1 text-center text-[#666]">{c.temperature ?? '-'}</td>
                            <td className="p-1 text-center">{c.include_call_c === false ? <span className="text-yellow-500">Skip</span> : <span className="text-green-500">Yes</span>}</td>
                            <td className="p-1 text-center">{c.strict_topic_labeling ? <span className="text-blue-400">Yes</span> : <span className="text-[#555]">No</span>}</td>
                            <td className="p-1 text-center">{s.questions_final ?? '-'}</td>
                            <td className="p-1 text-center">{s.drops_total ?? '-'}</td>
                            <td className="p-1 text-center">{s.drop_rate != null ? `${(s.drop_rate*100).toFixed(0)}%` : '-'}</td>
                            <td className="p-1 text-center">{s.flashcards_final ?? '-'}</td>
                            <td className="p-1 text-center">{s.topics_covered ?? '-'}</td>
                            <td className="p-1 text-center"><span className={biasColor(s.bias_post_eq*100)}>{s.bias_pre_eq != null ? `${(s.bias_pre_eq*100).toFixed(0)}→${(s.bias_post_eq*100).toFixed(0)}%` : '-'}</span></td>
                            <td className="p-1 text-center"><span className={biasColor(s.severe_post_eq*100)}>{s.severe_pre_eq != null ? `${(s.severe_pre_eq*100).toFixed(0)}→${(s.severe_post_eq*100).toFixed(0)}%` : '-'}</span></td>
                            <td className="p-1 text-center text-[#666]">${s.cost_total?.toFixed(3) ?? '-'}</td>
                            <td className="p-1 text-center text-[#666]">{s.time_wall_clock?.toFixed(0) ?? '-'}s</td>
                            <td className="p-1 font-mono text-[9px] text-[#555]">{[1,2,3,4,5,6].map(l => bl[l]||0).join(',')}</td>
                            <td className="p-1">
                              <div className="flex gap-1">
                                <button onClick={() => viewDetail(r.run_id)} title="Detail" data-testid={`detail-${r.run_id}`} className="text-[#555] hover:text-white"><BarChart3 className="w-3 h-3" /></button>
                                <a href={`${api.defaults.baseURL}/admin/test-runner/run/${r.run_id}/download/questions`} title="Questions" className="text-[#555] hover:text-white"><Download className="w-3 h-3" /></a>
                                <a href={`${api.defaults.baseURL}/admin/test-runner/run/${r.run_id}/download/flashcards`} title="Flashcards" className="text-[#555] hover:text-[#FF6B00]"><BookOpen className="w-3 h-3" /></a>
                                <button onClick={() => deleteRun(r.run_id)} title="Delete" data-testid={`delete-${r.run_id}`} className="text-[#555] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {/* Detail modal */}
              {detailRun && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDetailRun(null)}>
                  <div className="bg-[#111] border border-[#1a1a1a] rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm text-white font-semibold">{detailRun.label}</h3>
                      <div className="flex items-center gap-2">
                        <button onClick={() => {
                          const payload = { config: detailRun.config, stats: detailRun.stats };
                          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          const safe = (detailRun.label || 'run').replace(/ /g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
                          a.href = url; a.download = `DetailView_${safe}.json`; a.click(); URL.revokeObjectURL(url);
                        }} className="text-[10px] text-[#FF6B00] hover:text-white border border-[#FF6B00]/30 rounded px-2 py-0.5" data-testid="detail-download-btn">
                          <Download className="w-3 h-3 inline mr-1" />Download
                        </button>
                        <button onClick={() => setDetailRun(null)} className="text-[#555] hover:text-white"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="space-y-3 text-[10px]">
                      {[['Phase 0', detailRun.stats?.phase0], ['Call A', detailRun.stats?.call_a],
                        ['Topic Filter', detailRun.stats?.topic_filter],
                        ['Pre-Call C Filter', detailRun.stats?.pre_call_c_filter],
                        ['Call C', detailRun.stats?.call_c],
                        ['Call C Assembly', detailRun.stats?.call_c_assembly],
                        ['Call B', detailRun.stats?.call_b], ['Equalization', detailRun.stats?.equalization]].map(([lbl, data]) => (
                        <div key={lbl} className="bg-[#0a0a0a] rounded-lg p-3">
                          <span className="text-[#FF6B00] font-semibold block mb-1">{lbl}</span>
                          {data === null ? <span className="text-yellow-500 italic">Skipped</span> :
                           lbl === 'Topic Filter' && data ? (
                            <div>
                              <p className="text-[#888] mb-1">Entering: {data.questions_entering} | Drops: {data.drops_count} | Passing: {data.questions_passing}</p>
                              {data.drop_details?.length > 0 && (
                                <table className="w-full mt-1 text-[9px]">
                                  <thead><tr className="text-[#555]"><th className="text-left p-0.5">Question Topic</th><th className="text-left p-0.5">Best Match</th><th className="text-left p-0.5">Score</th></tr></thead>
                                  <tbody>
                                    {data.drop_details.map((dd, di) => (
                                      <tr key={di} className="border-t border-[#111]">
                                        <td className="p-0.5 text-white">{dd.question_topic}</td>
                                        <td className="p-0.5 text-[#888]">{dd.best_match_topic}</td>
                                        <td className={`p-0.5 ${dd.best_match_score < 0.5 ? 'text-red-400' : dd.best_match_score < 0.7 ? 'text-orange-400' : 'text-[#666]'}`}>{dd.best_match_score?.toFixed(3)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {(!data.drop_details || data.drop_details.length === 0) && <p className="text-[#555] italic">No drops</p>}
                            </div>
                           ) :
                           lbl === 'Call C' && data ? (
                            <div className="space-y-1">
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                <span>Batches: <span className="text-white">{data.batches_count ?? '-'}</span></span>
                                <span>Tokens: <span className="text-white">{data.tokens_in_total ?? 0}</span> in / <span className="text-white">{data.tokens_out_total ?? 0}</span> out</span>
                                <span>Cost: <span className="text-white">${(data.cost_total ?? 0).toFixed(4)}</span></span>
                                <span>Time: <span className="text-white">{data.time_s ?? 0}s</span></span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                <span>Drops Post-C: <span className="text-white">{data.drops_post_call_c ?? 0}</span></span>
                                <span>Drops No Options: <span className="text-white">{data.drops_no_options ?? 0}</span></span>
                                <span>Avg Option Words: <span className="text-white">{data.avg_option_length_words ?? 0}</span></span>
                                <span>Fallback Recovered: <span className={(data.fallback_recovered ?? 0) > 0 ? 'text-green-400 font-semibold' : 'text-white'}>{data.fallback_recovered ?? 0}</span></span>
                              </div>
                              {data.group_id_mismatch_debug && (
                                <div className="border-t border-[#1a1a1a] pt-1 mt-1 text-xs">
                                  <p className="text-red-400 font-semibold mb-0.5">Group ID Mismatch (batch {data.group_id_mismatch_debug.batch_index})</p>
                                  <div className="flex flex-col gap-0.5 text-[#888]">
                                    <span>Expected: <span className="text-white font-mono">[{data.group_id_mismatch_debug.expected_ids?.join(', ')}]</span></span>
                                    <span>Received: <span className="text-yellow-400 font-mono">[{data.group_id_mismatch_debug.received_ids?.join(', ')}]</span></span>
                                    <span>Raw Length: <span className="text-white">{data.group_id_mismatch_debug.raw_response_length?.toLocaleString()}</span> chars | Groups in Response: <span className="text-white">{data.group_id_mismatch_debug.raw_response_group_count}</span></span>
                                  </div>
                                  {data.group_id_mismatch_debug.raw_response_preview && (
                                    <pre className="mt-1 p-1.5 bg-[#0a0a0a] border border-[#222] rounded text-[9px] text-[#777] overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap break-all">{data.group_id_mismatch_debug.raw_response_preview}</pre>
                                  )}
                                </div>
                              )}
                            </div>
                           ) :
                           lbl === 'Call C Assembly' && data ? (
                            <div>
                              <p className="text-[#888] mb-1">Entering: {data.questions_entering} | Drops: {data.drops_count} | Passing: {data.questions_passing}</p>
                              {data.drop_details?.length > 0 && (
                                <table className="w-full mt-1 text-[9px]">
                                  <thead><tr className="text-[#555]"><th className="text-left p-0.5">Question</th><th className="text-left p-0.5">Original Correct</th><th className="text-left p-0.5">Score</th></tr></thead>
                                  <tbody>
                                    {data.drop_details.map((dd, di) => (
                                      <tr key={di} className="border-t border-[#111]">
                                        <td className="p-0.5 text-white max-w-[200px] truncate">{dd.question_text}</td>
                                        <td className="p-0.5 text-[#888] max-w-[200px] truncate">{dd.original_correct}</td>
                                        <td className={`p-0.5 ${dd.match_score == null || dd.match_score < 0.3 ? 'text-red-400' : dd.match_score < 0.6 ? 'text-orange-400' : 'text-[#666]'}`}>{dd.match_score != null ? dd.match_score.toFixed(3) : 'null'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {(!data.drop_details || data.drop_details.length === 0) && <p className="text-[#555] italic">No drops</p>}
                            </div>
                           ) :
                           lbl === 'Equalization' && data ? (
                            <div>
                              {data.skipped ? <p className="text-yellow-500 italic">Skipped: {data.reason}</p> : (
                                <div className="space-y-1">
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                    <span>Bias: <span className="text-white">{data.pre_bias != null ? `${(data.pre_bias*100).toFixed(1)}%` : data.bias_before != null ? `${(data.bias_before*100).toFixed(1)}%` : '-'}</span> → <span className="text-green-400">{data.post_bias != null ? `${(data.post_bias*100).toFixed(1)}%` : data.bias_after != null ? `${(data.bias_after*100).toFixed(1)}%` : '-'}</span></span>
                                    <span>Severe: <span className="text-white">{data.severe_before != null ? `${(data.severe_before*100).toFixed(1)}%` : '-'}</span> → <span className="text-green-400">{data.severe_after != null ? `${(data.severe_after*100).toFixed(1)}%` : '-'}</span></span>
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                    <span>Equalized: <span className="text-white">{data.equalized_count ?? data.questions_equalized ?? '-'}</span></span>
                                    <span>Selected: <span className="text-white">{data.n_selected ?? '-'}</span></span>
                                    {data.forced_outliers != null && <span>Forced Outliers: <span className="text-white">{data.forced_outliers}</span></span>}
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888] border-t border-[#1a1a1a] pt-1 mt-1">
                                    <span>Edits Accepted: <span className="text-green-400 font-semibold">{data.edits_accepted ?? '-'}</span></span>
                                    <span>Edits Rejected: <span className="text-red-400 font-semibold">{data.edits_rejected ?? '-'}</span></span>
                                    <span>Avg Char Reduction: <span className="text-white font-semibold">{data.avg_char_reduction_pct != null ? `${data.avg_char_reduction_pct}%` : '-'}</span></span>
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                    <span>Tokens: <span className="text-white">{data.eq_tokens_in ?? 0}</span> in / <span className="text-white">{data.eq_tokens_out ?? 0}</span> out</span>
                                    {data.eq_time_s != null && <span>Time: <span className="text-white">{data.eq_time_s}s</span></span>}
                                    {(data.tokens_in != null && !data.eq_tokens_in) && <span>Tokens: <span className="text-white">{data.tokens_in}</span> in / <span className="text-white">{data.tokens_out}</span> out</span>}
                                    {data.cost != null && <span>Cost: <span className="text-white">${data.cost.toFixed(4)}</span></span>}
                                  </div>
                                </div>
                              )}
                            </div>
                           ) :
                           lbl === 'Call A' && data ? (
                            <div className="space-y-1">
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                <span>Groups: <span className="text-white">{data.groups_count ?? '-'}</span></span>
                                <span>Questions Raw: <span className="text-white">{data.questions_raw ?? 0}</span></span>
                                <span>Per Group: <span className="text-white font-mono">[{data.questions_per_group?.join(', ')}]</span></span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                <span>Tokens: <span className="text-white">{data.tokens_in_total ?? 0}</span> in / <span className="text-white">{data.tokens_out_total ?? 0}</span> out</span>
                                <span>Cost: <span className="text-white">${(data.cost_total ?? 0).toFixed(4)}</span></span>
                                <span>Time: <span className="text-white">{data.time_s ?? 0}s</span></span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#888]">
                                <span>Coverage Retries: <span className={(data.coverage_retries ?? 0) > 0 ? 'text-yellow-400 font-semibold' : 'text-white'}>{data.coverage_retries ?? 0}</span></span>
                                <span>Retry Qs Added: <span className={(data.coverage_retry_questions_added ?? 0) > 0 ? 'text-green-400 font-semibold' : 'text-white'}>{data.coverage_retry_questions_added ?? 0}</span></span>
                              </div>
                            </div>
                           ) :
                           <pre className="text-[#888] whitespace-pre-wrap font-mono">{JSON.stringify(data || {}, null, 2)}</pre>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {selectedRuns.length >= 2 && (
                <Button onClick={() => setTab('compare')} className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs mt-2" data-testid="test-runner-compare-btn">
                  <GitCompare className="w-3.5 h-3.5 mr-1" /> Compare {selectedRuns.length} Runs
                </Button>
              )}
            </div>
          )}

          {/* ── Compare ── */}
          {tab === 'compare' && (
            <div className="space-y-3">
              {compareRuns.length < 2 ? (
                <p className="text-xs text-[#555]">Select 2-6 runs from History tab to compare.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]" data-testid="test-runner-compare-table">
                    <thead><tr className="text-[#555] border-b border-[#1a1a1a]">
                      <th className="p-1 text-left">Metric</th>
                      {compareRuns.map(r => <th key={r.run_id} className="p-1 text-center text-[#FF6B00] font-mono">{r.label}</th>)}
                    </tr></thead>
                    <tbody>
                      {(() => {
                        const metrics = [
                          ['Config', null],
                          ['Model A', r => r.config?.call_a_model],
                          ['Model C', r => r.config?.call_c_model],
                          ['Model B', r => r.config?.call_b_model],
                          ['Temperature', r => r.config?.temperature ?? '-'],
                          ['Include Call C', r => r.config?.include_call_c === false ? 'Skipped' : 'Yes'],
                          ['Strict Topic Labeling', r => r.config?.strict_topic_labeling ? 'Yes' : 'No'],
                          ['Prompt Override', r => (r.config?.prompt_override_call_a ? 'A' : '') + (r.config?.prompt_override_call_c ? 'C' : '') || 'No'],
                          ['EQ Prompt Override', r => (r.config?.prompt_override_eq_system || r.config?.prompt_override_eq_user) ? 'Yes' : 'No'],
                          ['Totals', null],
                          ['Questions Final', r => r.stats?.totals?.questions_final, 'high'],
                          ['Drops Total', r => r.stats?.totals?.drops_total, 'low'],
                          ['Drop Rate', r => r.stats?.totals?.drop_rate != null ? `${(r.stats.totals.drop_rate*100).toFixed(1)}%` : '-', 'low_pct'],
                          ['Flashcards', r => r.stats?.totals?.flashcards_final, 'high'],
                          ['Topics Covered', r => r.stats?.totals?.topics_covered, 'high'],
                          ['Topics Missed', r => r.stats?.totals?.topics_missed?.join(', ') || 'None'],
                          ['Cost Total', r => `$${r.stats?.totals?.cost_total?.toFixed(4) ?? '-'}`, 'low_cost'],
                          ['Cost/Question', r => `$${r.stats?.totals?.cost_per_question?.toFixed(4) ?? '-'}`, 'low_cost'],
                          ['Time (s)', r => r.stats?.totals?.time_wall_clock?.toFixed(0) ?? '-', 'low'],
                          ['Quality', null],
                          ['Bias Pre-EQ', r => r.stats?.totals?.bias_pre_eq != null ? `${(r.stats.totals.bias_pre_eq*100).toFixed(1)}%` : '-'],
                          ['Bias Post-EQ', r => r.stats?.totals?.bias_post_eq != null ? `${(r.stats.totals.bias_post_eq*100).toFixed(1)}%` : '-', 'low_pct'],
                          ['Severe Pre-EQ', r => r.stats?.totals?.severe_pre_eq != null ? `${(r.stats.totals.severe_pre_eq*100).toFixed(1)}%` : '-'],
                          ['Severe Post-EQ', r => r.stats?.totals?.severe_post_eq != null ? `${(r.stats.totals.severe_post_eq*100).toFixed(1)}%` : '-', 'low_pct'],
                          ['Equalization Edits', null],
                          ['Edits Accepted', r => r.stats?.equalization?.edits_accepted ?? '-', 'high'],
                          ['Edits Rejected', r => r.stats?.equalization?.edits_rejected ?? '-', 'low'],
                          ['Avg Char Reduction %', r => r.stats?.equalization?.avg_char_reduction_pct != null ? `${r.stats.equalization.avg_char_reduction_pct}%` : '-'],
                          ['Call C', null],
                          ['Call C Tokens In', r => r.stats?.call_c === null ? 'Skipped' : r.stats?.call_c?.tokens_in_total ?? '-'],
                          ['Call C Cost', r => r.stats?.call_c === null ? 'Skipped' : `$${r.stats?.call_c?.cost_total?.toFixed(4) ?? '-'}`],
                          ['Topic Filter', null],
                          ['Topic Filter Drops', r => r.stats?.topic_filter?.drops_count ?? '-', 'low'],
                          ['Topic Filter Pass', r => r.stats?.topic_filter?.questions_passing ?? '-', 'high'],
                          ['Bloom', null],
                          ...([1,2,3,4,5,6].map(l => [`Level ${l}`, r => r.stats?.totals?.bloom_distribution?.[l] ?? 0])),
                        ];
                        return metrics.map(([name, getter, best], idx) => {
                          if (!getter) return <tr key={idx}><td colSpan={compareRuns.length+1} className="p-1 pt-3 text-[#FF6B00] font-semibold border-b border-[#1a1a1a]">{name}</td></tr>;
                          const vals = compareRuns.map(getter);
                          const numVals = vals.map(v => typeof v === 'number' ? v : parseFloat(String(v).replace(/[$%,]/g, ''))).filter(n => !isNaN(n));
                          const bestVal = best?.startsWith('low') ? Math.min(...numVals) : best === 'high' ? Math.max(...numVals) : null;
                          const worstVal = best?.startsWith('low') ? Math.max(...numVals) : best === 'high' ? Math.min(...numVals) : null;
                          return (
                            <tr key={idx} className="border-b border-[#0a0a0a]">
                              <td className="p-1 text-[#888]">{name}</td>
                              {vals.map((v, i) => {
                                const num = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$%,]/g, ''));
                                const isBest = bestVal !== null && !isNaN(num) && num === bestVal && numVals.length > 1;
                                const isWorst = worstVal !== null && !isNaN(num) && num === worstVal && numVals.length > 1;
                                return <td key={i} className={`p-1 text-center font-mono ${isBest ? 'text-green-400' : isWorst ? 'text-red-400' : 'text-white'}`}>{String(v)}</td>;
                              })}
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Main AdminDashboard ──
export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/sb-ctrl/analytics');
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Acceso denegado.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!ADMIN_EMAIL || user?.email?.toLowerCase() !== ADMIN_EMAIL) {
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [user, navigate, fetchAnalytics]);

  const su = data?.service_usage || {};
  const mongo = su.mongodb || {};
  const totals = data?.totals || {};
  const ug = data?.user_growth || {};
  const eng = data?.engagement || {};
  const docStatus = eng.doc_status || {};

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}
            className="text-[#6a6a6a] hover:text-white" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="admin-dashboard-title">
              <Activity className="w-6 h-6 text-[#FF6B00]" /> Admin Dashboard
            </h1>
            <p className="text-[#6a6a6a] text-sm">User management & platform health</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/errors')}
            className="border-[#262626] text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] rounded-xl" data-testid="admin-errors-link">
            <AlertTriangle className="w-4 h-4 mr-2" /> Registro de errores
          </Button>
          <Button variant="outline" onClick={() => { setLoading(true); fetchAnalytics(); }}
            className="border-[#262626] text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] rounded-xl" data-testid="admin-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* User Management — always visible */}
      <UserManagement />

      {/* Analytics toggle */}
      <button onClick={() => setShowAnalytics(!showAnalytics)}
        className="flex items-center gap-2 text-sm font-medium text-[#e5e5e5] hover:text-white transition-colors" data-testid="admin-toggle-analytics">
        <ChevronDown className={`w-4 h-4 transition-transform ${showAnalytics ? 'rotate-180' : ''}`} />
        {showAnalytics ? 'Hide' : 'Show'} Platform Analytics
      </button>

      {showAnalytics && !loading && data && (
        <>
          {/* Top-level Totals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* post-t7-1 (F2 y F3, #580): los invitados con y sin actividad, y los documentos del alumno y del profe aparte */}
            <TarjetasDeCuentasArriba totals={totals} ug={ug} eng={eng} docStatus={docStatus} />
            <MetricCard icon={BookOpen} label="Study Sessions" value={totals.sessions || 0}
              sub={`${totals.questions || 0} questions | ${eng.sessions_this_week || 0} this week, ${eng.active_studiers || 0} active studiers`} color="text-violet-400" />
            <MetricCard icon={DollarSign} label="Monthly Revenue" value={`$${ug.mrr || '0.00'}`}
              sub={`est. | ${ug.total_pro || 0} Pro`} color="text-amber-400" />
          </div>

          {/* User Growth */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FF6B00]" /> User Growth
            </h2>
            <div className={REJILLA_DE_CRECIMIENTO}>
              <TarjetasDeCrecimiento ug={ug} />
            </div>
          </div>

          {/* Service Usage */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF6B00]" /> Service Usage
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <div className="glass-card rounded-[16px] p-5 space-y-4" data-testid="service-mongodb">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-medium text-white">MongoDB Atlas</span>
                  {mongo.warning && <span className="text-[10px] bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">WARNING</span>}
                </div>
                <UsageBar used={mongo.storage_used_mb || 0} limit={mongo.current_limit_mb || 512}
                  label="Storage (MB)" warning={mongo.warning} actionNeeded={mongo.action_needed} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* User Analytics */}
      <UserAnalytics />

      {/* Sales — purchase funnel & revenue */}
      <SalesSection />

      {/* TANDA 25 (#232) — quién paga por Stripe y quién por Apple */}
      <PagosPorProveedorSection />

      {/* Engine Sync Check — Sprint F2 shadow verifier (event log vs stored state) */}
      <SyncAnomaliesSection />

      {/* #158c — post-mortem crudo de las llamadas caidas (Call B / Call C) */}
      <CallFailuresSection />

      {/* Model Pricing — admin management */}
      <ModelPricingSection />

      {/* PIPE-INST T4 — costos del carril institucional (solo-admin) */}
      <CostosInstitucionalesSection />

      {/* Production Model Configuration */}
      <ProductionModelConfig />

      {/* Topic Split Configuration */}
      <TopicSplitConfig />

      {/* Token Budget Config */}
      <TokenFormulaConfig />

      {/* Equalization Toggle */}
      {/* Legacy equalization removed — Targeted equalization controlled via Length Bias Config */}

      {/* Length Bias Control */}
      <BiasConfig />

      {/* #418 — Opciones de respuesta: elegir, no editar */}
      <ElegirModoConfig />

      {/* #425 — Caché de opciones */}
      <CallcCacheConfig />

      {/* #426 — Respuesta natural */}
      <RespuestaNaturalConfig />
      <EstilosExamenConfig />
      <RefrescarDemoAlumno />

      {/* #438 — Blog */}
      <BlogConfig />

      {/* t7-2b (#518): el chat del panel del profe */}
      <ChatPanelConfig />

      {/* tutor-1 (A7, Anexo D): el Tutor del alumno, debajo del chat del profe */}
      <TutorConfig />

      {/* Test Runner */}
      <TestRunner />

      {/* t7-2c (#532): el correo del domingo de prueba, solo al profe dueño */}
      <ReporteSemanalPrueba />

      {/* t7-3 remate 8 (#592): el grupo demo y su botón "Refrescar demo" */}
      <GrupoDemo />
    </div>
  );
}

/* tutor-2 (F.2): "Tutor (30 dias)" dentro de User Analytics (panel solo de Carlos: textos en espanol, sin par en
   ingles). Una fila por pantalla y aparato con mensajes y alumnos distintos, y debajo el total, los topes (y cuantos
   tocaron el boton) y las fallas (y cuantas fueron "ocupado"). Lo lee de `GET /sb-ctrl/tutor/pantallas?days=30`. */
const ETIQUETAS_DEL_TUTOR = {
  'tutor|computadora': 'Tutor (computadora)', 'tutor|telefono': 'Tutor (teléfono)', 'tutor|tablet': 'Tutor (tablet)',
  'tutor|app': 'Tutor (app)', 'panel|': 'Panel (Preguntar al tutor)', 'analiticas|': 'Analíticas',
  'adaptive|': 'Adaptive', 'flashcards|': 'Flashcards', 'examen_resultados|': 'Resultados del examen',
  'sin_dato|': 'Sin dato (parte 1)', 'tutor|': 'Tutor (sin aparato)',
};

export function etiquetaDelTutor(fila) {
  const llave = `${fila.pantalla}|${fila.aparato || ''}`;
  return ETIQUETAS_DEL_TUTOR[llave] || llave;
}

export function CuadroDelTutor({ datos }) {
  const filas = (datos && datos.filas) || [];
  const total = (datos && datos.total) || { mensajes: 0, alumnos: 0 };
  const fallas = (datos && datos.fallas) || {};
  const todasLasFallas = Object.values(fallas).reduce((a, n) => a + (Number(n) || 0), 0);
  return (
    <div className="glass-card rounded-[20px] p-6" data-testid="analytics-tutor">
      <h3 className="text-base font-semibold text-white mb-4">Tutor (30 días)</h3>
      {filas.length === 0 ? (
        <p className="text-sm text-[#a0a0a0]" data-testid="analytics-tutor-vacio">Nadie ha usado el tutor en 30 días.</p>
      ) : (
        <>
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="text-[#8A8A8A] border-b border-[#1a1a1a]">
                <th className="text-left py-2 pr-4">Pantalla</th>
                <th className="text-right py-2 px-2">Mensajes</th>
                <th className="text-right py-2 pl-2">Alumnos</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={`${f.pantalla}|${f.aparato || ''}`} className="border-b border-[#111]" data-testid="analytics-tutor-fila">
                  <td className="py-2 pr-4 text-[#ccc]">{etiquetaDelTutor(f)}</td>
                  <td className="py-2 px-2 text-right text-white font-mono">{f.mensajes}</td>
                  <td className="py-2 pl-2 text-right text-white font-mono">{f.alumnos}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#a0a0a0]" data-testid="analytics-tutor-resumen">
            <span>Total: {total.mensajes} mensajes de {total.alumnos} alumnos</span>
            <span>Llegaron al tope: {datos.topes || 0} (tocaron el botón: {datos.clics_al_tope || 0})</span>
            <span>Fallas: {todasLasFallas} (ocupado: {fallas.tutor_busy || 0})</span>
            {/* tutor-3 (C.8, P13): los pulgares de las respuestas, contados en el servidor. Conteos, jamas porcentajes. */}
            <span data-testid="analytics-tutor-pulgares">
              Pulgares: buenas {(datos.pulgares && datos.pulgares.buenas) || 0}, malas {(datos.pulgares && datos.pulgares.malas) || 0}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// borrar-rotas (#764): las preguntas y tarjetas BORRADAS por una formula sin arreglo (las que borra la tuberia al
// nacer y las que borra el guion en lo publicado). Lo lee de `GET /sb-ctrl/borradas`. (marca-3, #751, contaba las
// ocultas.)
export function CuadroBorradas({ datos }) {
  return (
    <div className="glass-card rounded-[20px] p-6" data-testid="analytics-borradas">
      <h3 className="text-base font-semibold text-white mb-4">Preguntas y tarjetas borradas por fórmula</h3>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#a0a0a0]" data-testid="analytics-borradas-resumen">
        <span>Preguntas borradas: {datos.preguntas || 0}</span>
        <span>Tarjetas borradas: {datos.tarjetas || 0}</span>
        <span>Documentos: {datos.documentos || 0}</span>
      </div>
    </div>
  );
}

/* marca-2 (firma #740): "Clientes vistos (30 dias)" dentro de User Analytics (panel solo de Carlos: textos en
   espanol, sin par en ingles). Un renglon por dominio, web o app y build, con usuarios distintos y dias-usuario
   (una fila de `clientes_vistos` es un usuario en un dia). Con esto se decide cuando apagar la API de
   studybuddy.vc: cuando ya no aparezca ninguna app por ese dominio. Lo lee de `GET /sb-ctrl/clientes-vistos`. */
export function CuadroClientesVistos({ datos }) {
  const renglones = (datos && datos.renglones) || [];
  return (
    <div className="glass-card rounded-[20px] p-6" data-testid="analytics-clientes-vistos">
      <h3 className="text-base font-semibold text-white mb-4">Clientes vistos (30 días)</h3>
      {renglones.length === 0 ? (
        <p className="text-sm text-[#a0a0a0]" data-testid="analytics-clientes-vistos-vacio">Todavía no hay clientes anotados.</p>
      ) : (
        <>
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="text-[#8A8A8A] border-b border-[#1a1a1a]">
                <th className="text-left py-2 pr-4">Dominio</th>
                <th className="text-left py-2 pr-4">Cliente</th>
                <th className="text-left py-2 pr-4">Build</th>
                <th className="text-right py-2 px-2">Usuarios</th>
                <th className="text-right py-2 pl-2">Días</th>
              </tr>
            </thead>
            <tbody>
              {renglones.map((r) => (
                <tr key={`${r.host}|${r.cliente}|${r.build}`} className="border-b border-[#111]" data-testid="analytics-clientes-vistos-fila">
                  <td className="py-2 pr-4 text-[#ccc]">{r.host || 'sin dato'}</td>
                  <td className="py-2 pr-4 text-[#ccc]">{r.cliente === 'app' ? 'App' : 'Web'}</td>
                  <td className="py-2 pr-4 text-[#ccc]">{r.build || 'sin dato'}</td>
                  <td className="py-2 px-2 text-right text-white font-mono">{r.usuarios}</td>
                  <td className="py-2 pl-2 text-right text-white font-mono">{r.dias}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#a0a0a0]" data-testid="analytics-clientes-vistos-resumen">
            <span>Total: {datos.usuarios || 0} usuarios, {datos.dias || 0} días-usuario (desde {datos.desde})</span>
          </div>
        </>
      )}
    </div>
  );
}

// tutor-2 (F.2): exportada para su prueba (el ancla de las pruebas viejas es su declaracion sin `export`).
export { UserAnalytics };
